import * as i0 from '@angular/core';
import { Injectable, Component, Input, EventEmitter, Output, TemplateRef, Directive, Inject, HostListener, Pipe, ContentChildren, ViewChild, ContentChild, Optional, ChangeDetectionStrategy, NgModule } from '@angular/core';
import isEqual from 'lodash.isequal';
import * as i2 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i1 from '@angular/router';
import { NavigationEnd, NavigationStart } from '@angular/router';
import * as i1$1 from '@angular/platform-browser';
import * as i2$1 from '@ngx-translate/core';
import * as i4 from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import * as i5 from 'ngx-mask';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';

/**
 * Memoizes a function by caching its result based on the input arguments.
 *
 * This utility function takes a function `fnToMemoize` and returns a memoized version of it.
 * The memoized function will cache its result based on the input arguments. If the same set of
 * arguments is passed again, the cached result will be returned without re-executing the function.
 *
 * @param {Function} fnToMemoize - The function to memoize.
 * @returns {Function} - A memoized version of the input function.
 *
 * @example
 * // Define a function to memoize
 * function expensiveOperation(a, b) {
 *   console.log('Performing expensive operation...');
 *   return a + b;
 * }
 *
 * // Create a memoized version of the function
 * const memoizedOperation = useMemo(expensiveOperation);
 *
 * // Call the memoized function
 * const result1 = memoizedOperation(2, 3); // Expensive operation is performed.
 * const result2 = memoizedOperation(2, 3); // Cached result is returned without re-computation.
 */
function useMemo(fnToMemoize) {
    let prevArgs = [{}];
    let result;
    return function (...newArgs) {
        const args = JSON.parse(JSON.stringify(newArgs));
        if (!isEqual(prevArgs, args)) {
            result = fnToMemoize(...args);
            prevArgs = JSON.parse(JSON.stringify(args));
        }
        return result;
    };
}

/**
 * A utility service for working with colors in Angular applications.
 *
 * This service provides methods to retrieve CSS variable values and to lighten or darken colors.
 *
 * @example
 * // Inject the ColorsHelper service and use its methods:
 * constructor(private colorsHelper: ColorsHelper) { }
 *
 * // Or instance a new class
 * const colorsHelper = new ColorsHelper()
 *
 * const mainColor = this.colorsHelper.getCssVariableValue('--main-color');
 * const lighterColor = this.colorsHelper.lightenDarkenColor('#FF5733', 20);
 */
class ColorsHelper {
    /**
     * Get the value of a CSS variable.
     *
     * @param {string} variableName - The name of the CSS variable (e.g., '--main-color').
     * @returns {string} The value of the CSS variable or the variableName if not found.
     */
    getCssVariableValue(variableName) {
        if (document?.documentElement) {
            const clearVar = variableName?.replace('var(', '')?.replace(')', '')?.trim();
            return getComputedStyle(document?.documentElement).getPropertyValue(clearVar) || variableName;
        }
        return variableName;
    }
    /**
     * Lighten or darken a color by a specified amount.
     *
     * @param {string} color - The color to be adjusted (e.g., '#FF5733' or 'var(--main-color)').
     * @param {number} amount - The amount to lighten or darken the color (positive for lighten, negative for darken).
     * @returns {string} The adjusted color in hexadecimal format (e.g., '#FF5733').
     */
    lightenDarkenColor(color, amount) {
        color = color?.trim();
        let colorWithoutHash = color?.replace('var(', '')?.replace(')', '')?.replace('#', '');
        if (colorWithoutHash?.length === 3) {
            colorWithoutHash = colorWithoutHash
                .split('')
                .map((c) => `${c}${c}`)
                .join('');
        }
        const getColorChannel = (substring) => {
            let colorChannel = Math.max(Math.min(255, parseInt(substring, 16) + amount), 0).toString(16);
            if (colorChannel?.length < 2) {
                colorChannel = `0${colorChannel}`;
            }
            return colorChannel;
        };
        const colorChannelRed = getColorChannel(colorWithoutHash?.substring(0, 2));
        const colorChannelGreen = getColorChannel(colorWithoutHash?.substring(2, 4));
        const colorChannelBlue = getColorChannel(colorWithoutHash?.substring(4, 6));
        return `#${colorChannelRed}${colorChannelGreen}${colorChannelBlue}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: ColorsHelper, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: ColorsHelper, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: ColorsHelper, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/**
 * Represents the SqLoaderComponent which displays a loading spinner with customizable options.
 *
 * Look the link about the component in original framework and the appearance
 *
 * @see {@link https://css.squidit.com.br/components/loader}
 *
 * <br>
 * <div class="loader mb-3" role="status">
 *   <span class="visually-hidden">Loading...</span>
 * </div>
 *
 * @example
 * <sq-loader size="small" color="var(--blue)" customClass="custom-spinner"></sq-loader>
 *
 * @example
 * <sq-loader size="bigger" customSize="30px" borderSize="0.15em" color="#ff9900"></sq-loader>
 */
class SqLoaderComponent {
    constructor() {
        /**
         * The size of the loading spinner.
         * @default 'small'
         */
        this.size = 'small';
        /**
         * The border size of the loading spinner.
         * @default '0.20em'
         */
        this.borderSize = '0.20em';
        /**
         * The color of the loading spinner.
         * @default 'var(--primary_color)'
         * @example '#ff9900'
         */
        this.color = 'var(--primary_color)';
        /**
         * Custom CSS class to be applied to the loading spinner element.
         */
        this.customClass = '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqLoaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqLoaderComponent, selector: "sq-loader", inputs: { size: "size", customSize: "customSize", borderSize: "borderSize", color: "color", customClass: "customClass" }, ngImport: i0, template: "<div\n  class=\"loader {{ size }} {{ customClass }}\"\n  role=\"status\"\n  [ngStyle]=\"{\n    'border-width': borderSize,\n    height: customSize,\n    width: customSize,\n    'border-color': color\n  }\"\n>\n  <span class=\"visually-hidden\">Loading...</span>\n</div>\n", styles: [""], dependencies: [{ kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqLoaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-loader', template: "<div\n  class=\"loader {{ size }} {{ customClass }}\"\n  role=\"status\"\n  [ngStyle]=\"{\n    'border-width': borderSize,\n    height: customSize,\n    width: customSize,\n    'border-color': color\n  }\"\n>\n  <span class=\"visually-hidden\">Loading...</span>\n</div>\n" }]
        }], propDecorators: { size: [{
                type: Input
            }], customSize: [{
                type: Input
            }], borderSize: [{
                type: Input
            }], color: [{
                type: Input
            }], customClass: [{
                type: Input
            }] } });

/**
 * Represents the SqButtonComponent, a customizable button component.
 *
 * Look the link about the component in original framework and the appearance
 *
 * @see {@link https://css.squidit.com.br/components/button}
 *
 * <br>
 * <button type="button" class='button button-pink mb-3'>Click Me</button>
 *
 * @example
 * <sq-button type="button" color="pink" [loading]="false" (emitClick)="onClick($event)">
 *   Click Me
 * </sq-button>
 */
class SqButtonComponent {
    /**
     * Constructor for the SqButtonComponent class.
     * @param element - The ElementRef representing the button element.
     * @param colorsHelper - The ColorsHelper service for color manipulation.
     */
    constructor(element, colorsHelper) {
        this.element = element;
        this.colorsHelper = colorsHelper;
        /**
         * The type of the button (e.g., 'button', 'submit', 'reset').
         */
        this.type = 'button';
        /**
         * The background color of the button.
         */
        this.color = 'pink';
        /**
         * The text color of the button.
         */
        this.textColor = '';
        /**
         * The border color of the button.
         */
        this.borderColor = '';
        /**
         * The border style of the button.
         */
        this.borderStyle = '';
        /**
         * The text transformation of the button text (e.g., 'uppercase', 'lowercase').
         */
        this.textTransform = '';
        /**
         * The width of the button border.
         */
        this.borderWidth = '';
        /**
         * The border radius of the button.
         */
        this.borderRadius = '';
        /**
         * The size of the button ('sm', 'md', 'lg', 'xl').
         */
        this.size = 'md';
        /**
         * Indicates whether the button should have inverted hover colors.
         */
        this.invertedHover = false;
        /**
         * Event emitter for when the button is clicked.
         */
        this.emitClick = new EventEmitter();
        /**
         * Indicates whether the mouse is hovering over the button.
         */
        this.hover = false;
        /**
         * Performs hover-specific actions based on the type (text, background, border).
         * @param type - The type of hover action.
         * @returns The resulting hover action value.
         */
        this.doHoverAction = useMemo((type) => {
            if (this.validatePresetColors()) {
                return '';
            }
            switch (type) {
                case 'text':
                    return this.doHoverOnText();
                case 'background':
                    return this.doHoverOnBackground();
                case 'border':
                    return this.doHoverOnBorder();
                default:
                    return '';
            }
        });
        this.nativeElement = element.nativeElement;
    }
    /**
     * Validates if the preset colors are set correctly.
     * @returns True if the color is set correctly; otherwise, false.
     */
    validatePresetColors() {
        if (!this.color.startsWith('var(--') &&
            !this.color.startsWith('#') &&
            !this.color.startsWith('rgb') &&
            !this.color.startsWith('hsl') &&
            !this.color.startsWith('transparent')) {
            return !!this.colorsHelper?.getCssVariableValue(this.color);
        }
        return false;
    }
    /**
     * Determines the text color when hovering.
     * @returns The text color when hovering.
     */
    doHoverOnText() {
        if (this.hover) {
            return this.setHoverText();
        }
        return this.textColor;
    }
    /**
     * Determines the background color when hovering.
     * @returns The background color when hovering.
     */
    doHoverOnBackground() {
        if (this.hover) {
            return this.setHoverBg();
        }
        return this.color;
    }
    /**
     * Determines the border color when hovering.
     * @returns The border color when hovering.
     */
    doHoverOnBorder() {
        if (this.hover) {
            return this.setHover(this.borderColor || this.textColor || '');
        }
        return this.borderColor || this.textColor || '';
    }
    /**
     * Sets the hover color.
     * @param color - The color to apply the hover effect.
     * @returns The color with the hover effect applied.
     */
    setHover(color) {
        return this.colorsHelper?.lightenDarkenColor(this.colorsHelper?.getCssVariableValue(color), -25);
    }
    /**
     * Sets the hover background color.
     * @returns The background color with the hover effect applied.
     */
    setHoverBg() {
        if (this.invertedHover) {
            return this.setHover(this.textColor || '');
        }
        return this.setHover(this.color);
    }
    /**
     * Sets the hover text color.
     * @returns The text color with the hover effect applied.
     */
    setHoverText() {
        if (this.invertedHover) {
            return this.setHover(this.color !== 'transparent' ? this.color : 'var(--white-html)');
        }
        return this.setHover(this.textColor !== 'transparent' ? this.textColor || '' : 'var(--white-html)');
    }
    /**
     * Executes a function when the button is clicked.
     * @param event - The MouseEvent associated with the click event.
     */
    executeFunction(event) {
        const { loading, disabled } = this;
        if (!loading && !disabled) {
            this.emitClick.emit(event);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqButtonComponent, deps: [{ token: i0.ElementRef }, { token: ColorsHelper }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqButtonComponent, selector: "sq-button", inputs: { type: "type", color: "color", textColor: "textColor", borderColor: "borderColor", borderStyle: "borderStyle", textTransform: "textTransform", borderWidth: "borderWidth", borderRadius: "borderRadius", size: "size", fontSize: "fontSize", loading: "loading", disabled: "disabled", block: "block", noPadding: "noPadding", id: "id", buttonAsLink: "buttonAsLink", hideOnLoading: "hideOnLoading", invertedHover: "invertedHover", noUnderline: "noUnderline", boxShadow: "boxShadow", width: "width" }, outputs: { emitClick: "emitClick" }, ngImport: i0, template: "<button\r\n  id=\"{{ id }}\"\r\n  class=\"button button-{{ color }} button-{{ size }}\"\r\n  [ngStyle]=\"{\r\n    'font-size': fontSize,\r\n    color: doHoverAction('text'),\r\n    'background-color': doHoverAction('background'),\r\n    'border-color': doHoverAction('border'),\r\n    'border-style': borderStyle,\r\n    'border-radius': borderRadius,\r\n    'border-width': borderWidth,\r\n    'box-shadow': boxShadow,\r\n    width: width,\r\n    'text-transform': textTransform\r\n  }\"\r\n  [ngClass]=\"{\r\n    disabled: disabled,\r\n    loading: loading,\r\n    block: block,\r\n    'p-0': noPadding,\r\n    'button-as-link': buttonAsLink,\r\n    'no-underline': noUnderline,\r\n    'inverted': invertedHover\r\n  }\"\r\n  [type]=\"type || 'button'\"\r\n  (click)=\"executeFunction($event)\"\r\n  (mouseover)=\"hover = true\"\r\n  (mouseleave)=\"hover = false\"\r\n  [disabled]=\"disabled\"\r\n>\r\n  <ng-content *ngIf=\"!hideOnLoading || (hideOnLoading && !loading)\"></ng-content>\r\n  <sq-loader\r\n    class=\"{{ hideOnLoading ? 'hide-on-loading' : '' }}\"\r\n    *ngIf=\"loading\"\r\n    customSize=\"1rem\"\r\n    color=\"{{ textColor }}\"\r\n  ></sq-loader>\r\n</button>\r\n", styles: [".button.p-0{padding:0}.button.block{width:100%}.button.button-as-link{border-style:none!important;background-color:transparent!important;text-decoration:underline;color:initial!important;white-space:nowrap;text-transform:none}.button.no-underline{text-decoration:none}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: SqLoaderComponent, selector: "sq-loader", inputs: ["size", "customSize", "borderSize", "color", "customClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-button', template: "<button\r\n  id=\"{{ id }}\"\r\n  class=\"button button-{{ color }} button-{{ size }}\"\r\n  [ngStyle]=\"{\r\n    'font-size': fontSize,\r\n    color: doHoverAction('text'),\r\n    'background-color': doHoverAction('background'),\r\n    'border-color': doHoverAction('border'),\r\n    'border-style': borderStyle,\r\n    'border-radius': borderRadius,\r\n    'border-width': borderWidth,\r\n    'box-shadow': boxShadow,\r\n    width: width,\r\n    'text-transform': textTransform\r\n  }\"\r\n  [ngClass]=\"{\r\n    disabled: disabled,\r\n    loading: loading,\r\n    block: block,\r\n    'p-0': noPadding,\r\n    'button-as-link': buttonAsLink,\r\n    'no-underline': noUnderline,\r\n    'inverted': invertedHover\r\n  }\"\r\n  [type]=\"type || 'button'\"\r\n  (click)=\"executeFunction($event)\"\r\n  (mouseover)=\"hover = true\"\r\n  (mouseleave)=\"hover = false\"\r\n  [disabled]=\"disabled\"\r\n>\r\n  <ng-content *ngIf=\"!hideOnLoading || (hideOnLoading && !loading)\"></ng-content>\r\n  <sq-loader\r\n    class=\"{{ hideOnLoading ? 'hide-on-loading' : '' }}\"\r\n    *ngIf=\"loading\"\r\n    customSize=\"1rem\"\r\n    color=\"{{ textColor }}\"\r\n  ></sq-loader>\r\n</button>\r\n", styles: [".button.p-0{padding:0}.button.block{width:100%}.button.button-as-link{border-style:none!important;background-color:transparent!important;text-decoration:underline;color:initial!important;white-space:nowrap;text-transform:none}.button.no-underline{text-decoration:none}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: ColorsHelper }]; }, propDecorators: { type: [{
                type: Input
            }], color: [{
                type: Input
            }], textColor: [{
                type: Input
            }], borderColor: [{
                type: Input
            }], borderStyle: [{
                type: Input
            }], textTransform: [{
                type: Input
            }], borderWidth: [{
                type: Input
            }], borderRadius: [{
                type: Input
            }], size: [{
                type: Input
            }], fontSize: [{
                type: Input
            }], loading: [{
                type: Input
            }], disabled: [{
                type: Input
            }], block: [{
                type: Input
            }], noPadding: [{
                type: Input
            }], id: [{
                type: Input
            }], buttonAsLink: [{
                type: Input
            }], hideOnLoading: [{
                type: Input
            }], invertedHover: [{
                type: Input
            }], noUnderline: [{
                type: Input
            }], boxShadow: [{
                type: Input
            }], width: [{
                type: Input
            }], emitClick: [{
                type: Output
            }] } });

/**
 * Represents a progress bar component for displaying progress visually.
 *
 * Look the link about the component in original framework and the appearance
 *
 * @see {@link https://css.squidit.com.br/components/progress-bar}
 *
 * <div class="progress my-3">
 *  <div
 *    class="progress-bar background-dashed animate-bar"
 *    role="progressbar"
 *    aria-valuemin="0"
 *    aria-valuemax="100"
 *  ></div>
 * </div>
 *
 * @example
 * <sq-progress-bar [value]="50" [striped]="true"></sq-progress-bar>
 *
 */
class SqProgressBarComponent {
    constructor() {
        /**
         * The color of the progress bar.
         */
        this.color = 'black';
        /**
         * Indicates whether to display a label on the progress bar.
         */
        this.hasLabel = false;
        /**
         * The value of the progress bar (can be a string or number).
         */
        this.value = 0;
        /**
         * The height of the progress bar.
         */
        this.height = '1rem';
        /**
         * Indicates whether to display stripes on the progress bar.
         */
        this.striped = true;
        /**
         * Indicates whether the progress bar should have an animation.
         */
        this.animated = false;
        /**
         * The rounded value of the progress (rounded to the nearest integer).
         */
        this.roundValue = useMemo((value) => {
            return Math.round(Number(value));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqProgressBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqProgressBarComponent, selector: "sq-progress-bar", inputs: { color: "color", hasLabel: "hasLabel", value: "value", height: "height", striped: "striped", animated: "animated" }, ngImport: i0, template: "<div class=\"progress\" style=\"height: 14px\">\r\n  <div\r\n    class=\"progress-bar background-{{ color }}\"\r\n    role=\"progressbar\"\r\n    aria-valuemin=\"0\"\r\n    aria-valuemax=\"100\"\r\n    [ngClass]=\"{\r\n      'background-dashed': striped,\r\n      'animate-bar': animated\r\n    }\"\r\n    [ngStyle]=\"{\r\n      width: value + '%',\r\n      height: height,\r\n      'backgroud-color': color\r\n    }\"\r\n    style=\"font-size: 0.86rem\"\r\n  >\r\n    {{ hasLabel ? roundValue(value) + '%' : null }}\r\n  </div>\r\n</div>\r\n", styles: [".progress{width:100%}:host{display:inline-block;width:100%}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqProgressBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-progress-bar', template: "<div class=\"progress\" style=\"height: 14px\">\r\n  <div\r\n    class=\"progress-bar background-{{ color }}\"\r\n    role=\"progressbar\"\r\n    aria-valuemin=\"0\"\r\n    aria-valuemax=\"100\"\r\n    [ngClass]=\"{\r\n      'background-dashed': striped,\r\n      'animate-bar': animated\r\n    }\"\r\n    [ngStyle]=\"{\r\n      width: value + '%',\r\n      height: height,\r\n      'backgroud-color': color\r\n    }\"\r\n    style=\"font-size: 0.86rem\"\r\n  >\r\n    {{ hasLabel ? roundValue(value) + '%' : null }}\r\n  </div>\r\n</div>\r\n", styles: [".progress{width:100%}:host{display:inline-block;width:100%}\n"] }]
        }], propDecorators: { color: [{
                type: Input
            }], hasLabel: [{
                type: Input
            }], value: [{
                type: Input
            }], height: [{
                type: Input
            }], striped: [{
                type: Input
            }], animated: [{
                type: Input
            }] } });

/**
 * Sleeps for a specified number of milliseconds.
 *
 * @returns {Promise<void>} A Promise that resolves after the specified delay.
 *
 * @example
 * // Sleep for 1000 milliseconds (1 second)
 * await sleep(1000);
 */
async function sleep(ms = 300) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Angular directive for creating and managing tooltips.
 *
 * This directive allows you to easily add tooltips to elements in your Angular application.
 * Tooltips can display additional information when hovering or clicking on an element.
 *
 * @see {@link https://css.squidit.com.br/components/tooltip|Official Tooltip Documentation}
 *
 * @example
 * <!-- Basic usage -->
 * <div [tooltip]="'Tooltip message'" placement="center top"></div>
 */
class SqTooltipDirective {
    /**
     * Constructs a new SqTooltipDirective.
     *
     * @param {ElementRef} el - The ElementRef of the host element.
     * @param {Renderer2} renderer - The Renderer2 for DOM manipulation.
     * @param {Router} router - The Angular Router service.
     * @param {ViewContainerRef} viewContainerRef - The ViewContainerRef for the tooltip.
     * @param {Document} documentImported - The injected Document object for DOM manipulation.
     */
    constructor(el, renderer, router, viewContainerRef, documentImported) {
        this.el = el;
        this.renderer = renderer;
        this.router = router;
        this.viewContainerRef = viewContainerRef;
        this.documentImported = documentImported;
        /**
         * The content of the tooltip. Can be a string message or an Angular TemplateRef.
         */
        this.content = '';
        /**
         * The placement of the tooltip relative to the host element.
         * Possible values: 'left top', 'left center', 'left bottom', 'center top',
         * 'center center', 'center bottom', 'right top', 'right center', 'right bottom'.
         */
        this.placement = 'center top';
        /**
         * The delay in milliseconds before showing the tooltip.
         */
        this.delay = 0;
        /**
         * The theme of the tooltip. Possible values: 'light' or 'dark'.
         */
        this.theme = 'dark';
        /**
         * The trigger for displaying the tooltip. Possible values: 'hover' or 'click'.
         */
        this.trigger = 'hover';
        /**
         * Reference to the generated tooltip element.
         */
        this.tooltipElement = null;
        /**
         * The offset between the tooltip and the host element.
         */
        this.offset = 10;
        /**
         * Reference to the window object.
         */
        this.window = window;
        /**
         * Indicates whether the tooltip is open or closed. Used for internal control.
         */
        this.open = false;
        // Bind the hide function to the current instance.
        this.hide = this.hide.bind(this);
        // Assign the document object for DOM manipulation.
        this.document = this.documentImported || document;
    }
    /**
     * Event listener for the 'mouseenter' event to show the tooltip on hover.
     */
    onMouseEnter() {
        if (!this.isTouch() && !this.tooltipElement && this.trigger === 'hover') {
            this.show();
        }
    }
    /**
     * Event listener for the 'mouseleave' event to hide the tooltip on hover.
     */
    onMouseLeave() {
        if (this.tooltipElement && this.trigger === 'hover' && !this.isTouch()) {
            this.hide();
        }
    }
    /**
     * Event listener for the 'click' event to toggle the tooltip on click.
     */
    onClick() {
        if (this.tooltipElement && (this.trigger === 'click' || this.isTouch())) {
            this.hide();
        }
        if (!this.tooltipElement && (this.trigger === 'click' || this.isTouch())) {
            this.show();
        }
    }
    /**
     * Initializes the directive and subscribes to router events to hide the tooltip on navigation.
     */
    ngOnInit() {
        this.router.events.subscribe((val) => {
            if (val instanceof NavigationEnd) {
                this.hide();
            }
        });
    }
    /**
     * Cleans up the directive when it is destroyed, ensuring the tooltip is hidden.
     */
    ngOnDestroy() {
        this.hide();
    }
    /**
     * Checks if the device has touch support.
     *
     * @returns {boolean} - True if the device supports touch events; otherwise, false.
     */
    isTouch() {
        return 'ontouchstart' in window || navigator?.maxTouchPoints > 0;
    }
    /**
     * Shows the tooltip and sets its position.
     */
    async show() {
        this.create();
        this.setPosition();
        this.document?.addEventListener('click', this.hide, true);
        if (this.tooltipElement) {
            this.renderer.addClass(this.tooltipElement, 'tooltip-show');
            await sleep(500); // Wait for animations.
            this.open = true;
        }
    }
    /**
     * Hides the tooltip with a delay to allow for animations, and performs cleanup.
     */
    hide() {
        if (this.tooltipElement &&
            ((this.isTouch() || this.trigger === 'click') && this.open) ||
            (!this.isTouch() && this.trigger === 'hover')) {
            try {
                this.renderer.removeClass(this.tooltipElement, 'tooltip-show');
            }
            catch (e) {
                // Ignore error
            }
            window.setTimeout(() => {
                try {
                    this.renderer.removeChild(this.document.body, this.tooltipElement);
                }
                catch (e) {
                    // Ignore error
                }
                this.open = false;
                this.document.removeEventListener('click', this.hide, true);
                this.tooltipElement = null;
            }, this.delay);
        }
    }
    /**
     * Creates the tooltip element and appends it to the DOM.
     */
    create() {
        if (this.content) {
            const arrow = this.renderer.createElement('div');
            this.renderer.addClass(arrow, 'tooltip-arrow');
            if (this.isTouch()) {
                this.renderer.addClass(arrow, 'tooltip-not-arrow');
            }
            this.tooltipElement = this.renderer.createElement('div');
            if (this.tooltipElement) {
                if (this.content instanceof TemplateRef) {
                    for (const node of this.viewContainerRef.createEmbeddedView(this.content).rootNodes) {
                        this.renderer.appendChild(this.tooltipElement, node);
                    }
                }
                if (typeof this.content === 'string') {
                    this.tooltipElement.innerHTML = this.content;
                }
            }
            this.renderer.addClass(this.tooltipElement, 'tooltip');
            this.renderer.addClass(this.tooltipElement, 'tooltip-generated');
            this.renderer.addClass(this.tooltipElement, `tooltip-${this.theme}`);
            this.renderer.addClass(this.tooltipElement, `tooltip-${this.placement.replace(' ', '-')}`);
            this.renderer.addClass(this.tooltipElement, 'text-left');
            this.renderer.setStyle(this.tooltipElement, '-webkit-transition', `opacity ${this.delay}ms`);
            this.renderer.setStyle(this.tooltipElement, '-moz-transition', `opacity ${this.delay}ms`);
            this.renderer.setStyle(this.tooltipElement, '-o-transition', `opacity ${this.delay}ms`);
            this.renderer.setStyle(this.tooltipElement, 'transition', `opacity ${this.delay}ms`);
            this.renderer.appendChild(this.tooltipElement, arrow);
            this.renderer.appendChild(this.document.body, this.tooltipElement);
        }
    }
    /**
     * Sets the position of the tooltip relative to the host element.
     */
    setPosition() {
        const hostPos = this.el.nativeElement.getBoundingClientRect();
        if (this.tooltipElement) {
            const tooltipPos = this.tooltipElement.getBoundingClientRect();
            const scrollPos = window.pageYOffset || this.document.documentElement.scrollTop || this.document.body.scrollTop || 0;
            const percentage = Math.round(((hostPos.left + hostPos.width / 2) / window.innerWidth * 100));
            let top;
            let left;
            const posHorizontal = this.placement.split(' ')[0] || 'center';
            const posVertical = this.placement.split(' ')[1] || 'bottom';
            switch (posHorizontal) {
                case 'left':
                    left = hostPos.left - tooltipPos.width - this.offset;
                    break;
                case 'right':
                    left = hostPos.right + this.offset;
                    break;
                default:
                case 'center':
                    left = hostPos.left + (percentage / 100) * 8 + (hostPos.width - tooltipPos.width) / 2;
            }
            switch (posVertical) {
                case 'center':
                    top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
                    break;
                case 'bottom':
                    top = hostPos.bottom + this.offset;
                    break;
                default:
                case 'top':
                    top = hostPos.top - tooltipPos.height - this.offset;
            }
            this.renderer.setStyle(this.tooltipElement, 'top', `${top + scrollPos}px`);
            this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqTooltipDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i1.Router }, { token: i0.ViewContainerRef }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.8", type: SqTooltipDirective, selector: "[tooltip]", inputs: { content: ["tooltip", "content"], placement: "placement", delay: "delay", theme: "theme", trigger: "trigger" }, host: { listeners: { "mouseenter": "onMouseEnter()", "mouseleave": "onMouseLeave()", "click": "onClick()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqTooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[tooltip]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1.Router }, { type: i0.ViewContainerRef }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { content: [{
                type: Input,
                args: ['tooltip']
            }], placement: [{
                type: Input
            }], delay: [{
                type: Input
            }], theme: [{
                type: Input
            }], trigger: [{
                type: Input
            }], onMouseEnter: [{
                type: HostListener,
                args: ['mouseenter']
            }], onMouseLeave: [{
                type: HostListener,
                args: ['mouseleave']
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

/**
 * Represents a component for displaying a sequence of steps.
 *
 * Look the link about the component in original framework and the appearance
 *
 * @see {@link https://css.squidit.com.br/components/steps}
 *
 * <div class="steps my-3">
 *  <ul>
 *    <li class="old">
 *      <span></span>
 *    </li>
 *    <li class="active">
 *      <span></span>
 *    </li>
 *    <li>
 *      <span></span>
 *    </li>
 *  </ul>
 * </div>
 *
 * @example
 * <sq-steps [active]="0" [steps]="stepArray"></sq-steps>
 *
 */
class SqStepsComponent {
    constructor() {
        /**
         * The color theme for the steps component.
         */
        this.color = 'var(--pink)';
        /**
         * Flag to enable or disable clicking on steps.
         */
        this.click = false;
        /**
         * The index of the currently active step.
         */
        this.active = 0;
        /**
         * An array of step objects representing the steps in the sequence.
         */
        this.steps = [];
        /**
         * Event emitted when a step is clicked.
         */
        this.emitClick = new EventEmitter();
    }
    /**
     * Handles the click event on a step.
     * Emits the `emitClick` event with information about the clicked step and its index.
     *
     * @param {number} index - The index of the clicked step.
     */
    handleClick(index) {
        if (this.click && index >= 0 && index < this.steps.length) {
            this.emitClick.emit({ step: this.steps[index], i: index });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqStepsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqStepsComponent, selector: "sq-steps", inputs: { color: "color", click: "click", active: "active", steps: "steps" }, outputs: { emitClick: "emitClick" }, ngImport: i0, template: "<div class=\"steps\">\n  <ul>\n    <li\n      *ngFor=\"let step of steps; let i = index\"\n      [ngClass]=\"{\n        active: i === active,\n        old: i < active\n      }\"\n      [ngStyle]=\"{\n        background:\n          i === active ? 'linear-gradient(90deg, ' + color + ' 0%, ' + color + ' 50%, transparent 51%)' : i < active ? color : null\n      }\"\n    >\n      <span\n        (click)=\"click ? (active = i) : null; emitClick.emit({ step, i })\"\n        [tooltip]=\"step?.tip || ''\"\n        placement=\"center top\"\n        container=\"body\"\n        [ngClass]=\"{\n          pointer: click\n        }\"\n      ></span>\n    </li>\n  </ul>\n</div>\n", styles: [""], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: SqTooltipDirective, selector: "[tooltip]", inputs: ["tooltip", "placement", "delay", "theme", "trigger"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqStepsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-steps', template: "<div class=\"steps\">\n  <ul>\n    <li\n      *ngFor=\"let step of steps; let i = index\"\n      [ngClass]=\"{\n        active: i === active,\n        old: i < active\n      }\"\n      [ngStyle]=\"{\n        background:\n          i === active ? 'linear-gradient(90deg, ' + color + ' 0%, ' + color + ' 50%, transparent 51%)' : i < active ? color : null\n      }\"\n    >\n      <span\n        (click)=\"click ? (active = i) : null; emitClick.emit({ step, i })\"\n        [tooltip]=\"step?.tip || ''\"\n        placement=\"center top\"\n        container=\"body\"\n        [ngClass]=\"{\n          pointer: click\n        }\"\n      ></span>\n    </li>\n  </ul>\n</div>\n" }]
        }], propDecorators: { color: [{
                type: Input
            }], click: [{
                type: Input
            }], active: [{
                type: Input
            }], steps: [{
                type: Input
            }], emitClick: [{
                type: Output
            }] } });

/**
 * A custom Angular pipe for sanitizing and displaying trusted content.
 * This pipe can be used to bypass security and render trusted HTML, styles, scripts, URLs, or resource URLs.
 *
 * @example
 * // In your component template:
 * // Assuming 'htmlContent' is a string containing trusted HTML.
 * <div [innerHTML]="htmlContent | universalSafe:'html'"></div>
 *
 * @param {string} value - The content to sanitize and display.
 * @param {string} type - The type of content to sanitize. Can be 'html', 'style', 'script', 'url', or 'resourceUrl'.
 * @returns {SafeHtml | SafeStyle | SafeScript | SafeUrl | SafeResourceUrl} - The sanitized and trusted content.
 */
class UniversalSafePipe {
    /**
     * Creates an instance of UniversalSafePipe.
     *
     * @param {DomSanitizer} sanitizer - The Angular DOM sanitizer service.
     */
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    /**
     * Sanitizes and returns trusted content based on the specified type.
     *
     * @param {string} value - The content to sanitize and display.
     * @param {string} type - The type of content to sanitize. Can be 'html', 'style', 'script', 'url', or 'resourceUrl'.
     * @returns {SafeHtml | SafeStyle | SafeScript | SafeUrl | SafeResourceUrl} - The sanitized and trusted content.
     * @throws {Error} - If an invalid `type` is provided.
     */
    transform(value, type = 'html') {
        if (!value) {
            return '';
        }
        switch (type) {
            case 'html':
                return this.sanitizer.bypassSecurityTrustHtml(value);
            case 'style':
                return this.sanitizer.bypassSecurityTrustStyle(value);
            case 'script':
                return this.sanitizer.bypassSecurityTrustScript(value);
            case 'url':
                return this.sanitizer.bypassSecurityTrustUrl(value);
            case 'resourceUrl':
                return this.sanitizer.bypassSecurityTrustResourceUrl(value);
            default:
                throw new Error(`Invalid Safe sanitizer: ${type}`);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: UniversalSafePipe, deps: [{ token: i1$1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.8", ngImport: i0, type: UniversalSafePipe, name: "universalSafe" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: UniversalSafePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'universalSafe',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.DomSanitizer }]; } });

/**
 * Represents a tooltip component for displaying informative messages.
 *
 * Look the link about the component in original framework and the appearance
 *
 * @see {@link https://css.squidit.com.br/components/tooltip}
 *
 * @example
 * <sq-tooltip placement='center top' message='This is a tooltip message.'></sq-tooltip>
 *
 */
class SqTooltipComponent {
    constructor() {
        /**
         * The background color of the tooltip.
         */
        this.color = '';
        /**
         * The name of the icon to display in the tooltip.
         */
        this.icon = '';
        /**
         * The placement of the tooltip (e.g., 'center top').
         * @see {@link https://css.squidit.com.br/components/tooltip}
         */
        this.placement = 'center top';
        /**
         * The message to display in the tooltip.
         */
        this.message = '';
        /**
         * Additional CSS classes for styling the tooltip.
         */
        this.tooltipClass = '';
        /**
         * The text alignment within the tooltip.
         */
        this.textAlign = 'text-center';
        /**
         * The font size of the tooltip content.
         */
        this.fontSize = '1rem';
        /**
         * The tooltip trigger
         * Possible values: 'hover' or 'click'.
         */
        this.trigger = 'hover';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqTooltipComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqTooltipComponent, selector: "sq-tooltip", inputs: { color: "color", icon: "icon", placement: "placement", message: "message", tooltipClass: "tooltipClass", textAlign: "textAlign", fontSize: "fontSize", trigger: "trigger" }, ngImport: i0, template: "<ng-template #tooltip>\r\n  <div class=\"{{ textAlign }} {{ tooltipClass }}\" [innerHTML]=\"message\"></div>\r\n</ng-template>\r\n<div\r\n  [class]=\"'wrapper-tooltip {{ color }}'\"\r\n  [placement]=\"placement\"\r\n  [tooltip]=\"message\"\r\n  [trigger]=\"trigger\"\r\n  [ngStyle]=\"{\r\n    color: color\r\n  }\"\r\n>\r\n  <div class=\"icon\" *ngIf=\"icon\" [innerHtml]=\"icon | universalSafe\"></div>\r\n  <i\r\n    class=\"fa-solid fa-circle-question icon\"\r\n    *ngIf=\"!icon\"\r\n  ></i>\r\n</div>\r\n", styles: [".wrapper-tooltip{display:inline-block;cursor:help}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: SqTooltipDirective, selector: "[tooltip]", inputs: ["tooltip", "placement", "delay", "theme", "trigger"] }, { kind: "pipe", type: UniversalSafePipe, name: "universalSafe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqTooltipComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-tooltip', template: "<ng-template #tooltip>\r\n  <div class=\"{{ textAlign }} {{ tooltipClass }}\" [innerHTML]=\"message\"></div>\r\n</ng-template>\r\n<div\r\n  [class]=\"'wrapper-tooltip {{ color }}'\"\r\n  [placement]=\"placement\"\r\n  [tooltip]=\"message\"\r\n  [trigger]=\"trigger\"\r\n  [ngStyle]=\"{\r\n    color: color\r\n  }\"\r\n>\r\n  <div class=\"icon\" *ngIf=\"icon\" [innerHtml]=\"icon | universalSafe\"></div>\r\n  <i\r\n    class=\"fa-solid fa-circle-question icon\"\r\n    *ngIf=\"!icon\"\r\n  ></i>\r\n</div>\r\n", styles: [".wrapper-tooltip{display:inline-block;cursor:help}\n"] }]
        }], propDecorators: { color: [{
                type: Input
            }], icon: [{
                type: Input
            }], placement: [{
                type: Input
            }], message: [{
                type: Input
            }], tooltipClass: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], fontSize: [{
                type: Input
            }], trigger: [{
                type: Input
            }] } });

/**
 * Represents a tab component for displaying tabbed content.
 *
 * Look the link about the component in original framework and the appearance
 *
 * @see {@link https://css.squidit.com.br/components/tabs}
 *
 * @example
 * <sq-tab [title]="'Tab Title'" (whenOpen)="handleTabOpen()">
 * <!-- Content Here -->
 * </sq-tab>
 *
 */
class SqTabComponent {
    constructor() {
        /**
         * Flag to indicate if the tab is active (open).
         */
        this.active = false;
        /**
         * The title displayed on the tab.
         */
        this.title = '';
        /**
         * The text color of the tab title.
         */
        this.textColor = 'black';
        /**
         * The background color of the tab.
         */
        this.color = 'white';
        /**
         * The border color of the tab.
         */
        this.borderColor = 'transparent';
        /**
         * The name or identifier of the tab.
         */
        this.tabName = '';
        /**
         * Event emitted when the tab is opened.
         */
        this.whenOpen = new EventEmitter();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqTabComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqTabComponent, selector: "sq-tab", inputs: { active: "active", title: "title", textColor: "textColor", color: "color", borderColor: "borderColor", disabled: "disabled", loading: "loading", tabName: "tabName" }, outputs: { whenOpen: "whenOpen" }, ngImport: i0, template: "<div [hidden]=\"!active\" class=\"pane\">\n  <ng-content></ng-content>\n</div>\n", styles: [":host .pane{padding:0}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqTabComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-tab', template: "<div [hidden]=\"!active\" class=\"pane\">\n  <ng-content></ng-content>\n</div>\n", styles: [":host .pane{padding:0}\n"] }]
        }], propDecorators: { active: [{
                type: Input
            }], title: [{
                type: Input
            }], textColor: [{
                type: Input
            }], color: [{
                type: Input
            }], borderColor: [{
                type: Input
            }], disabled: [{
                type: Input
            }], loading: [{
                type: Input
            }], tabName: [{
                type: Input
            }], whenOpen: [{
                type: Output
            }] } });

/**
 * Represents a tab container component for managing a collection of tabs.
 *
 * Look the link about the component in original framework and the appearance
 *
 * @see {@link https://css.squidit.com.br/components/tabs}
 *
 * @example
 * <sq-tabs [lineStyle]="true" (tabChange)="handleTabChange($event)">
 *   <sq-tab [title]="'Tab 1'" (whenOpen)="handleTabOpen()">Tab 1 Content</sq-tab>
 *   <sq-tab [title]="'Tab 2'">Tab 2 Content</sq-tab>
 *   <!-- Add more sq-tab elements as needed -->
 * </sq-tabs>
 *
 * @implements {AfterViewInit}
 * @implements {AfterViewChecked}
 */
class SqTabsComponent {
    constructor() {
        /**
         * A query list of `SqTabComponent` elements representing the tabs.
         */
        this.tabs = [];
        /**
         * Custom CSS class for the input element.
         */
        this.customClass = '';
        /**
         * The maximum width of the tab container.
         */
        this.maxWidth = 'initial';
        /**
         * The margin of the tab container.
         */
        this.margin = '0 auto';
        /**
         * Flag to indicate whether to display a line-style indicator for the selected tab.
         */
        this.lineStyle = false;
        /**
         * The width of the tab container.
         */
        this.tabWidth = '';
        /**
         * Flag to indicate to use sm class com tabs header.
         */
        this.sm = true;
        /**
         * Event emitted when a tab is changed.
         */
        this.tabChange = new EventEmitter();
        /**
         * The total number of tabs in the container.
         */
        this.total = 1;
        /**
         * The initial position of the tabs.
         */
        this.tabsPosition = 'initial';
        /**
         * Determines the tab width based on the provided conditions.
         *
         * @param {string} tabWidth - The width of the tab.
         * @param {boolean} lineStyle - A flag to determine if line style is applied.
         *
         * @returns {string} - Returns 'fit-content' if lineStyle is true.
         *                     Returns the provided tabWidth if it exists.
         *                     Otherwise, returns 'initial'.
         */
        this.memoizedTabWidth = useMemo((tabWidth, lineStyle) => {
            if (tabWidth) {
                return tabWidth;
            }
            if (lineStyle) {
                return 'fit-content';
            }
            return 'initial';
        });
    }
    /**
     * Lifecycle hook called after the view initialization.
     */
    async ngAfterViewInit() {
        const activeTabs = this.tabs.filter((tab) => tab.active);
        if (activeTabs.length === 0) {
            await sleep(1000);
            if (this.tabs.first) {
                this.selectTab(this.tabs.first, 0);
            }
        }
        this.total = this.tabs.toArray().length || 1;
    }
    /**
     * Lifecycle hook called after the view has been checked.
     */
    ngAfterViewChecked() {
        if (this.tabs.toArray().length !== this.total) {
            this.total = this.tabs.toArray().length || 1;
        }
    }
    /**
     * Selects a tab by making it active.
     *
     * @param {SqTabComponent} tab - The tab to be selected.
     * @param {number} index - The index of the selected tab.
     */
    selectTab(tab, index) {
        if (tab?.disabled || tab?.loading) {
            return null;
        }
        this.tabs.toArray().forEach((tabItem) => (tabItem.active = false));
        if (tab) {
            this.tabChange.emit({
                tab,
                index,
            });
            tab.active = true;
            if (tab.whenOpen) {
                tab.whenOpen.emit();
            }
        }
        return null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqTabsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqTabsComponent, selector: "sq-tabs", inputs: { customClass: "customClass", height: "height", maxWidth: "maxWidth", margin: "margin", lineStyle: "lineStyle", tabWidth: "tabWidth", sm: "sm" }, outputs: { tabChange: "tabChange" }, queries: [{ propertyName: "tabs", predicate: SqTabComponent }], ngImport: i0, template: "<div class=\"tabs-squid\">\n  <div class=\"tabs-container\">\n    <div\n      class=\"wrapper {{ customClass }}\"\n      [ngStyle]=\"{\n        'width': memoizedTabWidth(tabWidth, lineStyle),\n      }\"\n    >\n      <ul\n        class=\"tabs-header tabs-center tabs-width\"\n        #tabsHeaderContainer\n        [ngClass]=\"{\n          'line-style': lineStyle\n        }\"\n        [ngStyle]=\"{\n          'max-width': maxWidth,\n          margin: margin\n        }\"\n      >\n        <li\n          *ngFor=\"let tab of tabs; let i = index\"\n          (click)=\"!tab?.disabled ? selectTab(tab, i) : null\"\n          [ngClass]=\"{\n            disabled: !tab?.title || tab?.disabled,\n            loading: tab?.title && tab.loading,\n            active: tab?.active,\n            sm: sm\n          }\"\n          [ngStyle]=\"{\n            background: tab?.active && !lineStyle ? tab?.color : null\n          }\"\n          #thisTab\n        >\n          <span\n            *ngIf=\"!tab.loading\"\n            [ngStyle]=\"{\n              color: tab?.active && !lineStyle ? tab?.textColor : null\n            }\"\n            [innerHtml]=\"tab?.title || '' | universalSafe\"\n          ></span>\n          <sq-loader *ngIf=\"tab.loading\"></sq-loader>\n        </li>\n      </ul>\n    </div>\n  </div>\n  <ng-content></ng-content>\n</div>\n", styles: [":host{display:block;width:100%}:host .wrapper{position:relative;justify-content:center;display:flex;align-items:center}:host .tabs-header{position:relative;scroll-behavior:smooth;background:var(--transparent)}:host .tabs-header li{min-width:min-content}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: SqLoaderComponent, selector: "sq-loader", inputs: ["size", "customSize", "borderSize", "color", "customClass"] }, { kind: "pipe", type: UniversalSafePipe, name: "universalSafe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqTabsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-tabs', template: "<div class=\"tabs-squid\">\n  <div class=\"tabs-container\">\n    <div\n      class=\"wrapper {{ customClass }}\"\n      [ngStyle]=\"{\n        'width': memoizedTabWidth(tabWidth, lineStyle),\n      }\"\n    >\n      <ul\n        class=\"tabs-header tabs-center tabs-width\"\n        #tabsHeaderContainer\n        [ngClass]=\"{\n          'line-style': lineStyle\n        }\"\n        [ngStyle]=\"{\n          'max-width': maxWidth,\n          margin: margin\n        }\"\n      >\n        <li\n          *ngFor=\"let tab of tabs; let i = index\"\n          (click)=\"!tab?.disabled ? selectTab(tab, i) : null\"\n          [ngClass]=\"{\n            disabled: !tab?.title || tab?.disabled,\n            loading: tab?.title && tab.loading,\n            active: tab?.active,\n            sm: sm\n          }\"\n          [ngStyle]=\"{\n            background: tab?.active && !lineStyle ? tab?.color : null\n          }\"\n          #thisTab\n        >\n          <span\n            *ngIf=\"!tab.loading\"\n            [ngStyle]=\"{\n              color: tab?.active && !lineStyle ? tab?.textColor : null\n            }\"\n            [innerHtml]=\"tab?.title || '' | universalSafe\"\n          ></span>\n          <sq-loader *ngIf=\"tab.loading\"></sq-loader>\n        </li>\n      </ul>\n    </div>\n  </div>\n  <ng-content></ng-content>\n</div>\n", styles: [":host{display:block;width:100%}:host .wrapper{position:relative;justify-content:center;display:flex;align-items:center}:host .tabs-header{position:relative;scroll-behavior:smooth;background:var(--transparent)}:host .tabs-header li{min-width:min-content}\n"] }]
        }], propDecorators: { tabs: [{
                type: ContentChildren,
                args: [SqTabComponent]
            }], customClass: [{
                type: Input
            }], height: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], margin: [{
                type: Input
            }], lineStyle: [{
                type: Input
            }], tabWidth: [{
                type: Input
            }], sm: [{
                type: Input
            }], tabChange: [{
                type: Output
            }] } });

/**
 * Represents the SqInfinityComponent, a component for infinite scrolling.
 *
 * @implements {AfterViewInit}
 * @implements {AfterContentChecked}
 * @implements {OnDestroy}
 *
 * @example
 * <sq-infinity-scroll [length]="totalItems" [hasMore]="hasMoreData" [loading]="isLoading" (scrolledEmitter)="loadMoreItems()">
 *    <!-- Content Here -->
 * </sq-infinity-scroll>
 */
class SqInfinityComponent {
    /**
     * Creates an instance of SqInfinityComponent.
     *
     * @param {Document} documentImported Reference to the Document object for interacting with the DOM.
     */
    constructor(documentImported) {
        this.documentImported = documentImported;
        /**
         * The total number of items in the list.
         */
        this.length = 0;
        /**
         * Indicates whether there are more items to load.
         */
        this.hasMore = true;
        /**
         * Event emitter for when the user scrolls to trigger loading more items.
         */
        this.scrolledEmitter = new EventEmitter();
        /**
         * Handles the scroll event and triggers loading more items if applicable.
         */
        this.onScroll = () => {
            if (!this.loading && this.length > 0 && this.hasMore) {
                if (this.elementToScrollId && this.elementToScroll instanceof HTMLElement) {
                    const allScroll = this.elementToScroll?.scrollTop + this.elementToScroll?.scrollHeight;
                    if (allScroll >= this.elementToScroll?.scrollHeight) {
                        this.elementToScroll?.removeEventListener('scroll', this.onScroll, false);
                        this.scrolledEmitter.emit();
                        this.elementToScroll?.addEventListener('scroll', this.onScroll, false);
                    }
                }
                else if (this.elementToScroll instanceof Window) {
                    const elementHeight = this.elementToScroll?.innerHeight;
                    const elementY = this.elementToScroll?.scrollY;
                    if (elementHeight + elementY >= this.scrollElement?.nativeElement.offsetHeight + this.scrollElement?.nativeElement.offsetTop) {
                        this.elementToScroll?.removeEventListener('scroll', this.onScroll, false);
                        this.scrolledEmitter.emit();
                        this.elementToScroll?.addEventListener('scroll', this.onScroll, false);
                    }
                }
            }
        };
        this.document = this.documentImported || document;
    }
    /**
     * Performs actions after the view has been initialized.
     */
    ngAfterViewInit() {
        const { elementToScrollId } = this;
        if (elementToScrollId) {
            this.elementToScroll = this.document.getElementById(elementToScrollId);
        }
        if (!elementToScrollId || !this.elementToScroll) {
            this.elementToScroll = window;
        }
        this.elementToScroll.addEventListener('scroll', this.onScroll, false);
    }
    /**
     * Performs actions after content has been checked.
     */
    ngAfterContentChecked() {
        if (this.elementToScrollId && this.elementToScroll && this.elementToScroll instanceof HTMLElement && typeof this.elementToScroll.getAttribute === 'undefined') {
            const element = this.document.getElementById(this.elementToScrollId);
            if (element) {
                this.elementToScroll.removeEventListener('scroll', this.onScroll, false);
                element.addEventListener('scroll', this.onScroll, false);
                this.elementToScroll = element;
            }
        }
    }
    /**
     * Performs actions before the component is destroyed.
     */
    ngOnDestroy() {
        this.elementToScroll?.removeEventListener('scroll', this.onScroll, false);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqInfinityComponent, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqInfinityComponent, selector: "sq-infinity-scroll", inputs: { length: "length", endMessage: "endMessage", hasMore: "hasMore", loading: "loading", loaderColor: "loaderColor", elementToScrollId: "elementToScrollId" }, outputs: { scrolledEmitter: "scrolledEmitter" }, viewQueries: [{ propertyName: "scrollElement", first: true, predicate: ["scroll"], descendants: true, static: true }], ngImport: i0, template: "<div #scroll class=\"scroll\">\r\n  <ng-content></ng-content>\r\n  <div *ngIf=\"loading\" class=\"display-flex justify-content-center\">\r\n    <sq-loader color=\"{{ loaderColor }}\"></sq-loader>\r\n  </div>\r\n  <div class=\"wrapper-message\" *ngIf=\"!hasMore && endMessage\">\r\n    <p>\r\n      {{ endMessage || '' }}\r\n    </p>\r\n  </div>\r\n</div>\r\n", styles: [".scroll{width:100%;height:auto}.scroll .wrapper-message p{text-align:center;font-size:.86rem;margin:1.1rem auto}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SqLoaderComponent, selector: "sq-loader", inputs: ["size", "customSize", "borderSize", "color", "customClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqInfinityComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-infinity-scroll', template: "<div #scroll class=\"scroll\">\r\n  <ng-content></ng-content>\r\n  <div *ngIf=\"loading\" class=\"display-flex justify-content-center\">\r\n    <sq-loader color=\"{{ loaderColor }}\"></sq-loader>\r\n  </div>\r\n  <div class=\"wrapper-message\" *ngIf=\"!hasMore && endMessage\">\r\n    <p>\r\n      {{ endMessage || '' }}\r\n    </p>\r\n  </div>\r\n</div>\r\n", styles: [".scroll{width:100%;height:auto}.scroll .wrapper-message p{text-align:center;font-size:.86rem;margin:1.1rem auto}\n"] }]
        }], ctorParameters: function () { return [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { scrollElement: [{
                type: ViewChild,
                args: ['scroll', { static: true }]
            }], length: [{
                type: Input
            }], endMessage: [{
                type: Input
            }], hasMore: [{
                type: Input
            }], loading: [{
                type: Input
            }], loaderColor: [{
                type: Input
            }], elementToScrollId: [{
                type: Input
            }], scrolledEmitter: [{
                type: Output
            }] } });

/**
 * Directive that emits an event when a click occurs outside of the bound element.
 *
 * @example
 * <!-- Add the clickOutside directive to an element and listen for the clickOutside event. -->
 * <div [clickOutside]="true" (clickOutside)="handleClickOutside()">
 *   <!-- Content here -->
 * </div>
 */
class SqClickOutsideDirective {
    /**
     * Constructs a new SqClickOutsideDirective.
     *
     * @param {ElementRef} elementRef - The ElementRef of the bound element.
     * @param {Renderer2} renderer - The Renderer2 for DOM manipulation.
     */
    constructor(elementRef, renderer) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        /**
         * Indicates whether the clickOutside functionality is enabled.
         */
        this.clickOutsideEnabled = false;
        /**
         * Event emitted when a click occurs outside of the bound element.
         */
        this.clickOutside = new EventEmitter();
    }
    /**
     * Lifecycle hook that handles changes to the clickOutsideEnabled property.
     *
     * @param {SimpleChanges} changes - The changes to input properties.
     */
    ngOnChanges(changes) {
        if (changes['clickOutsideEnabled']) {
            if (changes['clickOutsideEnabled'].currentValue) {
                this.createListener();
            }
            else if (typeof this.listener === 'function') {
                this.listener();
            }
        }
    }
    /**
     * Lifecycle hook that cleans up the directive when it is destroyed.
     */
    ngOnDestroy() {
        this.listener = () => null;
    }
    /**
     * Creates a click event listener to detect clicks outside of the bound element.
     */
    createListener() {
        this.listener = this.renderer.listen('window', 'click', ($event) => {
            const isClickedInside = this.elementRef.nativeElement.contains($event.target);
            if (!isClickedInside) {
                this.clickOutside.emit();
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqClickOutsideDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.8", type: SqClickOutsideDirective, selector: "[clickOutside]", inputs: { clickOutsideEnabled: "clickOutsideEnabled" }, outputs: { clickOutside: "clickOutside" }, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqClickOutsideDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[clickOutside]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { clickOutsideEnabled: [{
                type: Input
            }], clickOutside: [{
                type: Output
            }] } });

/**
 * Represents an overlay component, an abstraction with differente style but still a modal.
 *
 * @example
 * <sq-overlay [open]="isOverlayOpen" overlayDirection="right" (overlayClose)="onOverlayClose()">
 *   <ng-template #headerTemplate>
 *     <h2>Overlay Header</h2>
 *   </ng-template>
 *   <ng-template #footerTemplate>
 *     Footer
 *   </ng-template>
 *   <div>
 *     <!-- Your content here -->
 *   </div>
 * </sq-overlay>
 * <button (click)='isOverlayOpen = true'>Open Modal</button>
 *
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
class SqOverlayComponent {
    /**
     * Constructs an instance of SqOverlayComponent.
     *
     * @param {Document} documentImported - The injected Document object for DOM manipulation.
     * @param {Router} router - The Router service for programmatic navigation.
     */
    constructor(documentImported, router) {
        this.documentImported = documentImported;
        this.router = router;
        /**
         * A unique identifier for the overlay.
         */
        this.id = `overlay-random-id-${(1 + Date.now() + Math.random()).toString().replace('.', '')}`;
        /**
         * The direction in which the overlay slides in when opened.
         *
         */
        this.overlayDirection = 'right';
        /**
         * The width of the overlay.
         *
         */
        this.width = '475px';
        /**
         * Determines whether the overlay has a border.
         *
         */
        this.borderless = false;
        /**
         * The background color of the overlay header.
         *
         */
        this.headerColor = 'var(--background_secondary)';
        /**
         * The text color of items within the overlay header.
         *
         */
        this.headerItemsColor = '';
        /**
         * The background color of the overlay footer.
         *
         */
        this.footerColor = 'var(--background_secondary)';
        /**
         * The background color of the overlay body.
         *
        */
        this.bodyColor = 'var(--background_secondary)';
        /**
         * Determines whether the close button is shown.
         *
         */
        this.showClose = true;
        /**
         * Specifies the behavior of the backdrop when clicked.
         *
         */
        this.backdrop = 'static';
        /**
         * Emits an event when the overlay is closed.
         *
         */
        this.overlayClose = new EventEmitter();
        /**
         * Emits an event when the left arrow key is pressed.
         *
         */
        this.leftPress = new EventEmitter();
        /**
         * Emits an event when the right arrow key is pressed.
         *
         */
        this.rightPress = new EventEmitter();
        /**
         * A reference to the overlay element.
         *
         */
        this.overlay = null;
        /**
         * A reference to the header template.
         *
         */
        this.headerTemplate = null;
        /**
         * A reference to the footer template.
         *
         */
        this.footerTemplate = null;
        /**
         * The number of modal elements.
         *
         */
        this.modalNumber = 0;
        /**
         * Indicates whether the overlay has a header.
         *
         */
        this.hasHeader = false;
        /**
         * Indicates whether the overlay has a footer.
         *
         */
        this.hasFooter = false;
        /**
         * A unique style identifier.
         *
         */
        this.styleId = `overlay-style-random-id-${new Date().getTime()}-${Math.random().toString(36).substring(7)}`;
        /**
         * Indicates whether the overlay has finished opening.
         *
         */
        this.finishOpening = false;
        this.onKeydown = this.onKeydown.bind(this);
        this.document = documentImported || document;
        this.localized = new URL(window.location.href);
    }
    /**
     * Lifecycle hook that detects changes to the 'open' input property and handles modal behavior accordingly.
     *
     * @param changes - The changes detected in the component's input properties.
     */
    async ngOnChanges(changes) {
        if (changes.hasOwnProperty('width') && this.open) {
            this.doCssWidth();
        }
        if (changes.hasOwnProperty('open')) {
            const overlay = this.overlay;
            if (overlay) {
                const body = this.document.getElementsByTagName('body')[0];
                body.appendChild(overlay.nativeElement);
                const backdrop = this.document.getElementById('modal-backdrop') || this.document.createElement('div');
                if (this.open) {
                    this.observeRouter();
                    this.doCssWidth();
                    this.hasFooter = !!this.footerTemplate;
                    this.hasHeader = !!this.headerTemplate;
                    body.classList.add('block');
                    overlay.nativeElement.style.display = 'flex';
                    window.addEventListener('keydown', this.onKeydown);
                    this.modals = this.document.getElementsByClassName('modal open');
                    await sleep(10);
                    this.modalNumber = this.modals?.length || 0;
                    if (this.modalNumber <= 1) {
                        backdrop.setAttribute('id', 'modal-backdrop');
                        backdrop.setAttribute('class', 'modal-backdrop show');
                        body.appendChild(backdrop);
                    }
                    else if (this.modalNumber > 1) {
                        overlay.nativeElement.style.zIndex = 1060 + this.modalNumber + 1;
                        backdrop.setAttribute('style', `z-index: ${1060 + this.modalNumber};`);
                    }
                    this.finishOpening = true;
                }
                else {
                    this.removeOverlayFromBody();
                    window.removeEventListener('keydown', this.onKeydown);
                }
            }
        }
    }
    /**
     * Performs actions before the component is destroyed.
     */
    ngOnDestroy() {
        this.routerObservable?.unsubscribe();
    }
    /**
     * Function that init the routerObservable.
     */
    observeRouter() {
        this.routerObservable = this.router.events.subscribe(async (event) => {
            if (event instanceof NavigationStart) {
                const destinationRoute = new URL(event.url, this.localized.origin);
                if ((this.localized.origin + this.localized.pathname) !== (destinationRoute.origin + destinationRoute.pathname)) {
                    this.removeOverlayFromBody();
                    await sleep(1000);
                }
            }
        });
    }
    /**
     * Removes the overlay element from document body.
     */
    removeOverlayFromBody() {
        const body = this.document.getElementsByTagName('body')[0];
        const backdrop = this.document.getElementById('modal-backdrop');
        const overlay = this.document.getElementById(this.id);
        this.overlayClose.emit();
        this.finishOpening = false;
        this.undoCssWidth();
        backdrop?.removeAttribute('style');
        overlay?.parentNode?.removeChild(overlay);
        if (this.modalNumber <= 1) {
            backdrop?.parentNode?.removeChild(backdrop);
            body?.classList?.remove('block');
        }
    }
    /**
     * Applies CSS styles to set the width of the overlay.
     */
    doCssWidth() {
        const css = `
      .overlay.open .modal-dialog.opened {
        width: ${this.width};
      }
    `;
        const head = this.document.getElementsByTagName('head')[0];
        let style = this.document.getElementById(this.styleId);
        if (!style) {
            style = this.document.createElement('style');
            style.setAttribute('id', this.styleId);
            style.appendChild(this.document.createTextNode(css));
            head.appendChild(style);
        }
        else {
            style.innerHTML = '';
            style.appendChild(this.document.createTextNode(css));
        }
    }
    /**
     * Removes the CSS styles that set the width of the overlay.
     */
    undoCssWidth() {
        const style = this.document.getElementById(this.styleId);
        if (style?.parentNode) {
            style.parentNode.removeChild(style);
        }
    }
    /**
     * Handles keyboard events for the modal component.
     *
     * @param event - The keyboard event object.
     */
    onKeydown(event) {
        if (this.open) {
            this.modals = this.document.getElementsByClassName('modal open');
            if (this.modals?.length === this.modalNumber) {
                this.events(event.key);
            }
        }
    }
    /**
     * Handles specific keyboard events.
     *
     * @param key - The key code of the pressed key.
     */
    events(key) {
        switch (key) {
            case 'Escape':
                this.overlayClose.emit();
                break;
            case 'ArrowLeft':
                this.leftPress.emit();
                break;
            case 'ArrowRight':
                this.rightPress.emit();
                break;
        }
    }
    /**
     * Closes the overlay logic.
     */
    toCloseOverlay() {
        if (this.overlay && this.open) {
            const body = this.document.getElementsByTagName('body')[0];
            const backdrop = this.document.getElementById('modal-backdrop') || this.document.createElement('div');
            this.overlayClose.emit();
            this.overlay.nativeElement.style.display = 'none';
            if (backdrop.parentNode && this.modalNumber <= 1) {
                backdrop.parentNode.removeChild(backdrop);
                body.classList.remove('block');
            }
            window.removeEventListener('keydown', this.onKeydown);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqOverlayComponent, deps: [{ token: DOCUMENT }, { token: i1.Router }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqOverlayComponent, selector: "sq-overlay", inputs: { id: "id", open: "open", overlayDirection: "overlayDirection", width: "width", borderless: "borderless", headerColor: "headerColor", headerItemsColor: "headerItemsColor", footerColor: "footerColor", bodyColor: "bodyColor", showClose: "showClose", backdrop: "backdrop" }, outputs: { overlayClose: "overlayClose", leftPress: "leftPress", rightPress: "rightPress" }, queries: [{ propertyName: "headerTemplate", first: true, predicate: ["headerTemplate"], descendants: true }, { propertyName: "footerTemplate", first: true, predicate: ["footerTemplate"], descendants: true }], viewQueries: [{ propertyName: "overlay", first: true, predicate: ["overlay"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div [id]=\"id\" class=\"modal overlay {{ overlayDirection }}\" [ngClass]=\"{ open: open }\" #overlay>\r\n  <div\r\n    class=\"modal-dialog {{ overlayDirection }}\"\r\n    [ngStyle]=\"{ 'background-color': headerColor }\"\r\n    [ngClass]=\"{ opened: finishOpening }\"\r\n    [clickOutsideEnabled]=\"!!(open && backdrop !== 'static' && overlay)\"\r\n    (clickOutside)=\"toCloseOverlay()\"\r\n  >\r\n    <div class=\"modal-content scrollbar\">\r\n      <div\r\n        class=\"modal-header\"\r\n        [ngClass]=\"{\r\n          'without-header': !hasHeader,\r\n          borderless: borderless\r\n        }\"\r\n        [ngStyle]=\"{\r\n          'background-color': headerColor\r\n        }\"\r\n      >\r\n        <ng-container *ngIf=\"headerTemplate\">\r\n          <ng-container *ngTemplateOutlet=\"headerTemplate\"></ng-container>\r\n        </ng-container>\r\n\r\n        <button *ngIf=\"showClose\" id=\"close-button\" type=\"button\" class=\"close button-close\" aria-label=\"Close\" (click)=\"overlayClose.emit()\">\r\n          <i class=\"fa-solid fa-xmark\" [ngStyle]=\"{ color: headerItemsColor }\"></i>\r\n        </button>\r\n      </div>\r\n\r\n      <div\r\n        class=\"modal-body scrollbar\"\r\n        [ngClass]=\"{\r\n          'without-footer': !hasFooter\r\n        }\"\r\n        [ngStyle]=\"{\r\n          'background-color': bodyColor\r\n        }\"\r\n      >\r\n        <ng-content></ng-content>\r\n      </div>\r\n\r\n      <div\r\n        class=\"modal-footer\"\r\n        *ngIf=\"hasFooter\"\r\n        [ngClass]=\"{\r\n          borderless: borderless\r\n        }\"\r\n        [ngStyle]=\"{\r\n          'background-color': footerColor\r\n        }\"\r\n      >\r\n        <ng-container *ngIf=\"footerTemplate\">\r\n          <ng-container *ngTemplateOutlet=\"footerTemplate\"></ng-container>\r\n        </ng-container>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n", styles: ["::ng-deep .overlay{padding:0;border-radius:0}::ng-deep .overlay.left{justify-content:flex-start}::ng-deep .overlay.right{justify-content:flex-end}::ng-deep .overlay .modal-dialog{width:300px;max-width:100vw;height:100%;margin:0;position:fixed;transition:opacity .3s linear,left .3s ease-out,right .3s ease-out,width .3s ease-out,height .3s ease-out!important;transform:translateZ(0)!important}::ng-deep .overlay .modal-dialog .modal-content{height:100%;border-radius:0;overflow-y:auto;border:none;background-color:transparent}::ng-deep .overlay .modal-dialog .modal-content .modal-header{display:flex;justify-content:space-between;align-items:center;height:60px;border-radius:0;overflow:hidden;padding:1rem 2rem}::ng-deep .overlay .modal-dialog .modal-content .modal-header.without-header{padding:0 .08rem;border:none;justify-content:flex-end}::ng-deep .overlay .modal-dialog .modal-content .modal-header.without-header .button-close{margin:0;padding:0}::ng-deep .overlay .modal-dialog .modal-content .modal-header .button-close{max-height:30px;line-height:30px;opacity:.7!important;transition:var(--transition);z-index:1}::ng-deep .overlay .modal-dialog .modal-content .modal-header .button-close span{font-size:2.1rem;font-weight:300;transition:var(--transition)}::ng-deep .overlay .modal-dialog .modal-content .modal-header .button-close:hover,::ng-deep .overlay .modal-dialog .modal-content .modal-header .button-close:focus{outline:none}::ng-deep .overlay .modal-dialog .modal-content .modal-header .button-close:hover span,::ng-deep .overlay .modal-dialog .modal-content .modal-header .button-close:focus span{font-weight:500}::ng-deep .overlay .modal-dialog .modal-content .modal-body{height:calc(100vh - 112px);padding:2rem;overflow-y:auto;overflow-x:hidden}::ng-deep .overlay .modal-dialog .modal-content .modal-body.without-footer{height:calc(100vh - 52px)}::ng-deep .overlay .modal-dialog .modal-content .modal-footer{height:60px;border-radius:0;overflow:hidden;padding:.5rem 2rem}::ng-deep .overlay .modal-dialog .modal-content .modal-footer>*{margin:0}::ng-deep .overlay .modal-dialog .modal-content .borderless{border-color:transparent}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: SqClickOutsideDirective, selector: "[clickOutside]", inputs: ["clickOutsideEnabled"], outputs: ["clickOutside"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqOverlayComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-overlay', template: "<div [id]=\"id\" class=\"modal overlay {{ overlayDirection }}\" [ngClass]=\"{ open: open }\" #overlay>\r\n  <div\r\n    class=\"modal-dialog {{ overlayDirection }}\"\r\n    [ngStyle]=\"{ 'background-color': headerColor }\"\r\n    [ngClass]=\"{ opened: finishOpening }\"\r\n    [clickOutsideEnabled]=\"!!(open && backdrop !== 'static' && overlay)\"\r\n    (clickOutside)=\"toCloseOverlay()\"\r\n  >\r\n    <div class=\"modal-content scrollbar\">\r\n      <div\r\n        class=\"modal-header\"\r\n        [ngClass]=\"{\r\n          'without-header': !hasHeader,\r\n          borderless: borderless\r\n        }\"\r\n        [ngStyle]=\"{\r\n          'background-color': headerColor\r\n        }\"\r\n      >\r\n        <ng-container *ngIf=\"headerTemplate\">\r\n          <ng-container *ngTemplateOutlet=\"headerTemplate\"></ng-container>\r\n        </ng-container>\r\n\r\n        <button *ngIf=\"showClose\" id=\"close-button\" type=\"button\" class=\"close button-close\" aria-label=\"Close\" (click)=\"overlayClose.emit()\">\r\n          <i class=\"fa-solid fa-xmark\" [ngStyle]=\"{ color: headerItemsColor }\"></i>\r\n        </button>\r\n      </div>\r\n\r\n      <div\r\n        class=\"modal-body scrollbar\"\r\n        [ngClass]=\"{\r\n          'without-footer': !hasFooter\r\n        }\"\r\n        [ngStyle]=\"{\r\n          'background-color': bodyColor\r\n        }\"\r\n      >\r\n        <ng-content></ng-content>\r\n      </div>\r\n\r\n      <div\r\n        class=\"modal-footer\"\r\n        *ngIf=\"hasFooter\"\r\n        [ngClass]=\"{\r\n          borderless: borderless\r\n        }\"\r\n        [ngStyle]=\"{\r\n          'background-color': footerColor\r\n        }\"\r\n      >\r\n        <ng-container *ngIf=\"footerTemplate\">\r\n          <ng-container *ngTemplateOutlet=\"footerTemplate\"></ng-container>\r\n        </ng-container>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n", styles: ["::ng-deep .overlay{padding:0;border-radius:0}::ng-deep .overlay.left{justify-content:flex-start}::ng-deep .overlay.right{justify-content:flex-end}::ng-deep .overlay .modal-dialog{width:300px;max-width:100vw;height:100%;margin:0;position:fixed;transition:opacity .3s linear,left .3s ease-out,right .3s ease-out,width .3s ease-out,height .3s ease-out!important;transform:translateZ(0)!important}::ng-deep .overlay .modal-dialog .modal-content{height:100%;border-radius:0;overflow-y:auto;border:none;background-color:transparent}::ng-deep .overlay .modal-dialog .modal-content .modal-header{display:flex;justify-content:space-between;align-items:center;height:60px;border-radius:0;overflow:hidden;padding:1rem 2rem}::ng-deep .overlay .modal-dialog .modal-content .modal-header.without-header{padding:0 .08rem;border:none;justify-content:flex-end}::ng-deep .overlay .modal-dialog .modal-content .modal-header.without-header .button-close{margin:0;padding:0}::ng-deep .overlay .modal-dialog .modal-content .modal-header .button-close{max-height:30px;line-height:30px;opacity:.7!important;transition:var(--transition);z-index:1}::ng-deep .overlay .modal-dialog .modal-content .modal-header .button-close span{font-size:2.1rem;font-weight:300;transition:var(--transition)}::ng-deep .overlay .modal-dialog .modal-content .modal-header .button-close:hover,::ng-deep .overlay .modal-dialog .modal-content .modal-header .button-close:focus{outline:none}::ng-deep .overlay .modal-dialog .modal-content .modal-header .button-close:hover span,::ng-deep .overlay .modal-dialog .modal-content .modal-header .button-close:focus span{font-weight:500}::ng-deep .overlay .modal-dialog .modal-content .modal-body{height:calc(100vh - 112px);padding:2rem;overflow-y:auto;overflow-x:hidden}::ng-deep .overlay .modal-dialog .modal-content .modal-body.without-footer{height:calc(100vh - 52px)}::ng-deep .overlay .modal-dialog .modal-content .modal-footer{height:60px;border-radius:0;overflow:hidden;padding:.5rem 2rem}::ng-deep .overlay .modal-dialog .modal-content .modal-footer>*{margin:0}::ng-deep .overlay .modal-dialog .modal-content .borderless{border-color:transparent}\n"] }]
        }], ctorParameters: function () { return [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i1.Router }]; }, propDecorators: { id: [{
                type: Input
            }], open: [{
                type: Input
            }], overlayDirection: [{
                type: Input
            }], width: [{
                type: Input
            }], borderless: [{
                type: Input
            }], headerColor: [{
                type: Input
            }], headerItemsColor: [{
                type: Input
            }], footerColor: [{
                type: Input
            }], bodyColor: [{
                type: Input
            }], showClose: [{
                type: Input
            }], backdrop: [{
                type: Input
            }], overlayClose: [{
                type: Output
            }], leftPress: [{
                type: Output
            }], rightPress: [{
                type: Output
            }], overlay: [{
                type: ViewChild,
                args: ['overlay']
            }], headerTemplate: [{
                type: ContentChild,
                args: ['headerTemplate']
            }], footerTemplate: [{
                type: ContentChild,
                args: ['footerTemplate']
            }] } });

/**
 * Represents a modal component with customizable options and event handling.
 *
 * Look the link about the component in original framework and the appearance
 *
 * @see {@link https://css.squidit.com.br/components/modal}
 *
 * @example
 * <sq-modal [open]="isModalOpen" (modalClose)="onModalClose()">
 *   <ng-template #headerModal>
 *     <h2>Title</h2>
 *   </ng-template>
 *   <div>
 *     <!-- Your content here -->
 *   </div>
*    <ng-template #footerModal>
 *     Footer
 *   </ng-template>
 * </sq-modal>
 * <button (click)='isModalOpen = true'>Open Modal</button>
 *
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
class SqModalComponent {
    /**
     * Creates an instance of `SqModalComponent`.
     *
     * @param {Document} documentImported - The injected Document object for DOM manipulation.
     * @param {Router} router - The Router service for programmatic navigation.
     */
    constructor(documentImported, router) {
        this.documentImported = documentImported;
        this.router = router;
        /**
         * A unique identifier for the modal component.
         */
        this.id = `modal-random-id-${(1 + Date.now() + Math.random()).toString().replace('.', '')}`;
        /**
         * The size of the modal, which can be 'sm' (small), 'md' (medium), 'lg' (large), or 'xl' (extra large).
         */
        this.modalSize = 'md';
        /**
         * Determines whether clicking outside the modal closes it. Options: 'static' (no close), 'true' (close).
         */
        this.backdrop = 'static';
        /**
         * Determines whether to display the close button.
         */
        this.buttonClose = true;
        /**
         * Event emitted when the modal is closed.
         */
        this.modalClose = new EventEmitter();
        /**
         * Event emitted when the left arrow key is pressed while the modal is open.
         */
        this.leftPress = new EventEmitter();
        /**
         * Event emitted when the right arrow key is pressed while the modal is open.
         */
        this.rightPress = new EventEmitter();
        /**
         * Reference to the modal element in the component's template.
         */
        this.modal = null;
        /**
         * Reference to the header template provided in the component's content.
         */
        this.headerTemplate = null;
        /**
         * Reference to the footer template provided in the component's content.
         */
        this.footerTemplate = null;
        /**
         * The number of open modals in the document.
         */
        this.modalNumber = 0;
        /**
         * Indicates whether the modal has a header template.
         */
        this.hasHeader = false;
        this.onKeydown = this.onKeydown.bind(this);
        this.document = documentImported || document;
        this.localized = new URL(window.location.href);
    }
    /**
     * Lifecycle hook that detects changes to the 'open' input property and handles modal behavior accordingly.
     *
     * @param changes - The changes detected in the component's input properties.
     */
    async ngOnChanges(changes) {
        if (changes.hasOwnProperty('open')) {
            const modal = this.modal;
            if (modal) {
                const body = this.document.getElementsByTagName('body')[0];
                body.appendChild(modal.nativeElement);
                const backdrop = this.document.getElementById('modal-backdrop') || this.document.createElement('div');
                if (this.open) {
                    this.observeRouter();
                    this.hasHeader = !!this.headerTemplate;
                    body.classList.add('block');
                    modal.nativeElement.style.display = 'flex';
                    window.addEventListener('keydown', this.onKeydown);
                    this.modals = this.document.getElementsByClassName('modal open');
                    await sleep(10);
                    this.modalNumber = this.modals?.length || 0;
                    if (this.modalNumber <= 1) {
                        backdrop.setAttribute('id', 'modal-backdrop');
                        backdrop.setAttribute('class', 'modal-backdrop show');
                        body.appendChild(backdrop);
                    }
                    else if (this.modalNumber > 1) {
                        modal.nativeElement.style.zIndex = 1060 + this.modalNumber + 1;
                        backdrop.setAttribute('style', `z-index: ${1060 + this.modalNumber};`);
                    }
                }
                else {
                    this.removeModalFromBody();
                    window.removeEventListener('keydown', this.onKeydown);
                }
            }
        }
    }
    /**
     * Performs actions before the component is destroyed.
     */
    ngOnDestroy() {
        this.routerObservable?.unsubscribe();
    }
    /**
     * Function that init the routerObservable.
     */
    observeRouter() {
        this.routerObservable = this.router.events.subscribe(async (event) => {
            if (event instanceof NavigationStart) {
                const destinationRoute = new URL(event.url, this.localized.origin);
                if ((this.localized.origin + this.localized.pathname) !== (destinationRoute.origin + destinationRoute.pathname)) {
                    this.removeModalFromBody();
                    await sleep(1000);
                }
            }
        });
    }
    /**
     * Removes the modal element from document body.
     */
    removeModalFromBody() {
        const body = this.document.getElementsByTagName('body')[0];
        const backdrop = this.document.getElementById('modal-backdrop');
        const modal = this.document.getElementById(this.id);
        this.modalClose.emit();
        backdrop?.removeAttribute('style');
        modal?.parentNode?.removeChild(modal);
        if (this.modalNumber <= 1) {
            backdrop?.parentNode?.removeChild(backdrop);
            body?.classList?.remove('block');
        }
    }
    /**
     * Handles keyboard events for the modal component.
     *
     * @param event - The keyboard event object.
     */
    onKeydown(event) {
        if (this.open) {
            this.modals = this.document.getElementsByClassName('modal');
            if (this.modals?.length === this.modalNumber) {
                this.events(event.key);
            }
        }
    }
    /**
     * Handles specific keyboard events.
     *
     * @param key - The key code of the pressed key.
     */
    events(key) {
        switch (key) {
            case 'Escape':
                this.modalClose.emit();
                break;
            case 'ArrowLeft':
                this.leftPress.emit();
                break;
            case 'ArrowRight':
                this.rightPress.emit();
                break;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqModalComponent, deps: [{ token: DOCUMENT }, { token: i1.Router }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqModalComponent, selector: "sq-modal", inputs: { id: "id", open: "open", modalSize: "modalSize", modalClass: "modalClass", backdropClass: "backdropClass", backdrop: "backdrop", buttonClose: "buttonClose" }, outputs: { modalClose: "modalClose", leftPress: "leftPress", rightPress: "rightPress" }, queries: [{ propertyName: "headerTemplate", first: true, predicate: ["headerModal"], descendants: true }, { propertyName: "footerTemplate", first: true, predicate: ["footerModal"], descendants: true }], viewQueries: [{ propertyName: "modal", first: true, predicate: ["modal"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div\r\n  class=\"modal align-items-center {{ backdropClass }}\"\r\n  [id]=\"id\"\r\n  [ngClass]=\"{\r\n    open: open,\r\n  }\"\r\n  #modal\r\n>\r\n  <div\r\n    class=\"modal-dialog modal-{{ modalSize }} {{ modalClass }}\"\r\n    [clickOutsideEnabled]=\"!!(open && backdrop !== 'static' && modal)\"\r\n    (clickOutside)=\"modalClose.emit()\"\r\n  >\r\n    <div class=\"modal-content modal-sq\">\r\n      <div\r\n        class=\"modal-header\"\r\n        [ngClass]=\"{\r\n          'without-header': !hasHeader\r\n        }\"\r\n      >\r\n        <ng-container *ngIf=\"headerTemplate\">\r\n          <ng-container *ngTemplateOutlet=\"headerTemplate\"></ng-container>\r\n        </ng-container>\r\n        <button *ngIf=\"buttonClose\" type=\"button\" class=\"close button-close\" aria-label=\"Close\" (click)=\"modalClose.emit()\">\r\n          <i class=\"fa-solid fa-xmark fa-lg\"></i>\r\n        </button>\r\n      </div>\r\n\r\n      <div class=\"modal-body scrollbar\">\r\n        <ng-content></ng-content>\r\n      </div>\r\n\r\n      <div class=\"modal-footer\" *ngIf=\"footerTemplate\">\r\n        <ng-container *ngTemplateOutlet=\"footerTemplate\"></ng-container>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n", styles: [".modal.need-priority{z-index:1081}.modal .modal-sq{border-radius:4px}.modal .modal-sq .modal-header{display:flex;justify-content:space-between}.modal .modal-sq .without-header{padding:0 .08rem;border:none;justify-content:flex-end}.modal .modal-sq .without-header button.close{z-index:1;position:relative;top:5px;left:-4px;margin:0;padding:0}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: SqClickOutsideDirective, selector: "[clickOutside]", inputs: ["clickOutsideEnabled"], outputs: ["clickOutside"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-modal', template: "<div\r\n  class=\"modal align-items-center {{ backdropClass }}\"\r\n  [id]=\"id\"\r\n  [ngClass]=\"{\r\n    open: open,\r\n  }\"\r\n  #modal\r\n>\r\n  <div\r\n    class=\"modal-dialog modal-{{ modalSize }} {{ modalClass }}\"\r\n    [clickOutsideEnabled]=\"!!(open && backdrop !== 'static' && modal)\"\r\n    (clickOutside)=\"modalClose.emit()\"\r\n  >\r\n    <div class=\"modal-content modal-sq\">\r\n      <div\r\n        class=\"modal-header\"\r\n        [ngClass]=\"{\r\n          'without-header': !hasHeader\r\n        }\"\r\n      >\r\n        <ng-container *ngIf=\"headerTemplate\">\r\n          <ng-container *ngTemplateOutlet=\"headerTemplate\"></ng-container>\r\n        </ng-container>\r\n        <button *ngIf=\"buttonClose\" type=\"button\" class=\"close button-close\" aria-label=\"Close\" (click)=\"modalClose.emit()\">\r\n          <i class=\"fa-solid fa-xmark fa-lg\"></i>\r\n        </button>\r\n      </div>\r\n\r\n      <div class=\"modal-body scrollbar\">\r\n        <ng-content></ng-content>\r\n      </div>\r\n\r\n      <div class=\"modal-footer\" *ngIf=\"footerTemplate\">\r\n        <ng-container *ngTemplateOutlet=\"footerTemplate\"></ng-container>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n", styles: [".modal.need-priority{z-index:1081}.modal .modal-sq{border-radius:4px}.modal .modal-sq .modal-header{display:flex;justify-content:space-between}.modal .modal-sq .without-header{padding:0 .08rem;border:none;justify-content:flex-end}.modal .modal-sq .without-header button.close{z-index:1;position:relative;top:5px;left:-4px;margin:0;padding:0}\n"] }]
        }], ctorParameters: function () { return [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i1.Router }]; }, propDecorators: { id: [{
                type: Input
            }], open: [{
                type: Input
            }], modalSize: [{
                type: Input
            }], modalClass: [{
                type: Input
            }], backdropClass: [{
                type: Input
            }], backdrop: [{
                type: Input
            }], buttonClose: [{
                type: Input
            }], modalClose: [{
                type: Output
            }], leftPress: [{
                type: Output
            }], rightPress: [{
                type: Output
            }], modal: [{
                type: ViewChild,
                args: ['modal']
            }], headerTemplate: [{
                type: ContentChild,
                args: ['headerModal']
            }], footerTemplate: [{
                type: ContentChild,
                args: ['footerModal']
            }] } });

/**
 * Represents the SqCollapseComponent, a collapsible container component with customizable options.
 *
 * Look the link about the component in original framework and the appearance
 *
 * @see {@link https://css.squidit.com.br/components/accordion-collapse}
 *
 * @example
 * <sq-collapse [open]="true" color="blue" (openedEmitter)="onOpened($event)">
 *   <ng-container header>
 *     <div>Header Content</div>
 *   </ng-container>
 *   <div>Body Content</div>
 * </sq-collapse>
 */
class SqCollapseComponent {
    /**
     * Component Constructor
     * @param colorsHelper - The ColorsHelper instance
     */
    constructor(colorsHelper) {
        this.colorsHelper = colorsHelper;
        /**
         * Indicates whether the collapse is initially open.
         */
        this.open = false;
        /**
         * The color scheme of the collapse component.
         */
        this.color = '';
        /**
         * The color of the collapse icons.
         */
        this.colorIcons = '';
        /**
         * The background color of the collapse icon.
         */
        this.colorBackgroundIcon = '';
        /**
         * Custom CSS class to be applied to the collapse component.
         */
        this.class = '';
        /**
         * Indicates whether to remove padding from the collapse content.
         */
        this.noPadding = false;
        /**
         * Event emitter for when the collapse is opened or closed.
         */
        this.openedEmitter = new EventEmitter();
        /**
         * Reference to the header content template.
         */
        this.headerTemplate = null;
        /**
         * Indicates whether the collapse is in the process of opening.
         */
        this.opening = false;
        /**
         * Indicates whether the mouse is hovering over the header.
         */
        this.hoverHeader = false;
        /**
         * Indicates whether the mouse is hovering over the collapse icon.
         */
        this.hoverIcon = false;
        /**
         * Sets the hover state for a given color.
         * @param color - The color for which to set the hover state.
         * @returns The modified color.
         */
        this.setHover = useMemo((color) => {
            return this.colorsHelper?.lightenDarkenColor(this.colorsHelper?.getCssVariableValue(color), -25);
        });
    }
    /**
     * Toggles the state of the collapse component.
     */
    async toggleCollapse() {
        if (!this.disabled && !this.loading && !this.opening) {
            this.opening = this.wrapper?.nativeElement?.clientHeight + 'px';
            clearTimeout(this.timeout);
            this.timeout = setTimeout(() => {
                this.opening = false;
                this.open = !this.open;
            }, 500);
        }
    }
    /**
     * Emits an event when the collapse is opened or closed.
     * @param element - The HTML element associated with the collapse.
     */
    emit(element) {
        this.openedEmitter.emit({
            open: !this.open,
            element,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqCollapseComponent, deps: [{ token: ColorsHelper }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqCollapseComponent, selector: "sq-collapse", inputs: { open: "open", loading: "loading", disabled: "disabled", color: "color", colorIcons: "colorIcons", colorBackgroundIcon: "colorBackgroundIcon", fontSizeIcon: "fontSizeIcon", heightIcon: "heightIcon", class: "class", noPadding: "noPadding" }, outputs: { openedEmitter: "openedEmitter" }, queries: [{ propertyName: "headerTemplate", first: true, predicate: ["header"], descendants: true }], viewQueries: [{ propertyName: "element", first: true, predicate: ["element"], descendants: true }, { propertyName: "content", first: true, predicate: ["content"], descendants: true }, { propertyName: "wrapper", first: true, predicate: ["wrapper"], descendants: true }], ngImport: i0, template: "<div [class]=\"'wrapper-collapse ' + class\">\r\n  <header\r\n    #element\r\n    (click)=\"emit(element); toggleCollapse()\"\r\n    class=\"header-collapse\"\r\n    [ngStyle]=\"{\r\n      'background-color': hoverHeader ? setHover(color) : color,\r\n      'border-color': hoverHeader ? setHover(color) : color,\r\n    }\"\r\n    [ngClass]=\"{\r\n      disabled: disabled,\r\n      loading: loading,\r\n      'not-header': !headerTemplate,\r\n      opened: open,\r\n      'p-0': noPadding\r\n    }\"\r\n    (mouseover)=\"hoverHeader = true\"\r\n    (mouseleave)=\"hoverHeader = false\"\r\n  >\r\n    <span class=\"html\" *ngIf=\"headerTemplate\">\r\n      <ng-container *ngTemplateOutlet=\"headerTemplate\"></ng-container>\r\n    </span>\r\n    <div\r\n      class=\"wrapper-icons\"\r\n      [ngStyle]=\"{\r\n        color: colorIcons,\r\n        'background-color': hoverIcon ? setHover(colorBackgroundIcon) : colorBackgroundIcon,\r\n        'font-size': fontSizeIcon,\r\n        height: heightIcon,\r\n        'line-height': heightIcon\r\n      }\"\r\n      (mouseover)=\"hoverIcon = true\"\r\n      (mouseleave)=\"hoverIcon = false\"\r\n    >\r\n      <i\r\n        class=\"fa-solid fa-chevron-down icon\"\r\n        [ngClass]=\"{\r\n          'fa-rotate-180': !disabled && open\r\n        }\"\r\n        *ngIf=\"!loading\"\r\n      ></i>\r\n      <div *ngIf=\"loading\" class=\"display-flex justify-content-center align-items-center\" style=\"height: 100%\">\r\n        <sq-loader color=\"inherit\"></sq-loader>\r\n      </div>\r\n    </div>\r\n  </header>\r\n  <div\r\n    class=\"content animated scrollbar\"\r\n    [ngClass]=\"{\r\n      open: open && !disabled && !loading,\r\n      disabled: disabled,\r\n      loading: loading,\r\n      opening: opening\r\n    }\"\r\n    [ngStyle]=\"{\r\n      height: this.opening ? this.opening : null\r\n    }\"\r\n    #content\r\n  >\r\n    <div #wrapper>\r\n      <ng-content></ng-content>\r\n    </div>\r\n  </div>\r\n</div>\r\n", styles: [".wrapper-collapse{display:block;margin:.35rem auto}.wrapper-collapse .header-collapse{display:flex;align-items:stretch;border-radius:5px;padding:.7rem 1.1rem;background:var(--lilac_light);transition:var(--transition)}.wrapper-collapse .header-collapse:hover,.wrapper-collapse .header-collapse:focus{background:var(--cian)}.wrapper-collapse .header-collapse.p-0{padding:0}.wrapper-collapse .header-collapse span.html{cursor:pointer;display:inline-block;width:calc(100% - 50px);font-size:1.1rem;font-weight:300;transition:var(--transition);margin:0 1.1rem 0 0;line-height:25px}.wrapper-collapse .header-collapse .wrapper-icons{cursor:pointer;padding:0;margin:0;font-size:1.1rem;width:45px;height:25px;line-height:25px;transition:var(--transition);position:relative;overflow:hidden;border-radius:5px}.wrapper-collapse .header-collapse .wrapper-icons .icon{width:100%;height:100%;display:flex;align-items:center;justify-content:center;transition:var(--transition)}.wrapper-collapse .header-collapse .wrapper-icons .icon.spinner-border{color:inherit;position:relative;top:-3px;width:17px;height:17px}.wrapper-collapse .header-collapse.disabled,.wrapper-collapse .header-collapse.disabled:hover,.wrapper-collapse .header-collapse.disabled:focus{color:var(--color_text-icon_neutral_disabled)!important;border-color:var(--color_border_input_disabled)!important;background:var(--color_bg_input_disabled)!important}.wrapper-collapse .header-collapse.disabled span.html,.wrapper-collapse .header-collapse.disabled:hover span.html,.wrapper-collapse .header-collapse.disabled:focus span.html{cursor:not-allowed;color:var(--color_text-icon_neutral_disabled)!important}.wrapper-collapse .header-collapse.disabled .wrapper-icons,.wrapper-collapse .header-collapse.disabled:hover .wrapper-icons,.wrapper-collapse .header-collapse.disabled:focus .wrapper-icons{cursor:not-allowed;background:var(--color_bg_input_disabled)!important}.wrapper-collapse .header-collapse sq-loader{display:inline-block}.wrapper-collapse .header-collapse.loading .wrapper-icons,.wrapper-collapse .header-collapse.loading span.html{cursor:wait}.wrapper-collapse .header-collapse.not-header{width:100%;padding:.35rem;display:inline-block;text-align:center}.wrapper-collapse .header-collapse.not-header span.html{width:calc(100% - 50px)}.wrapper-collapse .header-collapse.not-header .wrapper-icons{line-height:35px}.wrapper-collapse .content{border-radius:5px;margin:0;transition:all .5s ease;height:0;overflow:hidden;visibility:hidden;position:relative}.wrapper-collapse .content.opening{visibility:visible;margin:0 auto}.wrapper-collapse .content.open{margin:0 auto;height:auto;visibility:visible;overflow-y:auto}.wrapper-collapse .content.loading,.wrapper-collapse .content.disabled{height:0!important;max-height:0;opacity:0;margin:0;visibility:hidden}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: SqLoaderComponent, selector: "sq-loader", inputs: ["size", "customSize", "borderSize", "color", "customClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqCollapseComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-collapse', template: "<div [class]=\"'wrapper-collapse ' + class\">\r\n  <header\r\n    #element\r\n    (click)=\"emit(element); toggleCollapse()\"\r\n    class=\"header-collapse\"\r\n    [ngStyle]=\"{\r\n      'background-color': hoverHeader ? setHover(color) : color,\r\n      'border-color': hoverHeader ? setHover(color) : color,\r\n    }\"\r\n    [ngClass]=\"{\r\n      disabled: disabled,\r\n      loading: loading,\r\n      'not-header': !headerTemplate,\r\n      opened: open,\r\n      'p-0': noPadding\r\n    }\"\r\n    (mouseover)=\"hoverHeader = true\"\r\n    (mouseleave)=\"hoverHeader = false\"\r\n  >\r\n    <span class=\"html\" *ngIf=\"headerTemplate\">\r\n      <ng-container *ngTemplateOutlet=\"headerTemplate\"></ng-container>\r\n    </span>\r\n    <div\r\n      class=\"wrapper-icons\"\r\n      [ngStyle]=\"{\r\n        color: colorIcons,\r\n        'background-color': hoverIcon ? setHover(colorBackgroundIcon) : colorBackgroundIcon,\r\n        'font-size': fontSizeIcon,\r\n        height: heightIcon,\r\n        'line-height': heightIcon\r\n      }\"\r\n      (mouseover)=\"hoverIcon = true\"\r\n      (mouseleave)=\"hoverIcon = false\"\r\n    >\r\n      <i\r\n        class=\"fa-solid fa-chevron-down icon\"\r\n        [ngClass]=\"{\r\n          'fa-rotate-180': !disabled && open\r\n        }\"\r\n        *ngIf=\"!loading\"\r\n      ></i>\r\n      <div *ngIf=\"loading\" class=\"display-flex justify-content-center align-items-center\" style=\"height: 100%\">\r\n        <sq-loader color=\"inherit\"></sq-loader>\r\n      </div>\r\n    </div>\r\n  </header>\r\n  <div\r\n    class=\"content animated scrollbar\"\r\n    [ngClass]=\"{\r\n      open: open && !disabled && !loading,\r\n      disabled: disabled,\r\n      loading: loading,\r\n      opening: opening\r\n    }\"\r\n    [ngStyle]=\"{\r\n      height: this.opening ? this.opening : null\r\n    }\"\r\n    #content\r\n  >\r\n    <div #wrapper>\r\n      <ng-content></ng-content>\r\n    </div>\r\n  </div>\r\n</div>\r\n", styles: [".wrapper-collapse{display:block;margin:.35rem auto}.wrapper-collapse .header-collapse{display:flex;align-items:stretch;border-radius:5px;padding:.7rem 1.1rem;background:var(--lilac_light);transition:var(--transition)}.wrapper-collapse .header-collapse:hover,.wrapper-collapse .header-collapse:focus{background:var(--cian)}.wrapper-collapse .header-collapse.p-0{padding:0}.wrapper-collapse .header-collapse span.html{cursor:pointer;display:inline-block;width:calc(100% - 50px);font-size:1.1rem;font-weight:300;transition:var(--transition);margin:0 1.1rem 0 0;line-height:25px}.wrapper-collapse .header-collapse .wrapper-icons{cursor:pointer;padding:0;margin:0;font-size:1.1rem;width:45px;height:25px;line-height:25px;transition:var(--transition);position:relative;overflow:hidden;border-radius:5px}.wrapper-collapse .header-collapse .wrapper-icons .icon{width:100%;height:100%;display:flex;align-items:center;justify-content:center;transition:var(--transition)}.wrapper-collapse .header-collapse .wrapper-icons .icon.spinner-border{color:inherit;position:relative;top:-3px;width:17px;height:17px}.wrapper-collapse .header-collapse.disabled,.wrapper-collapse .header-collapse.disabled:hover,.wrapper-collapse .header-collapse.disabled:focus{color:var(--color_text-icon_neutral_disabled)!important;border-color:var(--color_border_input_disabled)!important;background:var(--color_bg_input_disabled)!important}.wrapper-collapse .header-collapse.disabled span.html,.wrapper-collapse .header-collapse.disabled:hover span.html,.wrapper-collapse .header-collapse.disabled:focus span.html{cursor:not-allowed;color:var(--color_text-icon_neutral_disabled)!important}.wrapper-collapse .header-collapse.disabled .wrapper-icons,.wrapper-collapse .header-collapse.disabled:hover .wrapper-icons,.wrapper-collapse .header-collapse.disabled:focus .wrapper-icons{cursor:not-allowed;background:var(--color_bg_input_disabled)!important}.wrapper-collapse .header-collapse sq-loader{display:inline-block}.wrapper-collapse .header-collapse.loading .wrapper-icons,.wrapper-collapse .header-collapse.loading span.html{cursor:wait}.wrapper-collapse .header-collapse.not-header{width:100%;padding:.35rem;display:inline-block;text-align:center}.wrapper-collapse .header-collapse.not-header span.html{width:calc(100% - 50px)}.wrapper-collapse .header-collapse.not-header .wrapper-icons{line-height:35px}.wrapper-collapse .content{border-radius:5px;margin:0;transition:all .5s ease;height:0;overflow:hidden;visibility:hidden;position:relative}.wrapper-collapse .content.opening{visibility:visible;margin:0 auto}.wrapper-collapse .content.open{margin:0 auto;height:auto;visibility:visible;overflow-y:auto}.wrapper-collapse .content.loading,.wrapper-collapse .content.disabled{height:0!important;max-height:0;opacity:0;margin:0;visibility:hidden}\n"] }]
        }], ctorParameters: function () { return [{ type: ColorsHelper }]; }, propDecorators: { open: [{
                type: Input
            }], loading: [{
                type: Input
            }], disabled: [{
                type: Input
            }], color: [{
                type: Input
            }], colorIcons: [{
                type: Input
            }], colorBackgroundIcon: [{
                type: Input
            }], fontSizeIcon: [{
                type: Input
            }], heightIcon: [{
                type: Input
            }], class: [{
                type: Input
            }], noPadding: [{
                type: Input
            }], openedEmitter: [{
                type: Output
            }], headerTemplate: [{
                type: ContentChild,
                args: ['header']
            }], element: [{
                type: ViewChild,
                args: ['element']
            }], content: [{
                type: ViewChild,
                args: ['content']
            }], wrapper: [{
                type: ViewChild,
                args: ['wrapper']
            }] } });

/**
 * Represents the SqAccordionComponent, an accordion component that manages a collection of SqCollapseComponents.
 *
 * @implements {AfterContentInit}
 * @implements {OnDestroy}
 *
 * Look the link about the component in original framework and the appearance
 *
 * @see {@link https://css.squidit.com.br/components/accordion-collapse}
 *
 * @example
 * <sq-accordion [onlyOne]="true">
 *   <sq-collapse>
*      <ng-container header>
 *        <div>Header Content</div>
 *      </ng-container>
 *      Body Collpase content
 *    </sq-collapse>
 *   <sq-collapse>
 *    Body Collpase content
 *   </sq-collapse>
 * </sq-accordion>
 */
class SqAccordionComponent {
    constructor() {
        /**
         * A QueryList containing the SqCollapseComponent instances within the accordion.
         */
        this.collapses = [];
    }
    /**
     * Performs actions after the content has been initialized.
     */
    async ngAfterContentInit() {
        if (this.openFirst) {
            const collapses = this.collapses.toArray();
            if (collapses?.length) {
                await sleep();
                this.collapses.toArray()[0].toggleCollapse();
            }
        }
        this.collapses.toArray().forEach((collapse) => {
            collapse.openedEmitter.subscribe(() => {
                this.openCollapse(collapse);
            });
        });
        this.collapses.changes.subscribe(() => {
            this.collapses.toArray().forEach((collapse) => {
                collapse.openedEmitter.subscribe(() => {
                    this.openCollapse(collapse);
                });
            });
        });
    }
    /**
     * Performs actions before the component is destroyed.
     */
    ngOnDestroy() {
        if (this.collapses) {
            this.collapses.toArray().forEach((collapse) => {
                collapse.openedEmitter.unsubscribe();
            });
        }
    }
    /**
     * Opens or closes a specified SqCollapseComponent.
     * @param collapse - The SqCollapseComponent to open or close.
     */
    openCollapse(collapse) {
        if (this.onlyOne) {
            this.collapses.toArray().forEach((thisCollapse) => {
                if (thisCollapse.open) {
                    thisCollapse.toggleCollapse();
                }
            });
        }
        collapse.toggleCollapse();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqAccordionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqAccordionComponent, selector: "sq-accordion", inputs: { onlyOne: "onlyOne", openFirst: "openFirst" }, queries: [{ propertyName: "collapses", predicate: SqCollapseComponent }], ngImport: i0, template: "<div class='accordion-container'>\r\n  <ng-content></ng-content>\r\n</div>", styles: [".accordion-container{display:inline-block;margin:0 auto;padding:0;width:100%}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqAccordionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-accordion', template: "<div class='accordion-container'>\r\n  <ng-content></ng-content>\r\n</div>", styles: [".accordion-container{display:inline-block;margin:0 auto;padding:0;width:100%}\n"] }]
        }], propDecorators: { onlyOne: [{
                type: Input
            }], openFirst: [{
                type: Input
            }], collapses: [{
                type: ContentChildren,
                args: [SqCollapseComponent]
            }] } });

/**
 * Represents a pagination component for navigating through pages.
 *
 * Look the link about the component in original framework and the appearance
 *
 * @see {@link https://css.squidit.com.br/components/pagination}
 *
 *  <div class='my-3' >
 *    <ul class="pagination" style='padding: 0; margin: 0;'>
 *      <li class="disabled">
 *        <a>
 *          <i class="fas fa-chevron-left"></i>
 *        </a>
 *      </li>
 *      <li>
 *        <a>1</a>
 *      </li>
 *      <li class='active'>
 *        <a>2</a>
 *      </li>
 *      <li>
 *        <a>3</a>
 *      </li>
 *      <li>
 *        <a>
 *          <i class="fas fa-chevron-right"></i>
 *        </a>
 *      </li>
 *    </ul>
 *  </div>
 *
 * @example
 * <sq-pagination [currentPage]="currentPage" [totalPages]="totalPages" [showPages]="5" (pageChange)="onPageChange($event)"></sq-pagination>
 *
 * @implements {OnInit, OnChanges, OnDestroy}
 */
class SqPaginationComponent {
    /**
     * Initializes a new instance of the `SqPaginationComponent` class.
     *
     * @param {ActivatedRoute} route - The ActivatedRoute service for retrieving route information.
     * @param {Router} router - The Router service for programmatic navigation.
     */
    constructor(route, router) {
        this.route = route;
        this.router = router;
        /**
         * A custom CSS class for styling the component.
         */
        this.customClass = '';
        /**
         * The current page number.
         */
        this.currentPage = 1;
        /**
         * The total number of pages.
         */
        this.totalPages = 1;
        /**
         * The number of page links to show in the pagination control.
         */
        this.showPages = 5;
        /**
         * Indicates whether to use query string parameters for page navigation.
         */
        this.useQueryString = false;
        /**
         * Emits an event when the current page is changed.
         */
        this.pageChange = new EventEmitter();
        /**
         * The current page number.
         */
        this.page = this.currentPage;
        /**
         * An array of page numbers to display in the pagination control.
         */
        this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
        /**
         * Checks if a page number should be displayed based on the current page and showPages setting.
         *
         * @param {number} actualPage - The page number to check.
         * @returns {boolean} - `true` if the page should be shown, otherwise `false`.
         */
        this.canShow = useMemo((actualPage) => {
            const half = this.calculateHalf();
            return actualPage === this.page || (actualPage >= this.page - half && actualPage <= this.page + half);
        });
        /**
         * Checks if the maximum page number dot should be shown.
         *
         * @param {number} actualPage - The page number to check.
         * @returns {boolean} - `true` if the dot should be shown, otherwise `false`.
         */
        this.showDotMax = useMemo((actualPage) => {
            const half = this.calculateHalf();
            return actualPage + half < this.totalPages;
        });
        /**
         * Checks if the minimum page number dot should be shown.
         *
         * @param {number} actualPage - The page number to check.
         * @returns {boolean} - `true` if the dot should be shown, otherwise `false`.
         */
        this.showDotMin = useMemo((actualPage) => {
            const half = this.calculateHalf();
            return actualPage - half > 1;
        });
    }
    /**
     * Initializes the component.
     */
    ngOnInit() {
        this.createOrDestroyQueryObservable();
    }
    /**
     * Responds to changes in input properties.
     *
     * @param {SimpleChanges} changes - The changes in input properties.
     */
    ngOnChanges(changes) {
        if (changes['currentPage'] && changes['currentPage'].currentValue !== changes['currentPage'].previousValue) {
            this.page = this.currentPage;
        }
        if (changes['totalPages'] && changes['totalPages'].currentValue !== changes['totalPages'].previousValue) {
            this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
        }
        if (changes['useQueryString'] && changes['useQueryString'].currentValue !== changes['useQueryString'].previousValue) {
            this.createOrDestroyQueryObservable();
        }
    }
    /**
     * Cleans up resources when the component is destroyed.
     */
    ngOnDestroy() {
        this.destroyQueryObservable();
    }
    /**
     * Creates or destroys the query parameter observable based on the `useQueryString` input.
     */
    createOrDestroyQueryObservable() {
        if (this.useQueryString) {
            this.mountQueryObservable();
        }
        else {
            this.destroyQueryObservable();
        }
    }
    /**
     * Mounts the query parameter observable to track page changes in the URL query string.
     */
    mountQueryObservable() {
        this.routeObservable = this.route.queryParams.subscribe(search => {
            const searchParams = new URLSearchParams(search);
            const newPageQuery = parseInt(searchParams.get('page') || '1', 10);
            if (newPageQuery !== this.page) {
                this.page = newPageQuery;
            }
        });
    }
    /**
     * Destroys the query parameter observable and removes the 'page' query parameter from the URL.
     */
    destroyQueryObservable() {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete('page');
        this.router.navigate([], { relativeTo: this.route, queryParams: { page: null }, queryParamsHandling: 'merge' });
        this.routeObservable?.unsubscribe();
    }
    /**
     * Calculates half of the `showPages` setting for determining which pages to display.
     */
    calculateHalf() {
        return Math.floor(this.showPages / 2);
    }
    /**
     * Handles a page change event and updates the page number.
     *
     * @param {number} newPage - The new page number.
     */
    handlePageChange(newPage) {
        if (newPage < 1 || newPage > this.totalPages) {
            return;
        }
        if (this.useQueryString) {
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set('page', newPage.toString());
            this.router.navigate([], { relativeTo: this.route, queryParams: { page: newPage }, queryParamsHandling: 'merge' });
        }
        this.page = newPage;
        this.pageChange.emit(newPage);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqPaginationComponent, deps: [{ token: i1.ActivatedRoute }, { token: i1.Router }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqPaginationComponent, selector: "sq-pagination", inputs: { customClass: "customClass", currentPage: "currentPage", totalPages: "totalPages", showPages: "showPages", useQueryString: "useQueryString" }, outputs: { pageChange: "pageChange" }, usesOnChanges: true, ngImport: i0, template: "\n<div class='pagination-wrapper {{ customClass }}'>\n  <ul class=\"pagination\">\n    <li class='page-item' [ngClass]=\"{\n      'disabled': page === 1,\n    }\">\n      <button (click)='handlePageChange(page - 1)'>\n        <i class=\"fa-solid fa-chevrons-left\"></i>\n      </button>\n    </li>\n\n    <li class=\"disabled\" *ngIf=\"showDotMin(page)\">\n      <button>\n        <i class=\"fa-solid fa-ellipsis\"></i>\n      </button>\n    </li>\n\n    <ng-container *ngFor=\"let p of pages\">\n      <li\n        [ngClass]=\"{\n          'active': page === p,\n        }\"\n        *ngIf=\"canShow(p)\"\n      >\n        <button (click)='handlePageChange(p)'>\n          {{ p }}\n        </button>\n      </li>\n    </ng-container>\n\n    <li class=\"disabled\" *ngIf=\"showDotMax(page)\">\n      <button>\n        <i class=\"fa-solid fa-ellipsis\"></i>\n      </button>\n    </li>\n\n    <li class='page-item' [ngClass]=\"{\n      'disabled': page >= totalPages,\n    }\">\n      <button (click)='handlePageChange(page + 1)'>\n        <i class=\"fa-solid fa-chevrons-right\"></i>\n      </button>\n    </li>\n  </ul>\n</div>\n", styles: [".pagination-wrapper{width:100%;text-align:center}.pagination li.active{color:var(--primary_color)}:host{display:inline-block}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqPaginationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-pagination', template: "\n<div class='pagination-wrapper {{ customClass }}'>\n  <ul class=\"pagination\">\n    <li class='page-item' [ngClass]=\"{\n      'disabled': page === 1,\n    }\">\n      <button (click)='handlePageChange(page - 1)'>\n        <i class=\"fa-solid fa-chevrons-left\"></i>\n      </button>\n    </li>\n\n    <li class=\"disabled\" *ngIf=\"showDotMin(page)\">\n      <button>\n        <i class=\"fa-solid fa-ellipsis\"></i>\n      </button>\n    </li>\n\n    <ng-container *ngFor=\"let p of pages\">\n      <li\n        [ngClass]=\"{\n          'active': page === p,\n        }\"\n        *ngIf=\"canShow(p)\"\n      >\n        <button (click)='handlePageChange(p)'>\n          {{ p }}\n        </button>\n      </li>\n    </ng-container>\n\n    <li class=\"disabled\" *ngIf=\"showDotMax(page)\">\n      <button>\n        <i class=\"fa-solid fa-ellipsis\"></i>\n      </button>\n    </li>\n\n    <li class='page-item' [ngClass]=\"{\n      'disabled': page >= totalPages,\n    }\">\n      <button (click)='handlePageChange(page + 1)'>\n        <i class=\"fa-solid fa-chevrons-right\"></i>\n      </button>\n    </li>\n  </ul>\n</div>\n", styles: [".pagination-wrapper{width:100%;text-align:center}.pagination li.active{color:var(--primary_color)}:host{display:inline-block}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.ActivatedRoute }, { type: i1.Router }]; }, propDecorators: { customClass: [{
                type: Input
            }], currentPage: [{
                type: Input
            }], totalPages: [{
                type: Input
            }], showPages: [{
                type: Input
            }], useQueryString: [{
                type: Input
            }], pageChange: [{
                type: Output
            }] } });

/**
 * Represents a tag component with customizable appearance and behavior.
 *
 * Look the link about the component in original framework and the appearance
 *
 * @see {@link https://css.squidit.com.br/components/tag}
 *
 *  <div class=' my-3 tag-box'>
 *    Tag Content
 *  </div>
 *
 * @example
 * <sq-tag>Tag Content</sq-tag>
 */
class SqTagComponent {
    /**
   * Constructor for the SqButtonComponent class.
   * @param colorsHelper - The ColorsHelper service for color manipulation.
   */
    constructor(colorsHelper) {
        this.colorsHelper = colorsHelper;
        /**
         * Additional CSS classes for styling the tag.
         */
        this.customClass = '';
        /**
         * Flag to round the corners of the tag.
         */
        this.rounded = false;
        /**
         * The text color of the tag.
         */
        this.color = '';
        /**
         * The background color of the tag.
         */
        this.backgroundColor = '';
        /**
         * Flag to make the tag readonly.
         */
        this.readonly = false;
        /**
         * Flag to disable the tag.
         */
        this.disabled = false;
        /**
         * Flag to disable the tag.
         */
        this.pointer = false;
        /**
         * Event emitted when the tag is clicked.
         */
        this.emitClick = new EventEmitter();
        /**
         * Retrieves the computed text color for the tag.
         *
         * @returns {string} The computed text color.
         */
        this.getColor = useMemo(() => {
            if (this.validatePresetColors()) {
                return '';
            }
            return this.color;
        });
        /**
         * Retrieves the computed background color for the tag.
         *
         * @returns {string} The computed background color.
         */
        this.getBackgroundColor = useMemo(() => {
            if (this.validatePresetColors()) {
                return '';
            }
            return this.backgroundColor;
        });
    }
    /**
     * Validates whether the specified color is a preset color.
     *
     * @returns {boolean} True if the color is a valid preset color, false otherwise.
     */
    validatePresetColors() {
        return !!this.colorsHelper?.getCssVariableValue(this.color);
    }
    /**
     * Handles the click event on the tag.
     * Emits the `emitClick` event if the tag is not readonly or disabled.
     */
    handleClick() {
        if (this.readonly || this.disabled) {
            return;
        }
        this.emitClick.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqTagComponent, deps: [{ token: ColorsHelper }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqTagComponent, selector: "sq-tag", inputs: { customClass: "customClass", rounded: "rounded", color: "color", backgroundColor: "backgroundColor", readonly: "readonly", disabled: "disabled", pointer: "pointer" }, outputs: { emitClick: "emitClick" }, ngImport: i0, template: "<div\n  class='tag-box background-{{ backgroundColor }} {{ color }}'\n  [ngClass]=\"{\n    'readonly-tag': readonly,\n    'disabled': disabled,\n    'rounded': rounded,\n    'pointer': pointer,\n  }\"\n  [ngStyle]=\"{\n    'color': getColor(),\n    'background-color': getBackgroundColor(),\n  }\"\n  (click)=\"handleClick()\"\n>\n  <ng-content></ng-content>\n</div>", styles: [""], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqTagComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-tag', template: "<div\n  class='tag-box background-{{ backgroundColor }} {{ color }}'\n  [ngClass]=\"{\n    'readonly-tag': readonly,\n    'disabled': disabled,\n    'rounded': rounded,\n    'pointer': pointer,\n  }\"\n  [ngStyle]=\"{\n    'color': getColor(),\n    'background-color': getBackgroundColor(),\n  }\"\n  (click)=\"handleClick()\"\n>\n  <ng-content></ng-content>\n</div>" }]
        }], ctorParameters: function () { return [{ type: ColorsHelper }]; }, propDecorators: { customClass: [{
                type: Input
            }], rounded: [{
                type: Input
            }], color: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }], readonly: [{
                type: Input
            }], disabled: [{
                type: Input
            }], pointer: [{
                type: Input
            }], emitClick: [{
                type: Output
            }] } });

/**
 * A utility service for performing various types of validation tasks, such as email, URL, date,
 * CNPJ, phone number, CPF, and record employment number validation.
 *
 * @example
 * // Import and inject the ValidatorHelper service in a component or service.
 * import { Component } from '@angular/core';
 * import { ValidatorHelper } from './validator-helper.service';
 *
 * @Component({
 *   selector: 'app-root',
 *   template: '<button (click)="validateEmail()">Validate Email</button>',
 * })
 * export class AppComponent {
 *   constructor(private validatorHelper: ValidatorHelper) {}
 *
 *   validateEmail() {
 *     const isValid = this.validatorHelper.email('example@email.com');
 *     if (isValid) {
 *       console.log('Email is valid.');
 *     } else {
 *       console.log('Email is not valid.');
 *     }
 *   }
 * }
 */
class ValidatorHelper {
    /**
     * Validates XOR Logic
     *
     * @param {any} x - any value to validate.
     * @param {any} y - any value to validate.
     * @returns {boolean} - `true` if the is an xor logic, otherwise `false`.
     */
    XOR(x, y) {
        return (x || y) && !(x && y);
    }
    /**
     * Validates an email address.
     *
     * @param {string} email - The email address to validate.
     * @returns {boolean} - `true` if the email is valid, otherwise `false`.
     */
    email(email) {
        return /^[_\-a-z0-9]+(\.[-_a-z0-9]+)*@[a-z0-9-]+(\.[a-z0-9-]+)*(\.[a-z]+)$/i.test(email);
    }
    /**
     * Validates a URL.
     *
     * @param {string} url - The URL to validate.
     * @returns {boolean} - `true` if the URL is valid, otherwise `false`.
     */
    url(url) {
        const pattern = new RegExp('^(https?:\\/\\/)' + // protocol
            '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
            '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
            '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
            '(\\/\\@?[a-zA-Z0-9._%+-]*)*' +
            '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
            '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator
        return !!pattern.test(url);
    }
    /**
     * Validates a date string.
     *
     * @param {string} date - The date string to validate.
     * @returns {boolean} - `true` if the date is valid, otherwise `false`.
     */
    date(date) {
        return !!Date.parse(date);
    }
    /**
     * Validates a CNPJ (Cadastro Nacional da Pessoa Jurídica) number.
     *
     * @returns {boolean} - `true` if the CNPJ is valid, otherwise `false`.
     */
    cnpj(value = '') {
        if (value.length !== 14) {
            return false;
        }
        for (const digit of Array.from(Array(10).keys())) {
            if (Array(14).fill(digit).join('') === value) {
                return false;
            }
        }
        let size = value.length - 2;
        let numbers = value.substring(0, size);
        const digits = value.substring(size);
        let sum = 0;
        let pos = size - 7;
        for (let i = size; i >= 1; i--) {
            sum += parseFloat(numbers.charAt(size - i)) * pos--;
            if (pos < 2) {
                pos = 9;
            }
        }
        if (sum % 11 < 2 ? 0 : 11 - (sum % 11) !== parseInt(digits.charAt(0), 10)) {
            return false;
        }
        size = size + 1;
        numbers = value.substring(0, size);
        sum = 0;
        pos = size - 7;
        for (let i = size; i >= 1; i--) {
            sum += parseFloat(numbers.charAt(size - i)) * pos--;
            if (pos < 2) {
                pos = 9;
            }
        }
        if (sum % 11 < 2 ? 0 : 11 - (sum % 11) !== parseInt(digits.charAt(1), 10)) {
            return false;
        }
        return true;
    }
    /**
     * Validates a phone number.
     *
     * @param {string} phone - The phone number to validate.
     * @returns {boolean} - `true` if the phone number is valid, otherwise `false`.
     */
    phone(phone = '') {
        const regex = /(\d{2})(\d{0,1})(\d{4})(\d{4})/;
        return phone.match(regex) !== null;
    }
    /**
     * Validates a CPF (Cadastro de Pessoas Físicas) number.
     *
     * @param {string} value - The CPF number to validate.
     * @returns {boolean} - `true` if the CPF is valid, otherwise `false`.
     */
    cpf(value = '') {
        let sum = 0;
        let rest;
        if (/^(.)\1+$/.test(value)) {
            return false;
        }
        for (let i = 1; i <= 9; i++) {
            sum = sum + parseInt(value.substring(i - 1, i), 10) * (11 - i);
        }
        rest = (sum * 10) % 11;
        if (rest === 10 || rest === 11) {
            rest = 0;
        }
        if (rest !== parseInt(value.substring(9, 10), 10)) {
            return false;
        }
        sum = 0;
        for (let i = 1; i <= 10; i++) {
            sum = sum + parseInt(value.substring(i - 1, i), 10) * (12 - i);
        }
        rest = (sum * 10) % 11;
        if (rest === 10 || rest === 11) {
            rest = 0;
        }
        if (rest !== parseInt(value.substring(10, 11), 10)) {
            return false;
        }
        return true;
    }
    /**
     * Validates a record employment number.
     *
     * @param {string} recordEmployment - The record employment number to validate.
     * @returns {boolean} - `true` if the record employment number is valid, otherwise `false`.
     */
    recordEmployment(recordEmployment = '') {
        let total = 0;
        let residual = 0;
        let strResto = '';
        if (recordEmployment === '00000000000') {
            return false;
        }
        for (let i = 0, ftap = '3298765432', resultado = 0; i <= 9; i++) {
            resultado = Number(recordEmployment.slice(i, i + 1)) * Number(ftap.slice(i, i + 1));
            total = total + resultado;
        }
        residual = total % 11;
        if (residual !== 0) {
            residual = 11 - residual;
        }
        if (residual === 10 || residual === 11) {
            strResto = residual.toString();
            residual = Number(strResto.slice(1, 2));
        }
        if (residual !== Number(recordEmployment.slice(10, 11))) {
            return false;
        }
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: ValidatorHelper, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: ValidatorHelper, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: ValidatorHelper, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/**
 * Represents a textarea input component with various configuration options.
 *
 * Look the link about the component in original framework and the appearance
 *
 * @see {@link https://css.squidit.com.br/forms/textarea}
 *
 * <div class='col-6 my-3'>
 *  <label class='display-block' for='textarea-text'>
 *    Label
 *  </label>
 *  <div class='wrapper-input mb-3'>
 *    <textarea
 *      class='display-block textarea'
 *      type='text'
 *      name='textarea-text'
 *      id='textarea-text'
 *      placeholder='Placeholder'
 *    ></textarea>
 *  </div>
 *</div>
 *
 * @example
 * <sq-textarea [name]="'description'" [id]="'description'" [label]="'Description'"[placeholder]="'Enter a description...'" [(value)]="text"></sq-textarea>
 *
 */
class SqTextAreaComponent {
    /**
     * Constructor for the SqInputComponent class.
     * @param validatorHelper - The ValidatorHelper service for input validation.
     * @param element - The ElementRef representing the input element.
     * @param translate - The TranslateService for internationalization (optional).
     */
    constructor(validatorHelper, element, translate) {
        this.validatorHelper = validatorHelper;
        this.element = element;
        this.translate = translate;
        /**
         * The name attribute of the textarea.
         *
         * @default 'random-name-[hash-random-code]'
         */
        this.name = `random-name-${(1 + Date.now() + Math.random()).toString().replace('.', '')}`;
        /**
         * Additional CSS classes for styling the textarea.
         */
        this.customClass = '';
        /**
         * The placeholder text to display in the textarea.
         */
        this.placeholder = '';
        /**
         * External error message to display for the textarea.
         */
        this.externalError = '';
        /**
         * External icon to display for the textarea.
         */
        this.externalIcon = '';
        /**
         * The initial value of the textarea.
         */
        this.value = '';
        /**
         * The time interval for input timeout in ms.
         */
        this.timeToChange = 0;
        /**
         * Flag to show an error span.
         */
        this.errorSpan = true;
        /**
         * Flag to disable the textarea.
         */
        this.disabled = false;
        /**
         * Flag to make the textarea readonly.
         */
        this.readonly = false;
        /**
         * Flag to mark the textarea as required.
         */
        this.required = false;
        /**
         * Flag to use form errors for validation.
         */
        this.useFormErrors = true;
        /**
         * The tooltip message to display.
         */
        this.tooltipMessage = '';
        /**
         * The placement of the tooltip.
         */
        this.tooltipPlacement = 'right center';
        /**
         * The color of the tooltip.
         */
        this.tooltipColor = 'inherit';
        /**
         * The icon to display in the tooltip.
         */
        this.tooltipIcon = '';
        /**
         * The background color of the textarea.
         */
        this.backgroundColor = '';
        /**
         * The border color of the textarea.
         */
        this.borderColor = '';
        /**
         * The color of the textarea label.
         */
        this.labelColor = '';
        /**
         * The maximum length of the textarea.
         */
        this.maxLength = null;
        /**
         * Event emitted when a key is pressed down in the textarea.
         */
        this.keyPressDown = new EventEmitter();
        /**
         * Event emitted when a key is released in the textarea.
         */
        this.keyPressUp = new EventEmitter();
        /**
         * Event emitted when the textarea gains or loses focus.
         */
        this.inFocus = new EventEmitter();
        /**
         * Event emitted when the textarea value changes.
         */
        this.valueChange = new EventEmitter();
        /**
         * Event emitter for validation status.
         */
        this.valid = new EventEmitter();
        /**
         * Reference to a left-aligned label template.
         */
        this.leftLabel = null;
        /**
         * Reference to a right-aligned label template.
         */
        this.rightLabel = null;
        /**
         * Represents the error state of the textarea.
         */
        this.error = false;
        this.nativeElement = element.nativeElement;
    }
    /**
     * Asynchronously validates the input value.
     */
    async validate(isBlur = false) {
        if (isBlur) {
            this.inFocus.emit(false);
        }
        if (this.externalError) {
            this.error = false;
        }
        else if (this.required && !this.value) {
            this.valid.emit(false);
            this.setError('forms.required');
        }
        else {
            this.valid.emit(true);
            this.error = '';
        }
    }
    /**
     * Handles changes to the textarea value.
     *
     * @param {string} event - The new value of the textarea.
     */
    async change(event) {
        this.inFocus.emit(true);
        this.value = event;
        clearTimeout(this.timeoutInput);
        this.timeoutInput = setTimeout(() => {
            this.valueChange.emit(event);
        }, this.timeToChange);
        this.validate();
    }
    /**
     * Asynchronously sets an error message.
     *
     * @param {string} key - The key for the error message.
     */
    async setError(key) {
        if (this.useFormErrors && this.translate) {
            this.error = await this.translate.instant(key);
        }
    }
    /**
     * Handle keydown events.
     * @param event - The keydown event.
     */
    keyDown(event) {
        this.keyPressDown.emit(event);
    }
    /**
     * Handle keyup events.
     * @param event - The keyup event.
     */
    keyUp(event) {
        this.keyPressUp.emit(event);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqTextAreaComponent, deps: [{ token: ValidatorHelper }, { token: i0.ElementRef }, { token: i2$1.TranslateService, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqTextAreaComponent, selector: "sq-textarea", inputs: { name: "name", id: "id", label: "label", customClass: "customClass", placeholder: "placeholder", externalError: "externalError", externalIcon: "externalIcon", value: "value", timeToChange: "timeToChange", errorSpan: "errorSpan", disabled: "disabled", readonly: "readonly", required: "required", useFormErrors: "useFormErrors", tooltipMessage: "tooltipMessage", tooltipPlacement: "tooltipPlacement", tooltipColor: "tooltipColor", tooltipIcon: "tooltipIcon", backgroundColor: "backgroundColor", borderColor: "borderColor", labelColor: "labelColor", maxLength: "maxLength" }, outputs: { keyPressDown: "keyPressDown", keyPressUp: "keyPressUp", inFocus: "inFocus", valueChange: "valueChange", valid: "valid" }, queries: [{ propertyName: "leftLabel", first: true, predicate: ["leftLabel"], descendants: true }, { propertyName: "rightLabel", first: true, predicate: ["rightLabel"], descendants: true }], ngImport: i0, template: "<div class=\"wrapper-all-inside-input {{ customClass }}\">\n  <label\n    class=\"display-flex\"\n    *ngIf=\"label?.length || tooltipMessage\"\n    [ngClass]=\"{\n      readonly: readonly\n    }\"\n    [for]=\"id\"\n  >\n    <div *ngIf=\"label\" [ngStyle]=\"{ 'color': labelColor }\" [innerHtml]=\"label | universalSafe\"></div>\n    <sq-tooltip\n      *ngIf=\"tooltipMessage\"\n      class=\"ml-1\"\n      [message]=\"tooltipMessage\"\n      [placement]=\"tooltipPlacement\"\n      [color]=\"tooltipColor\"\n      [icon]=\"tooltipIcon\"\n    ></sq-tooltip>\n  </label>\n  <div\n    class=\"p-0 wrapper-input wrapper-input-squid text-ellipsisarea\"\n    [ngClass]=\"{\n      error: (externalError && externalError !== '') || (error && error !== ''),\n      readonly: readonly\n    }\"\n  >\n    <span class=\"input-group-text m-0\" *ngIf=\"leftLabel\">\n      <ng-container *ngTemplateOutlet=\"leftLabel\"></ng-container>\n    </span>\n    <textarea\n      class=\"col textarea scrollbar\"\n      [ngStyle]=\"{\n        'background-color': backgroundColor,\n        'border-color': borderColor\n      }\"\n      [ngClass]=\"{\n        error: (externalError && externalError !== '') || (error && error !== ''),\n        disabled: disabled,\n        readonly: readonly,\n        'has-icon-external': externalIcon\n      }\"\n      [id]=\"id\"\n      [name]=\"name\"\n      [placeholder]=\"placeholder || ''\"\n      [required]=\"required\"\n      [disabled]=\"disabled\"\n      [readonly]=\"readonly\"\n      (blur)=\"validate(true)\"\n      [maxlength]=\"maxLength\"\n      [ngModel]=\"value\"\n      (ngModelChange)=\"change($event)\"\n      (keydown)=\"keyDown($event)\"\n      (keyup)=\"keyUp($event)\"\n      ngDefaultControl\n      >{{ value }}</textarea\n    >\n    <span class=\"input-group-text m-0\" *ngIf=\"rightLabel\">\n      <ng-container *ngTemplateOutlet=\"rightLabel\"></ng-container>\n    </span>\n  </div>\n  <span\n    *ngIf=\"externalIcon\"\n    class=\"icon icon-external textarea-icon\"\n    [ngClass]=\"{\n      'no-label': !label?.length\n    }\"\n    [innerHtml]=\"externalIcon || '' | universalSafe\"\n  ></span>\n  <div\n    class=\"box-validation box-invalid show\"\n    *ngIf=\"errorSpan\"\n    [ngClass]=\"{\n      'has-max-length': maxLength\n    }\"\n  >\n    <ng-container *ngIf=\"externalError || error\">\n      <i class=\"fa-solid fa-triangle-exclamation\"></i>\n    </ng-container>\n    {{ externalError ? externalError : '' }}\n    {{ error && !externalError ? error : '' }}\n    <span\n      class=\"max-length-name\"\n      [ngClass]=\"{\n        'visibility-hidden-force': disabled || readonly\n      }\"\n      *ngIf=\"maxLength\"\n    >\n      {{ maxLength - (value?.length || 0) }}\n    </span>\n  </div>\n</div>\n", styles: [".wrapper-all-inside-input .icon{margin:0;font-size:1rem;font-weight:700;position:absolute;right:24px;top:40px}.wrapper-all-inside-input .icon.no-label{top:75px}.wrapper-all-inside-input .icon-external{color:inherit!important}.wrapper-all-inside-input .max-length-name{font-size:inherit;float:right}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i4.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i4.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i4.MaxLengthValidator, selector: "[maxlength][formControlName],[maxlength][formControl],[maxlength][ngModel]", inputs: ["maxlength"] }, { kind: "directive", type: i4.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: SqTooltipComponent, selector: "sq-tooltip", inputs: ["color", "icon", "placement", "message", "tooltipClass", "textAlign", "fontSize", "trigger"] }, { kind: "pipe", type: UniversalSafePipe, name: "universalSafe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqTextAreaComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-textarea', template: "<div class=\"wrapper-all-inside-input {{ customClass }}\">\n  <label\n    class=\"display-flex\"\n    *ngIf=\"label?.length || tooltipMessage\"\n    [ngClass]=\"{\n      readonly: readonly\n    }\"\n    [for]=\"id\"\n  >\n    <div *ngIf=\"label\" [ngStyle]=\"{ 'color': labelColor }\" [innerHtml]=\"label | universalSafe\"></div>\n    <sq-tooltip\n      *ngIf=\"tooltipMessage\"\n      class=\"ml-1\"\n      [message]=\"tooltipMessage\"\n      [placement]=\"tooltipPlacement\"\n      [color]=\"tooltipColor\"\n      [icon]=\"tooltipIcon\"\n    ></sq-tooltip>\n  </label>\n  <div\n    class=\"p-0 wrapper-input wrapper-input-squid text-ellipsisarea\"\n    [ngClass]=\"{\n      error: (externalError && externalError !== '') || (error && error !== ''),\n      readonly: readonly\n    }\"\n  >\n    <span class=\"input-group-text m-0\" *ngIf=\"leftLabel\">\n      <ng-container *ngTemplateOutlet=\"leftLabel\"></ng-container>\n    </span>\n    <textarea\n      class=\"col textarea scrollbar\"\n      [ngStyle]=\"{\n        'background-color': backgroundColor,\n        'border-color': borderColor\n      }\"\n      [ngClass]=\"{\n        error: (externalError && externalError !== '') || (error && error !== ''),\n        disabled: disabled,\n        readonly: readonly,\n        'has-icon-external': externalIcon\n      }\"\n      [id]=\"id\"\n      [name]=\"name\"\n      [placeholder]=\"placeholder || ''\"\n      [required]=\"required\"\n      [disabled]=\"disabled\"\n      [readonly]=\"readonly\"\n      (blur)=\"validate(true)\"\n      [maxlength]=\"maxLength\"\n      [ngModel]=\"value\"\n      (ngModelChange)=\"change($event)\"\n      (keydown)=\"keyDown($event)\"\n      (keyup)=\"keyUp($event)\"\n      ngDefaultControl\n      >{{ value }}</textarea\n    >\n    <span class=\"input-group-text m-0\" *ngIf=\"rightLabel\">\n      <ng-container *ngTemplateOutlet=\"rightLabel\"></ng-container>\n    </span>\n  </div>\n  <span\n    *ngIf=\"externalIcon\"\n    class=\"icon icon-external textarea-icon\"\n    [ngClass]=\"{\n      'no-label': !label?.length\n    }\"\n    [innerHtml]=\"externalIcon || '' | universalSafe\"\n  ></span>\n  <div\n    class=\"box-validation box-invalid show\"\n    *ngIf=\"errorSpan\"\n    [ngClass]=\"{\n      'has-max-length': maxLength\n    }\"\n  >\n    <ng-container *ngIf=\"externalError || error\">\n      <i class=\"fa-solid fa-triangle-exclamation\"></i>\n    </ng-container>\n    {{ externalError ? externalError : '' }}\n    {{ error && !externalError ? error : '' }}\n    <span\n      class=\"max-length-name\"\n      [ngClass]=\"{\n        'visibility-hidden-force': disabled || readonly\n      }\"\n      *ngIf=\"maxLength\"\n    >\n      {{ maxLength - (value?.length || 0) }}\n    </span>\n  </div>\n</div>\n", styles: [".wrapper-all-inside-input .icon{margin:0;font-size:1rem;font-weight:700;position:absolute;right:24px;top:40px}.wrapper-all-inside-input .icon.no-label{top:75px}.wrapper-all-inside-input .icon-external{color:inherit!important}.wrapper-all-inside-input .max-length-name{font-size:inherit;float:right}\n"] }]
        }], ctorParameters: function () { return [{ type: ValidatorHelper }, { type: i0.ElementRef }, { type: i2$1.TranslateService, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { name: [{
                type: Input
            }], id: [{
                type: Input
            }], label: [{
                type: Input
            }], customClass: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], externalError: [{
                type: Input
            }], externalIcon: [{
                type: Input
            }], value: [{
                type: Input
            }], timeToChange: [{
                type: Input
            }], errorSpan: [{
                type: Input
            }], disabled: [{
                type: Input
            }], readonly: [{
                type: Input
            }], required: [{
                type: Input
            }], useFormErrors: [{
                type: Input
            }], tooltipMessage: [{
                type: Input
            }], tooltipPlacement: [{
                type: Input
            }], tooltipColor: [{
                type: Input
            }], tooltipIcon: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }], borderColor: [{
                type: Input
            }], labelColor: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], keyPressDown: [{
                type: Output
            }], keyPressUp: [{
                type: Output
            }], inFocus: [{
                type: Output
            }], valueChange: [{
                type: Output
            }], valid: [{
                type: Output
            }], leftLabel: [{
                type: ContentChild,
                args: ['leftLabel']
            }], rightLabel: [{
                type: ContentChild,
                args: ['rightLabel']
            }] } });

/**
 * Represents a select input component for choosing options from a dropdown.
 *
 * Look the link about the component in original framework and the appearance
 *
 * @see {@link https://css.squidit.com.br/forms/select}
 *
 * <br>
 * <label for='select-name'>
 * Label select
 * </label>
 * <select
 *   class='select mb-3'
 *   [name]="'select-name'"
 * >
 *  <option value='' disabled>Select an option</option>
 *  <option value="1">Option 1</option>
 *  <option value="2">Option 2</option>
 * </select>
 *
 * @example
 * <sq-select
 *   [name]="'select-name'"
 *   [label]="'Select an option'"
 *   [options]="selectOptions"
 *   [(value)]='value'
 *   (valueChange)="handleValueChange($event)"
 * >
 * </sq-select>
 */
class SqSelectComponent {
    /**
     * Constructs a new SqSelectComponent.
     *
     * @param {ElementRef} element - The ElementRef for the component.
     * @param {TranslateService} translate - The optional TranslateService for internationalization.
     */
    constructor(element, translate) {
        this.element = element;
        this.translate = translate;
        /**
         * The name attribute for the select input.
         *
         * @default 'random-name-[hash-random-code]'
         */
        this.name = `random-name-${(1 + Date.now() + Math.random()).toString().replace('.', '')}`;
        /**
         * The label for the select input.
         */
        this.label = '';
        /**
         * Custom CSS class for the select input.
         */
        this.customClass = '';
        /**
         * Placeholder text for the select input.
         */
        this.placeholder = '';
        /**
         * External error message for the select input.
         */
        this.externalError = '';
        /**
         * External icon for the select input.
         */
        this.externalIcon = '';
        /**
         * An array of options for populating the select input.
         */
        this.options = [];
        /**
         * Options with groups for populating the select input.
         */
        this.optionsWithGroups = [];
        /**
         * Indicates whether to display an error span.
         */
        this.errorSpan = true;
        /**
         * Indicates whether the select input is disabled.
         */
        this.disabled = false;
        /**
         * Indicates whether the select input is readonly.
         */
        this.readonly = false;
        /**
         * Indicates whether the select input is required.
         */
        this.required = false;
        /**
         * Indicates whether the select input is in a loading state.
         */
        this.loading = false;
        /**
         * Indicates whether to use form errors for validation.
         */
        this.useFormErrors = true;
        /**
         * Tooltip message for the select input.
         */
        this.tooltipMessage = '';
        /**
         * Tooltip placement for the select input.
         */
        this.tooltipPlacement = 'right center';
        /**
         * Tooltip color for the select input.
         */
        this.tooltipColor = 'inherit';
        /**
         * Tooltip icon for the select input.
         */
        this.tooltipIcon = '';
        /**
         * Background color for the select input.
         */
        this.backgroundColor = '';
        /**
         * Border color for the select input.
         */
        this.borderColor = '';
        /**
         * Label color for the select input.
         */
        this.labelColor = '';
        /**
         * Event emitted when the select input gains or loses focus.
         */
        this.inFocus = new EventEmitter();
        /**
         * Event emitted when the select input becomes valid or invalid.
         */
        this.valid = new EventEmitter();
        /**
         * Event emitted when the value of the select input changes.
         */
        this.valueChange = new EventEmitter();
        /**
         * Reference to a left-aligned label template.
         */
        this.leftLabel = null;
        /**
         * Reference to a right-aligned label template.
         */
        this.rightLabel = null;
        /**
         * The error state of the select input.
         */
        this.error = false;
        this.nativeElement = element.nativeElement;
    }
    /**
     * Validates the select input and sets the error state.
     *
     * @param {boolean} isBlur - Indicates whether the input is blurred.
     */
    validate(isBlur = false) {
        if (this.externalError) {
            this.error = false;
        }
        else if (this.required && !this.value) {
            this.valid.emit(false);
            this.setError('forms.required');
        }
        else {
            this.valid.emit(true);
            this.error = '';
        }
        if (isBlur) {
            this.inFocus.emit(false);
        }
    }
    /**
     * Sets an error message.
     *
     * @param {string} key - The translation key for the error message.
     */
    async setError(key) {
        if (this.useFormErrors && this.translate) {
            this.error = await this.translate.instant(key);
        }
    }
    /**
     * Handles the change event of the select input.
     *
     * @param {any} value - The selected value.
     */
    change(value) {
        this.inFocus.emit(true);
        this.value = value;
        this.valueChange.emit(value);
        this.validate();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqSelectComponent, deps: [{ token: i0.ElementRef }, { token: i2$1.TranslateService, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqSelectComponent, selector: "sq-select", inputs: { name: "name", value: "value", id: "id", label: "label", customClass: "customClass", placeholder: "placeholder", externalError: "externalError", externalIcon: "externalIcon", options: "options", optionsWithGroups: "optionsWithGroups", errorSpan: "errorSpan", disabled: "disabled", readonly: "readonly", required: "required", loading: "loading", useFormErrors: "useFormErrors", tooltipMessage: "tooltipMessage", tooltipPlacement: "tooltipPlacement", tooltipColor: "tooltipColor", tooltipIcon: "tooltipIcon", backgroundColor: "backgroundColor", borderColor: "borderColor", labelColor: "labelColor" }, outputs: { inFocus: "inFocus", valid: "valid", valueChange: "valueChange" }, queries: [{ propertyName: "leftLabel", first: true, predicate: ["leftLabel"], descendants: true }, { propertyName: "rightLabel", first: true, predicate: ["rightLabel"], descendants: true }], ngImport: i0, template: "<div class=\"wrapper-all-inside-input {{ customClass }}\">\r\n  <label\r\n    class=\"display-flex\"\r\n    *ngIf=\"label?.length || tooltipMessage\"\r\n    [ngClass]=\"{\r\n      readonly: readonly\r\n    }\"\r\n    [for]=\"id\"\r\n  >\r\n    <div *ngIf=\"label\" [ngStyle]=\"{ 'color': labelColor }\" [innerHtml]=\"label | universalSafe\"></div>\r\n    <sq-tooltip\r\n      *ngIf=\"tooltipMessage\"\r\n      class=\"ml-1\"\r\n      [message]=\"tooltipMessage\"\r\n      [placement]=\"tooltipPlacement\"\r\n      [color]=\"tooltipColor\"\r\n      [icon]=\"tooltipIcon\"\r\n    ></sq-tooltip>\r\n  </label>\r\n  <div\r\n    class=\"p-0 wrapper-input wrapper-input-squid\"\r\n    [ngStyle]=\"{ 'border-color': borderColor }\"\r\n    [ngClass]=\"{\r\n      error: (externalError && externalError !== '') || (error && error !== ''),\r\n      readonly: readonly\r\n    }\"\r\n  >\r\n    <span class=\"input-group-text m-0\" *ngIf=\"leftLabel\">\r\n      <ng-container *ngTemplateOutlet=\"leftLabel\"></ng-container>\r\n    </span>\r\n    <select\r\n      class=\"select\"\r\n      [ngClass]=\"{\r\n        disabled: disabled,\r\n        readonly: readonly\r\n      }\"\r\n      [ngStyle]=\"{\r\n        'background-color': backgroundColor,\r\n        'border-color': borderColor\r\n      }\"\r\n      [id]=\"id\"\r\n      [name]=\"name\"\r\n      [required]=\"required\"\r\n      [disabled]=\"disabled || readonly || loading\"\r\n      (blur)=\"validate(true)\"\r\n      [value]=\"value\"\r\n      (change)=\"change(select?.value)\"\r\n      #select\r\n    >\r\n      <option *ngIf=\"placeholder\" value=\"\" [selected]=\"!value\" disabled>{{ placeholder }}</option>\r\n      <optgroup *ngFor=\"let group of optionsWithGroups\" [label]=\"group?.label\">\r\n        <option\r\n          *ngFor=\"let option of group?.options\"\r\n          [value]=\"option?.value\"\r\n          [selected]=\"value === option?.value\"\r\n          [disabled]=\"!!option?.disabled\"\r\n        >\r\n          {{ option?.label }}\r\n        </option>\r\n      </optgroup>\r\n      <option *ngFor=\"let option of options\" [value]=\"option?.value\" [selected]=\"value === option?.value\" [disabled]=\"!!option?.disabled\">\r\n        {{ option?.label }}\r\n      </option>\r\n    </select>\r\n    <span class=\"input-group-text m-0\" *ngIf=\"rightLabel\">\r\n      <ng-container *ngTemplateOutlet=\"rightLabel\"></ng-container>\r\n    </span>\r\n  </div>\r\n  <span *ngIf=\"externalIcon\" class=\"icon icon-external\" [innerHtml]=\"externalIcon || '' | universalSafe\"></span>\r\n  <div class=\"box-validation box-invalid show\" *ngIf=\"errorSpan\">\r\n    <ng-container *ngIf=\"externalError || error\">\r\n      <i class=\"fa-solid fa-triangle-exclamation\"></i>\r\n    </ng-container>\r\n    {{ externalError ? externalError : '' }}\r\n    {{ error && !externalError ? error : '' }}\r\n  </div>\r\n</div>\r\n", styles: [".wrapper-input.disabled{border-radius:5px}.wrapper-input select.readonly,.wrapper-input select[readonly]{opacity:1!important}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i4.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "component", type: SqTooltipComponent, selector: "sq-tooltip", inputs: ["color", "icon", "placement", "message", "tooltipClass", "textAlign", "fontSize", "trigger"] }, { kind: "pipe", type: UniversalSafePipe, name: "universalSafe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqSelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-select', template: "<div class=\"wrapper-all-inside-input {{ customClass }}\">\r\n  <label\r\n    class=\"display-flex\"\r\n    *ngIf=\"label?.length || tooltipMessage\"\r\n    [ngClass]=\"{\r\n      readonly: readonly\r\n    }\"\r\n    [for]=\"id\"\r\n  >\r\n    <div *ngIf=\"label\" [ngStyle]=\"{ 'color': labelColor }\" [innerHtml]=\"label | universalSafe\"></div>\r\n    <sq-tooltip\r\n      *ngIf=\"tooltipMessage\"\r\n      class=\"ml-1\"\r\n      [message]=\"tooltipMessage\"\r\n      [placement]=\"tooltipPlacement\"\r\n      [color]=\"tooltipColor\"\r\n      [icon]=\"tooltipIcon\"\r\n    ></sq-tooltip>\r\n  </label>\r\n  <div\r\n    class=\"p-0 wrapper-input wrapper-input-squid\"\r\n    [ngStyle]=\"{ 'border-color': borderColor }\"\r\n    [ngClass]=\"{\r\n      error: (externalError && externalError !== '') || (error && error !== ''),\r\n      readonly: readonly\r\n    }\"\r\n  >\r\n    <span class=\"input-group-text m-0\" *ngIf=\"leftLabel\">\r\n      <ng-container *ngTemplateOutlet=\"leftLabel\"></ng-container>\r\n    </span>\r\n    <select\r\n      class=\"select\"\r\n      [ngClass]=\"{\r\n        disabled: disabled,\r\n        readonly: readonly\r\n      }\"\r\n      [ngStyle]=\"{\r\n        'background-color': backgroundColor,\r\n        'border-color': borderColor\r\n      }\"\r\n      [id]=\"id\"\r\n      [name]=\"name\"\r\n      [required]=\"required\"\r\n      [disabled]=\"disabled || readonly || loading\"\r\n      (blur)=\"validate(true)\"\r\n      [value]=\"value\"\r\n      (change)=\"change(select?.value)\"\r\n      #select\r\n    >\r\n      <option *ngIf=\"placeholder\" value=\"\" [selected]=\"!value\" disabled>{{ placeholder }}</option>\r\n      <optgroup *ngFor=\"let group of optionsWithGroups\" [label]=\"group?.label\">\r\n        <option\r\n          *ngFor=\"let option of group?.options\"\r\n          [value]=\"option?.value\"\r\n          [selected]=\"value === option?.value\"\r\n          [disabled]=\"!!option?.disabled\"\r\n        >\r\n          {{ option?.label }}\r\n        </option>\r\n      </optgroup>\r\n      <option *ngFor=\"let option of options\" [value]=\"option?.value\" [selected]=\"value === option?.value\" [disabled]=\"!!option?.disabled\">\r\n        {{ option?.label }}\r\n      </option>\r\n    </select>\r\n    <span class=\"input-group-text m-0\" *ngIf=\"rightLabel\">\r\n      <ng-container *ngTemplateOutlet=\"rightLabel\"></ng-container>\r\n    </span>\r\n  </div>\r\n  <span *ngIf=\"externalIcon\" class=\"icon icon-external\" [innerHtml]=\"externalIcon || '' | universalSafe\"></span>\r\n  <div class=\"box-validation box-invalid show\" *ngIf=\"errorSpan\">\r\n    <ng-container *ngIf=\"externalError || error\">\r\n      <i class=\"fa-solid fa-triangle-exclamation\"></i>\r\n    </ng-container>\r\n    {{ externalError ? externalError : '' }}\r\n    {{ error && !externalError ? error : '' }}\r\n  </div>\r\n</div>\r\n", styles: [".wrapper-input.disabled{border-radius:5px}.wrapper-input select.readonly,.wrapper-input select[readonly]{opacity:1!important}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i2$1.TranslateService, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { name: [{
                type: Input
            }], value: [{
                type: Input
            }], id: [{
                type: Input
            }], label: [{
                type: Input
            }], customClass: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], externalError: [{
                type: Input
            }], externalIcon: [{
                type: Input
            }], options: [{
                type: Input
            }], optionsWithGroups: [{
                type: Input
            }], errorSpan: [{
                type: Input
            }], disabled: [{
                type: Input
            }], readonly: [{
                type: Input
            }], required: [{
                type: Input
            }], loading: [{
                type: Input
            }], useFormErrors: [{
                type: Input
            }], tooltipMessage: [{
                type: Input
            }], tooltipPlacement: [{
                type: Input
            }], tooltipColor: [{
                type: Input
            }], tooltipIcon: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }], borderColor: [{
                type: Input
            }], labelColor: [{
                type: Input
            }], inFocus: [{
                type: Output
            }], valid: [{
                type: Output
            }], valueChange: [{
                type: Output
            }], leftLabel: [{
                type: ContentChild,
                args: ['leftLabel']
            }], rightLabel: [{
                type: ContentChild,
                args: ['rightLabel']
            }] } });

/**
 * Represents a selector input component for checkboxes or radio buttons.
 *
 * Look the link about the component in original framework and the appearance
 *
 * @see {@link https://css.squidit.com.br/forms/selectors}
 *
 * <br />
 * <div class='row'>
 *  <div class='col-md-4'>
 *    <div class='wrapper-selectors mb-3'>
 *      <input
 *        type='checkbox'
 *        name='checkbox'
 *        id='checkbox'
 *      />
 *      <label
 *        class='checkbox'
 *        for='checkbox'
 *      ></label>
 *      <label
 *        for='checkbox'
 *      >
 *        Label
 *      </label>
 *    </div>
 *  </div>
 *  <div class='col-md-4'>
 *    <div class='wrapper-selectors mb-3'>
 *      <input
 *        type='radio'
 *        name='radio'
 *        id='radio'
 *      />
 *      <label
 *        class='checkbox'
 *        for='radio'
 *      ></label>
 *      <label
 *        for='radio'
 *      >
 *        Label
 *      </label>
 *    </div>
 *  </div>
 *  <div class='col-md-4'>
 *    <div class='wrapper-selectors toggle mb-3'>
 *      <input
 *        type='checkbox'
 *        name='toggle'
 *        id='toggle'
 *      />
 *      <label
 *        class='checkbox'
 *        for='toggle'
 *      ></label>
 *      <label
 *        for='toggle'
 *      >
 *        Label
 *      </label>
 *    </div>
 *  </div>
 *</div>
 *
 * @example
 * <sq-selector
 *   [label]="'Check this box'"
 *   [checked]="isChecked"
 *   [value]="selectedValue"
 *   (valueChange)="handleValueChange($event)"
 * ></sq-selector>
 */
class SqSelectorComponent {
    /**
     * Constructs a new SqSelectorComponent.
     *
     * @param {TranslateService} translate - The optional TranslateService for internationalization.
     */
    constructor(translate) {
        this.translate = translate;
        /**
         * The name attribute for the selector input.
         */
        this.name = '';
        /**
         * The type of selector: 'checkbox' or 'radio'.
         */
        this.type = 'checkbox';
        /**
         * The selected value of the selector input.
         */
        this.value = '';
        /**
         * Indicates whether the selector input is checked.
         */
        this.checked = false;
        /**
         * Indicates whether the selector input is in an indeterminate state.
         */
        this.indeterminate = false;
        /**
         * Text color for the selector input.
         */
        this.colorText = '';
        /**
         * Background color for the selector input when checked.
         */
        this.colorBackground = 'green';
        /**
         * Indicates whether to hide the actual input element.
         */
        this.hideInput = false;
        /**
         * Indicates whether the selector input supports toggle behavior.
         */
        this.toggle = false;
        /**
         * External error message for the selector input.
         */
        this.externalError = '';
        /**
         * Indicates whether to use form errors for validation.
         */
        this.useFormErrors = true;
        /**
         * The label for the selector input.
         */
        this.label = '';
        /**
         * Indicates whether to display an error span.
         */
        this.errorSpan = true;
        /**
         * Block (width: 100%) the selector input.
         */
        this.block = false;
        /**
         * Event emitted when the value of the selector input changes.
         */
        this.valueChange = new EventEmitter();
        /**
         * Event emitted when the selector input becomes valid or invalid.
         */
        this.valid = new EventEmitter();
        /**
         * Content child for the right label template.
         */
        this.rightLabel = null;
        /**
         * Content child for the left label template.
         */
        this.leftLabel = null;
        /**
         * Indicates whether the selector input is currently checked.
         */
        this.thisChecked = false;
        /**
         * Indicates whether the selector input is in an indeterminate state.
         */
        this.thisIndeterminate = false;
        /**
         * Error message associated with the selector input.
         */
        this.error = '';
        /**
         * Context object containing selector properties.
         */
        this.context = {
            checked: this.thisChecked,
            indeterminate: !this.thisChecked ? this.thisIndeterminate : false,
            value: this.value
        };
    }
    /**
     * Lifecycle hook called when any input properties change.
     *
     * @param {SimpleChanges} changes - An object containing changed input properties.
     */
    async ngOnChanges(changes) {
        if (changes.hasOwnProperty('checked')) {
            this.thisChecked = this.checked;
            this.context.checked = this.thisChecked;
        }
        if (changes.hasOwnProperty('indeterminate')) {
            this.thisIndeterminate = this.indeterminate;
            this.context.indeterminate = !this.thisChecked ? this.thisIndeterminate : false;
        }
        if (changes.hasOwnProperty('value')) {
            this.context.value = this.value;
        }
    }
    /**
     * Validates the selector input and sets the error state.
     */
    async validate() {
        if (this.externalError) {
            this.error = '';
        }
        else if (this.required && !this.thisChecked) {
            this.valid.emit(false);
            this.error = await this.translate.instant('forms.required');
        }
        else {
            this.valid.emit(true);
            this.error = '';
        }
    }
    /**
     * Sets an error message.
     *
     * @param {string} key - The translation key for the error message.
     */
    async setError(key) {
        if (this.useFormErrors && this.translate) {
            this.error = await this.translate.instant(key);
        }
    }
    /**
     * Handles the change event of the selector input.
     *
     * @param {any} event - The change event object.
     */
    change(event) {
        if (!this.readonly && !this.disabled) {
            this.valueChange.emit({
                value: this.value,
                checked: event?.target?.checked,
            });
            this.thisChecked = event?.target?.checked;
        }
        this.validate();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqSelectorComponent, deps: [{ token: i2$1.TranslateService, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqSelectorComponent, selector: "sq-selector", inputs: { name: "name", type: "type", id: "id", value: "value", checked: "checked", indeterminate: "indeterminate", disabled: "disabled", readonly: "readonly", required: "required", colorText: "colorText", colorBackground: "colorBackground", hideInput: "hideInput", toggle: "toggle", externalError: "externalError", useFormErrors: "useFormErrors", label: "label", errorSpan: "errorSpan", block: "block" }, outputs: { valueChange: "valueChange", valid: "valid" }, queries: [{ propertyName: "rightLabel", first: true, predicate: ["rightLabel"], descendants: true }, { propertyName: "leftLabel", first: true, predicate: ["leftLabel"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div\n  class=\"wrapper-selectors\"\n  [ngClass]=\"{\n    toggle: toggle,\n    checked: thisChecked,\n    indeterminate: !thisChecked ? thisIndeterminate : false,\n    error: error,\n    block: block,\n  }\"\n>\n  <label [for]=\"id\" *ngIf=\"leftLabel\" [ngClass]=\"{'label-max-width': hideInput}\">\n    <ng-container\n      *ngTemplateOutlet=\"leftLabel; context: context\"\n    ></ng-container>\n  </label>\n  <input\n    [id]=\"id\"\n    [name]=\"name\"\n    [type]=\"type\"\n    (change)=\"change($event)\"\n    [value]=\"value\"\n    [disabled]=\"disabled\"\n    [readonly]=\"readonly\"\n    [required]=\"required\"\n    [checked]=\"thisChecked\"\n    [ngClass]=\"{\n      indeterminate: !(checked || thisChecked) ? indeterminate || thisIndeterminate : null\n    }\"\n    [indeterminate]=\"!(checked || thisChecked) ? indeterminate || thisIndeterminate : null\"\n  />\n  <label\n    [for]=\"id\"\n    [ngClass]=\"{\n      disabled: disabled,\n      'hide-input': hideInput\n    }\"\n    class=\"checkbox {{ type }}\"\n  ></label>\n  <label [for]=\"id\" *ngIf=\"label\" [innerHtml]=\"label | universalSafe\" [ngClass]=\"{'label-max-width': hideInput}\"></label>\n  <label [for]=\"id\" *ngIf=\"rightLabel\" [ngClass]=\"{'label-max-width': hideInput}\">\n    <ng-container\n      *ngTemplateOutlet=\"rightLabel; context: context\"\n    ></ng-container>\n  </label>\n</div>\n<div\n  class=\"box-validation box-invalid show\"\n  *ngIf=\"errorSpan\"\n>\n  <ng-container *ngIf=\"externalError || error\">\n    <i class=\"fa-solid fa-triangle-exclamation\"></i>\n  </ng-container>\n  {{ externalError ? externalError : '' }}\n  {{ error && !externalError ? error : '' }}\n</div>\n", styles: [".wrapper-selectors .hide-input{display:none!important}.wrapper-selectors.block .label-max-width,.block{width:100%}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "pipe", type: UniversalSafePipe, name: "universalSafe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqSelectorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-selector', template: "<div\n  class=\"wrapper-selectors\"\n  [ngClass]=\"{\n    toggle: toggle,\n    checked: thisChecked,\n    indeterminate: !thisChecked ? thisIndeterminate : false,\n    error: error,\n    block: block,\n  }\"\n>\n  <label [for]=\"id\" *ngIf=\"leftLabel\" [ngClass]=\"{'label-max-width': hideInput}\">\n    <ng-container\n      *ngTemplateOutlet=\"leftLabel; context: context\"\n    ></ng-container>\n  </label>\n  <input\n    [id]=\"id\"\n    [name]=\"name\"\n    [type]=\"type\"\n    (change)=\"change($event)\"\n    [value]=\"value\"\n    [disabled]=\"disabled\"\n    [readonly]=\"readonly\"\n    [required]=\"required\"\n    [checked]=\"thisChecked\"\n    [ngClass]=\"{\n      indeterminate: !(checked || thisChecked) ? indeterminate || thisIndeterminate : null\n    }\"\n    [indeterminate]=\"!(checked || thisChecked) ? indeterminate || thisIndeterminate : null\"\n  />\n  <label\n    [for]=\"id\"\n    [ngClass]=\"{\n      disabled: disabled,\n      'hide-input': hideInput\n    }\"\n    class=\"checkbox {{ type }}\"\n  ></label>\n  <label [for]=\"id\" *ngIf=\"label\" [innerHtml]=\"label | universalSafe\" [ngClass]=\"{'label-max-width': hideInput}\"></label>\n  <label [for]=\"id\" *ngIf=\"rightLabel\" [ngClass]=\"{'label-max-width': hideInput}\">\n    <ng-container\n      *ngTemplateOutlet=\"rightLabel; context: context\"\n    ></ng-container>\n  </label>\n</div>\n<div\n  class=\"box-validation box-invalid show\"\n  *ngIf=\"errorSpan\"\n>\n  <ng-container *ngIf=\"externalError || error\">\n    <i class=\"fa-solid fa-triangle-exclamation\"></i>\n  </ng-container>\n  {{ externalError ? externalError : '' }}\n  {{ error && !externalError ? error : '' }}\n</div>\n", styles: [".wrapper-selectors .hide-input{display:none!important}.wrapper-selectors.block .label-max-width,.block{width:100%}\n"] }]
        }], ctorParameters: function () { return [{ type: i2$1.TranslateService, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { name: [{
                type: Input
            }], type: [{
                type: Input
            }], id: [{
                type: Input
            }], value: [{
                type: Input
            }], checked: [{
                type: Input
            }], indeterminate: [{
                type: Input
            }], disabled: [{
                type: Input
            }], readonly: [{
                type: Input
            }], required: [{
                type: Input
            }], colorText: [{
                type: Input
            }], colorBackground: [{
                type: Input
            }], hideInput: [{
                type: Input
            }], toggle: [{
                type: Input
            }], externalError: [{
                type: Input
            }], useFormErrors: [{
                type: Input
            }], label: [{
                type: Input
            }], errorSpan: [{
                type: Input
            }], block: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], valid: [{
                type: Output
            }], rightLabel: [{
                type: ContentChild,
                args: ['rightLabel']
            }], leftLabel: [{
                type: ContentChild,
                args: ['leftLabel']
            }] } });

/**
 * Represents the SqInputComponent, a customizable input component.
 *
 * Look the link about the component in original framework and the appearance
 *
 * @see {@link https://css.squidit.com.br/forms/input}
 *
 * <br>
 * <label for='id-exemple'>
 *  Example Input
 * </label>
 * <input
 *   class='input mb-3'
 *   name="name-exemple"
 *   id="id-exemple"
 *   placeholder="Enter text"
 * ></input>
 *
 * @example
 * <sq-input
 *   [name]="'name-exemple'"
 *   [id]="'id-exemple'"
 *   [label]="'Example Input'"
 *   [placeholder]="'Enter text'"
 *   [(value)]="inputValue"
 *   [required]="true"
 *   (valueChange)="onInputChange($event)"
 * ></sq-input>
 */
class SqInputComponent {
    /**
     * The value of the input element.
     */
    set value(value) {
        this._value = value;
    }
    get value() {
        return this._value;
    }
    /**
     * Constructor for the SqInputComponent class.
     * @param validatorHelper - The ValidatorHelper service for input validation.
     * @param element - The ElementRef representing the input element.
     * @param translate - The TranslateService for internationalization (optional).
     */
    constructor(validatorHelper, element, translate) {
        this.validatorHelper = validatorHelper;
        this.translate = translate;
        /**
         * The name attribute for the input element.
         *
         * @default 'random-name-[hash-random-code]'
         */
        this.name = `random-name-${(1 + Date.now() + Math.random()).toString().replace('.', '')}`;
        /**
         * Custom CSS class for the input element.
         */
        this.customClass = '';
        /**
         * Placeholder text for the input element.
         */
        this.placeholder = '';
        /**
         * External error message to display.
         */
        this.externalError = '';
        /**
         * External icon to display.
         */
        this.externalIcon = '';
        /**
         * Time in milliseconds before triggering input timeout.
         */
        this.timeToChange = 0;
        /**
         * Flag to display an error span.
         */
        this.errorSpan = true;
        /**
         * Flag to disable the input element.
         */
        this.disabled = false;
        /**
         * Flag to make the input element readonly.
         */
        this.readonly = false;
        /**
         * Flag to mark the input as required.
         */
        this.required = false;
        /**
         * Flag to use form errors for validation messages.
         */
        this.useFormErrors = true;
        /**
         * Tooltip message to display.
         */
        this.tooltipMessage = '';
        /**
         * Placement of the tooltip.
         */
        this.tooltipPlacement = 'right center';
        /**
         * Color of the tooltip.
         */
        this.tooltipColor = 'inherit';
        /**
         * Icon for the tooltip.
         */
        this.tooltipIcon = '';
        /**
         * Background color of the input element.
         */
        this.backgroundColor = '';
        /**
         * Border color of the input element.
         */
        this.borderColor = '';
        /**
         * Color of the input label.
         */
        this.labelColor = '';
        /**
         * Type of the input element (e.g., text, email, password).
         */
        this.type = 'text';
        /**
         * Maximum length for the input element.
         */
        this.maxLength = null;
        /**
         * Regular expression pattern for input validation.
         */
        this.pattern = '';
        /**
         * Input mode for mobile devices.
         */
        this.inputMode = '';
        /**
         * Event emitter for keydown events.
         */
        this.keyPressDown = new EventEmitter();
        /**
         * Event emitter for keyup events.
         */
        this.keyPressUp = new EventEmitter();
        /**
         * Event emitter for input focus events.
         */
        this.inFocus = new EventEmitter();
        /**
         * Event emitter for validation status.
         */
        this.valid = new EventEmitter();
        /**
         * Event emitter for input value changes.
         */
        this.valueChange = new EventEmitter();
        /**
         * Reference to a left-aligned label template.
         */
        this.leftLabel = null;
        /**
         * Reference to a right-aligned label template.
         */
        this.rightLabel = null;
        /**
         * The internal value of the input element.
         */
        this._value = '';
        /**
         * Error message to display.
         */
        this.error = false;
        this.nativeElement = element.nativeElement;
    }
    /**
     * Asynchronously validate the input value.
     * @param isBlur - Indicates if the input has lost focus.
     */
    async validate(isBlur = false) {
        if (this.externalError) {
            this.error = false;
        }
        else if (!!this.required && !this.value) {
            this.valid.emit(false);
            this.setError('forms.required');
        }
        else if (this.type === 'email' && !this.validatorHelper.email(this.value)) {
            this.valid.emit(false);
            this.setError('forms.email');
        }
        else if (this.type === 'tel' && !this.validatorHelper.phone(this.value)) {
            this.valid.emit(false);
            this.setError('forms.phone');
        }
        else if (this.type === 'url' && this.value && this.value.length && !this.validatorHelper.url(this.value)) {
            this.valid.emit(false);
            this.setError('forms.url');
        }
        else {
            this.valid.emit(true);
            this.error = '';
        }
        if (isBlur) {
            this.inFocus.emit(false);
        }
    }
    /**
     * Handle input value changes.
     * @param event - The input change event.
     */
    async change(event) {
        this.inFocus.emit(true);
        this.value = event;
        clearTimeout(this.timeoutInput);
        this.timeoutInput = setTimeout(() => {
            this.valueChange.emit(event);
        }, this.timeToChange);
        this.validate();
    }
    /**
     * Sets an error message.
     *
     * @param {string} key - The translation key for the error message.
     * @param interpolateParams - Value to interpolate with translation.
     */
    async setError(key, interpolateParams = {}) {
        if (this.useFormErrors && this.translate) {
            this.error = await this.translate.instant(key, interpolateParams);
        }
    }
    /**
     * Handle keydown events.
     * @param event - The keydown event.
     */
    keyDown(event) {
        this.keyPressDown.emit(event);
    }
    /**
     * Handle keyup events.
     * @param event - The keyup event.
     */
    keyUp(event) {
        this.keyPressUp.emit(event);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqInputComponent, deps: [{ token: ValidatorHelper }, { token: i0.ElementRef }, { token: i2$1.TranslateService, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqInputComponent, selector: "sq-input", inputs: { name: "name", id: "id", label: "label", customClass: "customClass", placeholder: "placeholder", externalError: "externalError", externalIcon: "externalIcon", value: "value", timeToChange: "timeToChange", errorSpan: "errorSpan", disabled: "disabled", readonly: "readonly", required: "required", useFormErrors: "useFormErrors", tooltipMessage: "tooltipMessage", tooltipPlacement: "tooltipPlacement", tooltipColor: "tooltipColor", tooltipIcon: "tooltipIcon", backgroundColor: "backgroundColor", borderColor: "borderColor", labelColor: "labelColor", type: "type", maxLength: "maxLength", pattern: "pattern", inputMode: "inputMode" }, outputs: { keyPressDown: "keyPressDown", keyPressUp: "keyPressUp", inFocus: "inFocus", valid: "valid", valueChange: "valueChange" }, queries: [{ propertyName: "leftLabel", first: true, predicate: ["leftLabel"], descendants: true }, { propertyName: "rightLabel", first: true, predicate: ["rightLabel"], descendants: true }], ngImport: i0, template: "<div class=\"wrapper-all-inside-input {{ customClass }}\">\r\n  <label\r\n    class=\"display-flex\"\r\n    *ngIf=\"label?.length || tooltipMessage\"\r\n    [ngClass]=\"{\r\n      readonly: readonly\r\n    }\"\r\n    [for]=\"id\"\r\n  >\r\n    <div *ngIf=\"label\" [ngStyle]=\"{ 'color': labelColor }\" [innerHtml]=\"label | universalSafe\"></div>\r\n    <sq-tooltip\r\n      *ngIf=\"tooltipMessage\"\r\n      class=\"ml-1\"\r\n      [message]=\"tooltipMessage\"\r\n      [placement]=\"tooltipPlacement\"\r\n      [color]=\"tooltipColor\"\r\n      [icon]=\"tooltipIcon\"\r\n    ></sq-tooltip>\r\n  </label>\r\n  <div\r\n    class=\"p-0 wrapper-input wrapper-input-squid text-ellipsisarea\"\r\n    [ngClass]=\"{\r\n      error: (externalError && externalError !== '') || (error && error !== ''),\r\n      readonly: readonly\r\n    }\"\r\n  >\r\n    <span class=\"input-group-text m-0\" *ngIf=\"leftLabel\">\r\n      <ng-container *ngTemplateOutlet=\"leftLabel\"></ng-container>\r\n    </span>\r\n    <input\r\n      class=\"col input\"\r\n      [ngClass]=\"{\r\n        'has-icon': error || externalError,\r\n        disabled: disabled,\r\n        readonly: readonly\r\n      }\"\r\n      [ngStyle]=\"{\r\n        'background-color': backgroundColor,\r\n        'border-color': borderColor\r\n      }\"\r\n      [id]=\"id\"\r\n      [type]=\"type || 'text'\"\r\n      [name]=\"name\"\r\n      [placeholder]=\"placeholder || ''\"\r\n      [required]=\"required\"\r\n      [disabled]=\"disabled\"\r\n      [readonly]=\"readonly\"\r\n      (blur)=\"validate(true)\"\r\n      [ngModel]=\"_value\"\r\n      [maxlength]=\"maxLength\"\r\n      (ngModelChange)=\"change($event)\"\r\n      (keydown)=\"keyDown($event)\"\r\n      (keyup)=\"keyUp($event)\"\r\n      [pattern]=\"pattern\"\r\n      [attr.inputmode]=\"inputMode\"\r\n      ngDefaultControl\r\n    />\r\n    <span class=\"input-group-text m-0\" *ngIf=\"rightLabel\">\r\n      <ng-container *ngTemplateOutlet=\"rightLabel\"></ng-container>\r\n    </span>\r\n  </div>\r\n  <span\r\n    *ngIf=\"externalIcon\"\r\n    class=\"icon icon-external textarea-icon\"\r\n    [ngClass]=\"{\r\n      'no-label': !label?.length\r\n    }\"\r\n    [innerHtml]=\"externalIcon || '' | universalSafe\"\r\n  ></span>\r\n  <div\r\n    class=\"box-validation box-invalid show\"\r\n    *ngIf=\"errorSpan\"\r\n    [ngClass]=\"{\r\n      'has-max-length': maxLength\r\n    }\"\r\n  >\r\n    <i [ngClass]=\"{\r\n      'visibility-hidden-force': !error && !externalError\r\n    }\" class=\"fa-solid fa-triangle-exclamation\"></i>\r\n    {{ externalError ? externalError : '' }}\r\n    {{ error && !externalError ? error : '' }}\r\n    <span\r\n      class=\"max-length-name\"\r\n      [ngClass]=\"{\r\n        'visibility-hidden-force': disabled || readonly\r\n      }\"\r\n      *ngIf=\"maxLength\"\r\n    >\r\n      {{ maxLength - (value?.length || 0) }}\r\n    </span>\r\n  </div>\r\n</div>", styles: [".wrapper-all-inside-input{position:relative}.wrapper-all-inside-input .icon{margin:0;font-size:1rem;font-weight:700;position:absolute;right:24px;top:40px}.wrapper-all-inside-input .icon.no-label{top:12px}.wrapper-all-inside-input .icon-external{color:inherit!important}.wrapper-all-inside-input .max-length-name{font-size:inherit;float:right}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i4.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i4.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i4.MaxLengthValidator, selector: "[maxlength][formControlName],[maxlength][formControl],[maxlength][ngModel]", inputs: ["maxlength"] }, { kind: "directive", type: i4.PatternValidator, selector: "[pattern][formControlName],[pattern][formControl],[pattern][ngModel]", inputs: ["pattern"] }, { kind: "directive", type: i4.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: SqTooltipComponent, selector: "sq-tooltip", inputs: ["color", "icon", "placement", "message", "tooltipClass", "textAlign", "fontSize", "trigger"] }, { kind: "pipe", type: UniversalSafePipe, name: "universalSafe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqInputComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-input', template: "<div class=\"wrapper-all-inside-input {{ customClass }}\">\r\n  <label\r\n    class=\"display-flex\"\r\n    *ngIf=\"label?.length || tooltipMessage\"\r\n    [ngClass]=\"{\r\n      readonly: readonly\r\n    }\"\r\n    [for]=\"id\"\r\n  >\r\n    <div *ngIf=\"label\" [ngStyle]=\"{ 'color': labelColor }\" [innerHtml]=\"label | universalSafe\"></div>\r\n    <sq-tooltip\r\n      *ngIf=\"tooltipMessage\"\r\n      class=\"ml-1\"\r\n      [message]=\"tooltipMessage\"\r\n      [placement]=\"tooltipPlacement\"\r\n      [color]=\"tooltipColor\"\r\n      [icon]=\"tooltipIcon\"\r\n    ></sq-tooltip>\r\n  </label>\r\n  <div\r\n    class=\"p-0 wrapper-input wrapper-input-squid text-ellipsisarea\"\r\n    [ngClass]=\"{\r\n      error: (externalError && externalError !== '') || (error && error !== ''),\r\n      readonly: readonly\r\n    }\"\r\n  >\r\n    <span class=\"input-group-text m-0\" *ngIf=\"leftLabel\">\r\n      <ng-container *ngTemplateOutlet=\"leftLabel\"></ng-container>\r\n    </span>\r\n    <input\r\n      class=\"col input\"\r\n      [ngClass]=\"{\r\n        'has-icon': error || externalError,\r\n        disabled: disabled,\r\n        readonly: readonly\r\n      }\"\r\n      [ngStyle]=\"{\r\n        'background-color': backgroundColor,\r\n        'border-color': borderColor\r\n      }\"\r\n      [id]=\"id\"\r\n      [type]=\"type || 'text'\"\r\n      [name]=\"name\"\r\n      [placeholder]=\"placeholder || ''\"\r\n      [required]=\"required\"\r\n      [disabled]=\"disabled\"\r\n      [readonly]=\"readonly\"\r\n      (blur)=\"validate(true)\"\r\n      [ngModel]=\"_value\"\r\n      [maxlength]=\"maxLength\"\r\n      (ngModelChange)=\"change($event)\"\r\n      (keydown)=\"keyDown($event)\"\r\n      (keyup)=\"keyUp($event)\"\r\n      [pattern]=\"pattern\"\r\n      [attr.inputmode]=\"inputMode\"\r\n      ngDefaultControl\r\n    />\r\n    <span class=\"input-group-text m-0\" *ngIf=\"rightLabel\">\r\n      <ng-container *ngTemplateOutlet=\"rightLabel\"></ng-container>\r\n    </span>\r\n  </div>\r\n  <span\r\n    *ngIf=\"externalIcon\"\r\n    class=\"icon icon-external textarea-icon\"\r\n    [ngClass]=\"{\r\n      'no-label': !label?.length\r\n    }\"\r\n    [innerHtml]=\"externalIcon || '' | universalSafe\"\r\n  ></span>\r\n  <div\r\n    class=\"box-validation box-invalid show\"\r\n    *ngIf=\"errorSpan\"\r\n    [ngClass]=\"{\r\n      'has-max-length': maxLength\r\n    }\"\r\n  >\r\n    <i [ngClass]=\"{\r\n      'visibility-hidden-force': !error && !externalError\r\n    }\" class=\"fa-solid fa-triangle-exclamation\"></i>\r\n    {{ externalError ? externalError : '' }}\r\n    {{ error && !externalError ? error : '' }}\r\n    <span\r\n      class=\"max-length-name\"\r\n      [ngClass]=\"{\r\n        'visibility-hidden-force': disabled || readonly\r\n      }\"\r\n      *ngIf=\"maxLength\"\r\n    >\r\n      {{ maxLength - (value?.length || 0) }}\r\n    </span>\r\n  </div>\r\n</div>", styles: [".wrapper-all-inside-input{position:relative}.wrapper-all-inside-input .icon{margin:0;font-size:1rem;font-weight:700;position:absolute;right:24px;top:40px}.wrapper-all-inside-input .icon.no-label{top:12px}.wrapper-all-inside-input .icon-external{color:inherit!important}.wrapper-all-inside-input .max-length-name{font-size:inherit;float:right}\n"] }]
        }], ctorParameters: function () { return [{ type: ValidatorHelper }, { type: i0.ElementRef }, { type: i2$1.TranslateService, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { name: [{
                type: Input
            }], id: [{
                type: Input
            }], label: [{
                type: Input
            }], customClass: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], externalError: [{
                type: Input
            }], externalIcon: [{
                type: Input
            }], value: [{
                type: Input
            }], timeToChange: [{
                type: Input
            }], errorSpan: [{
                type: Input
            }], disabled: [{
                type: Input
            }], readonly: [{
                type: Input
            }], required: [{
                type: Input
            }], useFormErrors: [{
                type: Input
            }], tooltipMessage: [{
                type: Input
            }], tooltipPlacement: [{
                type: Input
            }], tooltipColor: [{
                type: Input
            }], tooltipIcon: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }], borderColor: [{
                type: Input
            }], labelColor: [{
                type: Input
            }], type: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], pattern: [{
                type: Input
            }], inputMode: [{
                type: Input
            }], keyPressDown: [{
                type: Output
            }], keyPressUp: [{
                type: Output
            }], inFocus: [{
                type: Output
            }], valid: [{
                type: Output
            }], valueChange: [{
                type: Output
            }], leftLabel: [{
                type: ContentChild,
                args: ['leftLabel']
            }], rightLabel: [{
                type: ContentChild,
                args: ['rightLabel']
            }] } });

/**
 * Represents a date input component that extends SqInputComponent.
 *
 * This component extends the {@link SqInputComponent} and adds additional properties and behavior for handling money input.
 *
 * <br>
 * <label for='id-exemple-date'>
 *  Example Input
 * </label>
 * <input
 *   class='input mb-3'
 *   name="name-exemple-date"
 *   id="id-exemple-date"
 *   type="date"
 * ></input>
 *
 * @example
 * <sq-input-date [name]="'date-input'" [id]="'date-input'" [label]="'Date'" [(value)]='date'></sq-input-date>
 */
class SqInputDateComponent extends SqInputComponent {
    /**
     * The value of the input element in 'yyyy-mm-dd' format.
     */
    set value(value) {
        value = value.split('T')[0] || value;
        this._value = new Date(value);
    }
    get value() {
        return this._value.toISOString().split('T')[0];
    }
    /**
     * Constructs a new instance of SqInputDateComponent.
     * @param validatorHelper - The ValidatorHelper service for input validation.
     * @param element - Reference to the native element.
     * @param translate - The TranslateService for translation support (optional).
     */
    constructor(validatorHelper, element, translate) {
        super(validatorHelper, element, translate);
        this.validatorHelper = validatorHelper;
        this.translate = translate;
        /**
         * Minimum allowed date in 'yyyy-mm-dd' format.
         */
        this.minDate = '0001-01-01';
        /**
         * Maximum allowed date in 'yyyy-mm-dd' format.
         */
        this.maxDate = '9999-12-31';
        /**
         * Placeholder text for the date input.
         */
        this.placeholder = 'dd-mm-yyyy';
        this.nativeElement = element.nativeElement;
    }
    /**
     * Asynchronously validate the date input value.
     * @param isBlur - Indicates if the input has lost focus.
     */
    async validate(isBlur = false) {
        if (this.externalError) {
            this.error = false;
        }
        else if (!!this.required && !this._value) {
            this.valid.emit(false);
            this.setError('forms.required');
        }
        else if (!this.validatorHelper.date(this._value)) {
            this.valid.emit(false);
            this.setError('forms.date');
        }
        else if (this.formatDate(this.minDate) > this._value) {
            this.valid.emit(false);
            this.setError('forms.rangeDate');
        }
        else if (this.formatDate(this.maxDate) < this._value) {
            this.valid.emit(false);
            this.setError('forms.rangeDate');
        }
        else {
            this.valid.emit(true);
            this.error = '';
        }
        if (isBlur) {
            this.inFocus.emit(false);
        }
    }
    /**
     * Handle the change event for the date input.
     * @param event - The input change event.
     */
    async change(event) {
        event = event?.target?.valueAsDate ? event.target.valueAsDate : event?.target?.value || event;
        if (!this.disabled && !this.readonly) {
            this.inFocus.emit(true);
            this._value = event;
            clearTimeout(this.timeoutInput);
            this.timeoutInput = setTimeout(() => {
                this.valueChange.emit(this.getISOValidDate(event));
            }, this.timeToChange);
            this.validate();
        }
    }
    /**
     * Get the ISO-formatted valid date from a Date object.
     * @param value - The Date object to format.
     * @returns The ISO-formatted date string.
     */
    getISOValidDate(value) {
        try {
            let isoDate = '';
            if (value) {
                isoDate = new Date(value)?.toISOString().split('T')[0] + 'T00:00:00.000Z';
            }
            if (isoDate === 'Invalid date') {
                return '';
            }
            return isoDate;
        }
        catch (error) {
            return '';
        }
    }
    /**
     * Format a value as an ISO date string.
     * @param value - The value to format as an ISO date string.
     * @returns The ISO date string.
     */
    formatDate(value) {
        if (!value) {
            return '';
        }
        try {
            return new Date(value).toISOString().split('T')[0];
        }
        catch (error) {
            return '';
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqInputDateComponent, deps: [{ token: ValidatorHelper }, { token: i0.ElementRef }, { token: i2$1.TranslateService, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqInputDateComponent, selector: "sq-input-date", inputs: { minDate: "minDate", maxDate: "maxDate", placeholder: "placeholder", value: "value" }, usesInheritance: true, ngImport: i0, template: "<div class=\"wrapper-all-inside-input {{ customClass }}\">\n  <label\n    class=\"display-flex\"\n    *ngIf=\"label?.length || tooltipMessage\"\n    [ngClass]=\"{\n      readonly: readonly\n    }\"\n    [for]=\"id\"\n  >\n    <div *ngIf=\"label\" [ngStyle]=\"{ 'color': labelColor }\" [innerHtml]=\"label | universalSafe\"></div>\n    <sq-tooltip\n      *ngIf=\"tooltipMessage\"\n      class=\"ml-1\"\n      [message]=\"tooltipMessage\"\n      [placement]=\"tooltipPlacement\"\n      [color]=\"tooltipColor\"\n      [icon]=\"tooltipIcon\"\n    ></sq-tooltip>\n  </label>\n  <div\n    class=\"p-0 wrapper-input wrapper-input-squid text-ellipsisarea\"\n    [ngClass]=\"{\n      error: (externalError && externalError !== '') || (error && error !== ''),\n      readonly: readonly\n    }\"\n  >\n    <span class=\"input-group-text m-0\" *ngIf=\"leftLabel\">\n      <ng-container *ngTemplateOutlet=\"leftLabel\"></ng-container>\n    </span>\n    <input\n      class=\"col input\"\n      [ngClass]=\"{\n        'has-icon': error || externalError,\n        disabled: disabled,\n        readonly: readonly\n      }\"\n      [ngStyle]=\"{\n        'background-color': backgroundColor,\n        'border-color': borderColor\n      }\"\n      [min]=\"formatDate(minDate)\"\n      [max]=\"formatDate(maxDate)\"\n      [id]=\"id\"\n      [type]=\"'date'\"\n      [name]=\"name\"\n      [placeholder]=\"placeholder || ''\"\n      [required]=\"required\"\n      [disabled]=\"disabled\"\n      [readonly]=\"readonly\"\n      (blur)=\"validate(true)\"\n      [valueAsDate]=\"_value\"\n      (change)=\"change($event)\"\n      (keydown)=\"keyDown($event)\"\n      (keyup)=\"keyUp($event)\"\n    />\n    <span class=\"input-group-text m-0\" *ngIf=\"rightLabel\">\n      <ng-container *ngTemplateOutlet=\"rightLabel\"></ng-container>\n    </span>\n  </div>\n  <span\n    *ngIf=\"externalIcon\"\n    class=\"icon icon-external textarea-icon\"\n    [ngClass]=\"{\n      'no-label': !label?.length\n    }\"\n    [innerHtml]=\"externalIcon || '' | universalSafe\"\n  ></span>\n  <div\n    class=\"box-validation box-invalid show\"\n    *ngIf=\"errorSpan\"\n  >\n    <i [ngClass]=\"{\n      'visibility-hidden-force': !error && !externalError\n    }\" class=\"fa-solid fa-triangle-exclamation\"></i>\n    {{ externalError ? externalError : '' }}\n    {{ error && !externalError ? error : '' }}\n  </div>\n</div>", styles: [".wrapper-all-inside-input .icon{margin:0;font-size:1rem;font-weight:700;position:absolute;right:24px;top:40px}.wrapper-all-inside-input .icon.no-label{top:75px}.wrapper-all-inside-input .icon-external{color:inherit!important}.wrapper-all-inside-input .max-length-name{font-size:inherit;float:right}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: SqTooltipComponent, selector: "sq-tooltip", inputs: ["color", "icon", "placement", "message", "tooltipClass", "textAlign", "fontSize", "trigger"] }, { kind: "pipe", type: UniversalSafePipe, name: "universalSafe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqInputDateComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-input-date', template: "<div class=\"wrapper-all-inside-input {{ customClass }}\">\n  <label\n    class=\"display-flex\"\n    *ngIf=\"label?.length || tooltipMessage\"\n    [ngClass]=\"{\n      readonly: readonly\n    }\"\n    [for]=\"id\"\n  >\n    <div *ngIf=\"label\" [ngStyle]=\"{ 'color': labelColor }\" [innerHtml]=\"label | universalSafe\"></div>\n    <sq-tooltip\n      *ngIf=\"tooltipMessage\"\n      class=\"ml-1\"\n      [message]=\"tooltipMessage\"\n      [placement]=\"tooltipPlacement\"\n      [color]=\"tooltipColor\"\n      [icon]=\"tooltipIcon\"\n    ></sq-tooltip>\n  </label>\n  <div\n    class=\"p-0 wrapper-input wrapper-input-squid text-ellipsisarea\"\n    [ngClass]=\"{\n      error: (externalError && externalError !== '') || (error && error !== ''),\n      readonly: readonly\n    }\"\n  >\n    <span class=\"input-group-text m-0\" *ngIf=\"leftLabel\">\n      <ng-container *ngTemplateOutlet=\"leftLabel\"></ng-container>\n    </span>\n    <input\n      class=\"col input\"\n      [ngClass]=\"{\n        'has-icon': error || externalError,\n        disabled: disabled,\n        readonly: readonly\n      }\"\n      [ngStyle]=\"{\n        'background-color': backgroundColor,\n        'border-color': borderColor\n      }\"\n      [min]=\"formatDate(minDate)\"\n      [max]=\"formatDate(maxDate)\"\n      [id]=\"id\"\n      [type]=\"'date'\"\n      [name]=\"name\"\n      [placeholder]=\"placeholder || ''\"\n      [required]=\"required\"\n      [disabled]=\"disabled\"\n      [readonly]=\"readonly\"\n      (blur)=\"validate(true)\"\n      [valueAsDate]=\"_value\"\n      (change)=\"change($event)\"\n      (keydown)=\"keyDown($event)\"\n      (keyup)=\"keyUp($event)\"\n    />\n    <span class=\"input-group-text m-0\" *ngIf=\"rightLabel\">\n      <ng-container *ngTemplateOutlet=\"rightLabel\"></ng-container>\n    </span>\n  </div>\n  <span\n    *ngIf=\"externalIcon\"\n    class=\"icon icon-external textarea-icon\"\n    [ngClass]=\"{\n      'no-label': !label?.length\n    }\"\n    [innerHtml]=\"externalIcon || '' | universalSafe\"\n  ></span>\n  <div\n    class=\"box-validation box-invalid show\"\n    *ngIf=\"errorSpan\"\n  >\n    <i [ngClass]=\"{\n      'visibility-hidden-force': !error && !externalError\n    }\" class=\"fa-solid fa-triangle-exclamation\"></i>\n    {{ externalError ? externalError : '' }}\n    {{ error && !externalError ? error : '' }}\n  </div>\n</div>", styles: [".wrapper-all-inside-input .icon{margin:0;font-size:1rem;font-weight:700;position:absolute;right:24px;top:40px}.wrapper-all-inside-input .icon.no-label{top:75px}.wrapper-all-inside-input .icon-external{color:inherit!important}.wrapper-all-inside-input .max-length-name{font-size:inherit;float:right}\n"] }]
        }], ctorParameters: function () { return [{ type: ValidatorHelper }, { type: i0.ElementRef }, { type: i2$1.TranslateService, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { minDate: [{
                type: Input
            }], maxDate: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], value: [{
                type: Input
            }] } });

/**
 * Represents an input component for selecting a numeric value within a specified range.
 *
 * This component allows users to input a numeric value within a defined range and provides visual feedback for the selected value.
 *
 * @implements {AfterContentInit}
 * @implements {OnChanges}
 *
 * <br>
 * <label for='id-exemple-range'>
 *  Example Input Range
 * </label>
 * <input
 *   class='range mb-3'
 *   name="name-exemple-range"
 *   id="id-exemple-range"
 *   type="range"
 * ></input>
 *
 * @example
 * <sq-input-range [name]='"input-range"' [(value)]='number' ></sq-input-range>
 */
class SqInputRangeComponent {
    /**
     * Creates an instance of `SqInputRangeComponent`.
     *
     * @param validatorHelper - An instance of the `ValidatorHelper` service.
     * @param element - A reference to the element hosting the component.
     * @param translate - An optional instance of the `TranslateService` for internationalization.
     */
    constructor(validatorHelper, element, translate) {
        this.validatorHelper = validatorHelper;
        this.translate = translate;
        /**
         * A custom CSS class to apply to the input element.
         */
        this.customClass = '';
        /**
         * The name attribute for the input element.
         *
         * @default 'random-name-[hash-random-code]'
         */
        this.name = `random-name-${(1 + Date.now() + Math.random()).toString().replace('.', '')}`;
        /**
         * The initial value of the input.
         */
        this.value = '0';
        /**
         * Whether the input is read-only.
         */
        this.readonly = false;
        /**
         * Whether the input is required.
         */
        this.required = false;
        /**
         * The label to display for the input.
         */
        this.label = '';
        /**
         * The color of the input component.
         */
        this.color = 'var(--pink)';
        /**
         * The color of the input label.
         */
        this.labelColor = '';
        /**
         * Whether to display an error message below the input.
         */
        this.errorSpan = true;
        /**
         * The step value for the input.
         */
        this.step = 1;
        /**
         * The minimum allowed value for the input.
         */
        this.minNumber = 0;
        /**
         * The maximum allowed value for the input.
         */
        this.maxNumber = 100;
        /**
         * Whether to use form-level error messages.
         */
        this.useFormErrors = true;
        /**
         * Event emitter for changes in the input value.
         */
        this.valueChange = new EventEmitter();
        /**
         * Event emitter for focusing or blurring the input.
         */
        this.inFocus = new EventEmitter();
        /**
         * Event emitter for indicating the validity of the input.
         */
        this.valid = new EventEmitter();
        /**
         * The error message associated with the input.
         */
        this.error = false;
        this.nativeElement = element.nativeElement;
    }
    /**
     * Lifecycle hook that runs after content initialization.
     * It sets a timeout to change the value position.
     */
    async ngAfterContentInit() {
        await sleep();
        this.changeValuePosition();
    }
    /**
     * Lifecycle hook that runs when changes occur in the component's input properties.
     * It recalculates the value position when value-related properties change.
     *
     * @param changes - An object containing changes to input properties.
     */
    ngOnChanges(changes) {
        if (changes.hasOwnProperty('value') || changes.hasOwnProperty('minNumber') || changes.hasOwnProperty('maxNumber')) {
            this.changeValuePosition();
        }
    }
    /**
     * Validates the input value.
     *
     * @param isBlur - Indicates whether the validation is triggered by blurring the input.
     */
    validate(isBlur = false) {
        if (this.externalError) {
            this.error = false;
        }
        else if (!!this.required && !this.value && this.value !== '0') {
            this.setError('forms.required');
            this.valid.emit(false);
        }
        else {
            this.valid.emit(true);
            this.error = '';
        }
        if (isBlur) {
            this.inFocus.emit(false);
        }
    }
    /**
     * Changes the visual position of the input value based on the current value,
     * minimum and maximum allowed values.
     */
    changeValuePosition() {
        const val = parseFloat(this.value);
        const min = this.minNumber ? this.minNumber : 0;
        const max = this.maxNumber ? this.maxNumber : 100;
        const newVal = Number(((val - min) * 100) / (max - min));
        if (this.valueFloating) {
            this.valueFloating.nativeElement.style.left = `calc(${newVal}% + (${10 - newVal * 0.36}px))`;
        }
    }
    /**
     * Handle input value changes.
     * @param event - The input change event.
     */
    change(event) {
        this.inFocus.emit(true);
        this.changeValuePosition();
        this.value = event;
        this.valueChange.emit(event);
        this.validate();
    }
    /**
     * Set an error message based on a translation key.
     * @param key - The translation key for the error message.
     */
    async setError(key) {
        if (this.useFormErrors && this.translate) {
            this.error = await this.translate.instant(key);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqInputRangeComponent, deps: [{ token: ValidatorHelper }, { token: i0.ElementRef }, { token: i2$1.TranslateService, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqInputRangeComponent, selector: "sq-input-range", inputs: { customClass: "customClass", name: "name", id: "id", value: "value", readonly: "readonly", required: "required", label: "label", color: "color", labelColor: "labelColor", errorSpan: "errorSpan", externalError: "externalError", step: "step", minNumber: "minNumber", maxNumber: "maxNumber", useFormErrors: "useFormErrors" }, outputs: { valueChange: "valueChange", inFocus: "inFocus", valid: "valid" }, viewQueries: [{ propertyName: "valueFloating", first: true, predicate: ["valueFloating"], descendants: true }, { propertyName: "input", first: true, predicate: ["input"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"wrapper-all-inside-input {{ customClass }}\" [ngClass]=\"{\n  'no-label': !label.length\n}\">\n  <label\n    class=\"display-flex\"\n    *ngIf=\"label?.length\"\n    [ngClass]=\"{\n      readonly: readonly\n    }\"\n    [for]=\"id\"\n  >\n    <div *ngIf=\"label?.length\" [ngStyle]=\"{ 'color': labelColor }\" [innerHtml]=\"label | universalSafe\"></div>\n  </label>\n  <div\n    class=\"p-0 wrapper-input wrapper-input-squid\"\n    [ngClass]=\"{\n      error: (externalError && externalError !== '') || (error && error !== ''),\n    }\"\n  >\n    <output class=\"value-floating\" #valueFloating>\n      {{ value }}\n    </output>\n    <input\n      [id]=\"id\"\n      type=\"range\"\n      class=\"range\"\n      [name]=\"name\"\n      [step]=\"step\"\n      [required]=\"required\"\n      [disabled]=\"readonly\"\n      [min]=\"minNumber || ''\"\n      [max]=\"maxNumber || ''\"\n      (blur)=\"validate(true)\"\n      [ngModel]=\"value\"\n      (ngModelChange)=\"change($event)\"\n      ngDefaultControl\n      #input\n    />\n  </div>\n  <div class=\"box-validation box-invalid show\" *ngIf=\"errorSpan\">\n    <i [ngClass]=\"{\n      'visibility-hidden-force': !error && !externalError\n    }\" class=\"fa-solid fa-triangle-exclamation\"></i>\n    {{ externalError ? externalError : '' }}\n    {{ error && !externalError ? error : '' }}\n  </div>\n</div>\n", styles: [".wrapper-all-inside-input{position:relative}.wrapper-all-inside-input.no-label{margin-top:2rem}.wrapper-all-inside-input label{margin:0 auto 2.5rem}.wrapper-all-inside-input input[type=range]{border-radius:12px}.wrapper-all-inside-input .value-floating{position:absolute;bottom:26px;font-size:.86rem;line-height:1;left:10px}.wrapper-all-inside-input .box-validation{margin-top:.5rem}.wrapper-all-inside-input .wrapper-input.wrapper-input-squid{position:relative}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i4.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i4.RangeValueAccessor, selector: "input[type=range][formControlName],input[type=range][formControl],input[type=range][ngModel]" }, { kind: "directive", type: i4.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i4.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "pipe", type: UniversalSafePipe, name: "universalSafe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqInputRangeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-input-range', template: "<div class=\"wrapper-all-inside-input {{ customClass }}\" [ngClass]=\"{\n  'no-label': !label.length\n}\">\n  <label\n    class=\"display-flex\"\n    *ngIf=\"label?.length\"\n    [ngClass]=\"{\n      readonly: readonly\n    }\"\n    [for]=\"id\"\n  >\n    <div *ngIf=\"label?.length\" [ngStyle]=\"{ 'color': labelColor }\" [innerHtml]=\"label | universalSafe\"></div>\n  </label>\n  <div\n    class=\"p-0 wrapper-input wrapper-input-squid\"\n    [ngClass]=\"{\n      error: (externalError && externalError !== '') || (error && error !== ''),\n    }\"\n  >\n    <output class=\"value-floating\" #valueFloating>\n      {{ value }}\n    </output>\n    <input\n      [id]=\"id\"\n      type=\"range\"\n      class=\"range\"\n      [name]=\"name\"\n      [step]=\"step\"\n      [required]=\"required\"\n      [disabled]=\"readonly\"\n      [min]=\"minNumber || ''\"\n      [max]=\"maxNumber || ''\"\n      (blur)=\"validate(true)\"\n      [ngModel]=\"value\"\n      (ngModelChange)=\"change($event)\"\n      ngDefaultControl\n      #input\n    />\n  </div>\n  <div class=\"box-validation box-invalid show\" *ngIf=\"errorSpan\">\n    <i [ngClass]=\"{\n      'visibility-hidden-force': !error && !externalError\n    }\" class=\"fa-solid fa-triangle-exclamation\"></i>\n    {{ externalError ? externalError : '' }}\n    {{ error && !externalError ? error : '' }}\n  </div>\n</div>\n", styles: [".wrapper-all-inside-input{position:relative}.wrapper-all-inside-input.no-label{margin-top:2rem}.wrapper-all-inside-input label{margin:0 auto 2.5rem}.wrapper-all-inside-input input[type=range]{border-radius:12px}.wrapper-all-inside-input .value-floating{position:absolute;bottom:26px;font-size:.86rem;line-height:1;left:10px}.wrapper-all-inside-input .box-validation{margin-top:.5rem}.wrapper-all-inside-input .wrapper-input.wrapper-input-squid{position:relative}\n"] }]
        }], ctorParameters: function () { return [{ type: ValidatorHelper }, { type: i0.ElementRef }, { type: i2$1.TranslateService, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { customClass: [{
                type: Input
            }], name: [{
                type: Input
            }], id: [{
                type: Input
            }], value: [{
                type: Input
            }], readonly: [{
                type: Input
            }], required: [{
                type: Input
            }], label: [{
                type: Input
            }], color: [{
                type: Input
            }], labelColor: [{
                type: Input
            }], errorSpan: [{
                type: Input
            }], externalError: [{
                type: Input
            }], step: [{
                type: Input
            }], minNumber: [{
                type: Input
            }], maxNumber: [{
                type: Input
            }], useFormErrors: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], inFocus: [{
                type: Output
            }], valid: [{
                type: Output
            }], valueFloating: [{
                type: ViewChild,
                args: ['valueFloating']
            }], input: [{
                type: ViewChild,
                args: ['input']
            }] } });

/**
 * Represents a file input component that extends SqInputComponent.
 *
 * This component extends the {@link SqInputComponent} and adds additional properties and behavior for handling money input.
 *
 * @example
 * <sq-input-file [name]="'file-input'" [id]="'file-input'" [label]="'Upload File'" [(value)]='files'></sq-input-file>
 */
class SqInputFileComponent extends SqInputComponent {
    /**
     * Constructs a new instance of SqInputFileComponent.
     * @param validatorHelper - The ValidatorHelper service for input validation.
     * @param element - Reference to the native element.
     * @param translate - The TranslateService for translation support (optional).
     */
    constructor(validatorHelper, element, translate) {
        super(validatorHelper, element, translate);
        this.validatorHelper = validatorHelper;
        this.translate = translate;
        /**
         * Text color for the file input.
         */
        this.textColor = 'var(--white-html)';
        /**
         * Border color for the file input.
         */
        this.borderColor = 'var(--pink)';
        /**
         * Color for the file input.
         */
        this.color = 'var(--pink)';
        /**
         * Font size for the file input.
         */
        this.fontSize = '1rem';
        /**
         * Indicates whether the file input is in a loading state.
         */
        this.loading = false;
        /**
         * Allowed file types pattern (e.g., '*.jpg, *.png').
         */
        this.fileType = '*.*';
        /**
         * Indicates whether multiple files can be selected.
         */
        this.multiple = false;
        /**
         * Indicates whether padding should be removed from the file input.
         */
        this.noPadding = false;
        /**
         * Display the file input as a block element.
         */
        this.block = false;
        /**
         * Custom content to be displayed within the file input.
         */
        this.customContent = null;
        this.nativeElement = element.nativeElement;
        this.type = 'file';
    }
    /**
     * Asynchronously validate the file input value.
     * @param isBlur - Indicates if the input has lost focus.
     */
    async validate(isBlur = false) {
        if (this.externalError) {
            this.error = false;
        }
        else if (!!this.required && (!this.value || this.value.length < 1) && this.value !== 0) {
            this.setError('forms.required');
            this.valid.emit(false);
        }
        else if (this.maxSize && this.value && this.value.length > 0) {
            let bigFiles = 0;
            for (const file of this.value) {
                if (file.size > this.maxSize) {
                    bigFiles++;
                }
            }
            if (bigFiles > 0) {
                this.setError('forms.fileSize');
                this.valid.emit(false);
            }
            else {
                this.valid.emit(true);
                this.error = '';
            }
        }
        else {
            this.valid.emit(true);
            this.error = '';
        }
        if (isBlur) {
            this.inFocus.emit(false);
        }
    }
    /**
     * Handle the change event for the file input.
     * @param event - The input change event.
     */
    async change(event) {
        this.value = event.target?.files || event;
        this.valueChange.emit(this.value);
        this.validate();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqInputFileComponent, deps: [{ token: ValidatorHelper }, { token: i0.ElementRef }, { token: i2$1.TranslateService, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqInputFileComponent, selector: "sq-input-file", inputs: { textColor: "textColor", borderColor: "borderColor", color: "color", fontSize: "fontSize", maxSize: "maxSize", loading: "loading", fileType: "fileType", multiple: "multiple", noPadding: "noPadding", block: "block" }, queries: [{ propertyName: "customContent", first: true, predicate: ["customContent"], descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<div\r\n  class=\"wrapper-input-file-squid\"\r\n  [ngClass]=\"{\r\n    error: (externalError && externalError !== '') || (error && error !== ''),\r\n    block: block\r\n  }\"\r\n>\r\n  <label\r\n    *ngIf=\"label?.length\"\r\n    class=\"label\"\r\n    [ngClass]=\"{\r\n      loading: loading\r\n    }\"\r\n  >\r\n    {{ label }}\r\n  </label>\r\n  <label *ngIf=\"customContent\" [for]=\"id\" class=\"label-custom\">\r\n    <ng-container *ngTemplateOutlet=\"customContent\"></ng-container>\r\n  </label>\r\n  <label\r\n    *ngIf=\"!customContent\"\r\n    class=\"placeholder button\"\r\n    [for]=\"id\"\r\n    [ngStyle]=\"{\r\n      'font-size': fontSize,\r\n      color: textColor,\r\n      'background-color': color,\r\n      'border-color': borderColor || textColor\r\n    }\"\r\n    [ngClass]=\"{\r\n      loading: loading,\r\n      'p-0': noPadding\r\n    }\"\r\n  >\r\n    <span\r\n      [innerHtml]=\"placeholder || '' | universalSafe\"\r\n      [ngStyle]=\"{\r\n        'font-size': fontSize\r\n      }\"\r\n    ></span>\r\n    <sq-loader *ngIf=\"loading\" color=\"{{ textColor }}\"></sq-loader>\r\n  </label>\r\n  <input\r\n    #inputFile\r\n    type=\"file\"\r\n    [id]=\"id\"\r\n    [name]=\"name\"\r\n    [required]=\"required\"\r\n    [disabled]=\"disabled || loading\"\r\n    [readonly]=\"disabled || loading\"\r\n    [accept]=\"fileType\"\r\n    (click)=\"inputFile.value = ''\"\r\n    (change)=\"change($event)\"\r\n    (keydown)=\"keyDown($event)\"\r\n    [multiple]=\"multiple\"\r\n  />\r\n  <div\r\n    class=\"box-validation box-invalid show\"\r\n    *ngIf=\"errorSpan\"\r\n    [ngClass]=\"{\r\n      'visibility-hidden-force': ((!externalError || externalError === '') && (!error || error === '')) || disabled || readonly\r\n    }\"\r\n  >\r\n    <i [ngClass]=\"{\r\n      'visibility-hidden-force': !error && !externalError\r\n    }\" class=\"fa-solid fa-triangle-exclamation\"></i>\r\n    {{ externalError ? externalError : '' }}\r\n    {{ error && !externalError ? error : '' }}\r\n  </div>\r\n</div>\r\n", styles: [".wrapper-input-file-squid{display:inline-block}.wrapper-input-file-squid.block{display:block;width:100%}.wrapper-input-file-squid label{color:inherit;width:100%;margin:0;font-size:.86rem;line-height:10px;margin:0 auto .35rem}.wrapper-input-file-squid label.label-custom{margin:0}.wrapper-input-file-squid label.placeholder{line-height:initial;width:initial;display:inline-flex;text-align:center;position:relative;text-transform:capitalize;font-weight:500;font-size:.86rem;border-radius:7px;overflow:hidden;box-shadow:none;border:2px solid transparent;transition:var(--transition);opacity:1;cursor:pointer;margin:0}.wrapper-input-file-squid label.placeholder sq-loader{display:inline-block;margin-left:.35rem}.wrapper-input-file-squid label.placeholder span{color:inherit;width:100%;display:inline-block;margin:0;font-weight:inherit}.wrapper-input-file-squid label.placeholder.p-0{padding:0}.wrapper-input-file-squid label.placeholder:hover,.wrapper-input-file-squid label.placeholder:focus{outline:none;cursor:pointer;color:inherit;border-color:inherit}.wrapper-input-file-squid label.placeholder:hover:before,.wrapper-input-file-squid label.placeholder:focus:before{border-color:inherit}.wrapper-input-file-squid label.placeholder.loading{cursor:wait}.wrapper-input-file-squid label.placeholder.loading:hover,.wrapper-input-file-squid label.placeholder.loading:focus{border-color:transparent!important}.wrapper-input-file-squid label.placeholder.disabled{cursor:not-allowed}.wrapper-input-file-squid input{display:none}.wrapper-input-file-squid .icon{position:absolute;right:35px;bottom:3px;color:var(--color_text-icon_system_success)}.wrapper-input-file-squid.error{background:transparent;border-color:var(--color_border_input_error)!important}.wrapper-input-file-squid.error .icon{color:var(--color_text-icon_system_danger)}.wrapper-input-file-squid.error label.label{color:var(--color_text-icon_system_danger)!important}.error-name{display:block;margin:0 auto;min-height:22px;color:var(--color_text-icon_system_danger);font-weight:700;font-size:.86rem}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: SqLoaderComponent, selector: "sq-loader", inputs: ["size", "customSize", "borderSize", "color", "customClass"] }, { kind: "pipe", type: UniversalSafePipe, name: "universalSafe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqInputFileComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-input-file', template: "<div\r\n  class=\"wrapper-input-file-squid\"\r\n  [ngClass]=\"{\r\n    error: (externalError && externalError !== '') || (error && error !== ''),\r\n    block: block\r\n  }\"\r\n>\r\n  <label\r\n    *ngIf=\"label?.length\"\r\n    class=\"label\"\r\n    [ngClass]=\"{\r\n      loading: loading\r\n    }\"\r\n  >\r\n    {{ label }}\r\n  </label>\r\n  <label *ngIf=\"customContent\" [for]=\"id\" class=\"label-custom\">\r\n    <ng-container *ngTemplateOutlet=\"customContent\"></ng-container>\r\n  </label>\r\n  <label\r\n    *ngIf=\"!customContent\"\r\n    class=\"placeholder button\"\r\n    [for]=\"id\"\r\n    [ngStyle]=\"{\r\n      'font-size': fontSize,\r\n      color: textColor,\r\n      'background-color': color,\r\n      'border-color': borderColor || textColor\r\n    }\"\r\n    [ngClass]=\"{\r\n      loading: loading,\r\n      'p-0': noPadding\r\n    }\"\r\n  >\r\n    <span\r\n      [innerHtml]=\"placeholder || '' | universalSafe\"\r\n      [ngStyle]=\"{\r\n        'font-size': fontSize\r\n      }\"\r\n    ></span>\r\n    <sq-loader *ngIf=\"loading\" color=\"{{ textColor }}\"></sq-loader>\r\n  </label>\r\n  <input\r\n    #inputFile\r\n    type=\"file\"\r\n    [id]=\"id\"\r\n    [name]=\"name\"\r\n    [required]=\"required\"\r\n    [disabled]=\"disabled || loading\"\r\n    [readonly]=\"disabled || loading\"\r\n    [accept]=\"fileType\"\r\n    (click)=\"inputFile.value = ''\"\r\n    (change)=\"change($event)\"\r\n    (keydown)=\"keyDown($event)\"\r\n    [multiple]=\"multiple\"\r\n  />\r\n  <div\r\n    class=\"box-validation box-invalid show\"\r\n    *ngIf=\"errorSpan\"\r\n    [ngClass]=\"{\r\n      'visibility-hidden-force': ((!externalError || externalError === '') && (!error || error === '')) || disabled || readonly\r\n    }\"\r\n  >\r\n    <i [ngClass]=\"{\r\n      'visibility-hidden-force': !error && !externalError\r\n    }\" class=\"fa-solid fa-triangle-exclamation\"></i>\r\n    {{ externalError ? externalError : '' }}\r\n    {{ error && !externalError ? error : '' }}\r\n  </div>\r\n</div>\r\n", styles: [".wrapper-input-file-squid{display:inline-block}.wrapper-input-file-squid.block{display:block;width:100%}.wrapper-input-file-squid label{color:inherit;width:100%;margin:0;font-size:.86rem;line-height:10px;margin:0 auto .35rem}.wrapper-input-file-squid label.label-custom{margin:0}.wrapper-input-file-squid label.placeholder{line-height:initial;width:initial;display:inline-flex;text-align:center;position:relative;text-transform:capitalize;font-weight:500;font-size:.86rem;border-radius:7px;overflow:hidden;box-shadow:none;border:2px solid transparent;transition:var(--transition);opacity:1;cursor:pointer;margin:0}.wrapper-input-file-squid label.placeholder sq-loader{display:inline-block;margin-left:.35rem}.wrapper-input-file-squid label.placeholder span{color:inherit;width:100%;display:inline-block;margin:0;font-weight:inherit}.wrapper-input-file-squid label.placeholder.p-0{padding:0}.wrapper-input-file-squid label.placeholder:hover,.wrapper-input-file-squid label.placeholder:focus{outline:none;cursor:pointer;color:inherit;border-color:inherit}.wrapper-input-file-squid label.placeholder:hover:before,.wrapper-input-file-squid label.placeholder:focus:before{border-color:inherit}.wrapper-input-file-squid label.placeholder.loading{cursor:wait}.wrapper-input-file-squid label.placeholder.loading:hover,.wrapper-input-file-squid label.placeholder.loading:focus{border-color:transparent!important}.wrapper-input-file-squid label.placeholder.disabled{cursor:not-allowed}.wrapper-input-file-squid input{display:none}.wrapper-input-file-squid .icon{position:absolute;right:35px;bottom:3px;color:var(--color_text-icon_system_success)}.wrapper-input-file-squid.error{background:transparent;border-color:var(--color_border_input_error)!important}.wrapper-input-file-squid.error .icon{color:var(--color_text-icon_system_danger)}.wrapper-input-file-squid.error label.label{color:var(--color_text-icon_system_danger)!important}.error-name{display:block;margin:0 auto;min-height:22px;color:var(--color_text-icon_system_danger);font-weight:700;font-size:.86rem}\n"] }]
        }], ctorParameters: function () { return [{ type: ValidatorHelper }, { type: i0.ElementRef }, { type: i2$1.TranslateService, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { textColor: [{
                type: Input
            }], borderColor: [{
                type: Input
            }], color: [{
                type: Input
            }], fontSize: [{
                type: Input
            }], maxSize: [{
                type: Input
            }], loading: [{
                type: Input
            }], fileType: [{
                type: Input
            }], multiple: [{
                type: Input
            }], noPadding: [{
                type: Input
            }], block: [{
                type: Input
            }], customContent: [{
                type: ContentChild,
                args: ['customContent', { static: true }]
            }] } });

/**
 * A custom Angular pipeline to filter an array of objects and transform another array based on a search string.
 *
 * @example
 * // In your component template:
 * // Assuming 'items' is an array of objects and 'searchTerm' is a string.
 * <ul>
 *   <li *ngFor="let item of items | searchFromAlternativeArray: searchTerm">{{ item.name }}</li>
 * </ul>
 *
 * @param {any[]} value - The array of objects to filter.
 * @param {string} search - The search string to filter by.
 * @returns {any[]} - The filtered array of objects.
 */
class SearchFromAlternativeArrayPipe {
    /**
     * Transforms an array of objects by filtering into an alternate array based on a search string.
     *
     * @param {any[]} value - The array of objects to transform.
     * @param {string} search - The search string to filter by.
     * @param {any[]} alternative - The array to filter the values.
     * @returns {any[]} - The filtered array of objects.
     */
    transform(value, search, alternative) {
        if (!search) {
            return value;
        }
        if (!value) {
            return '';
        }
        if (!alternative?.length) {
            return '';
        }
        const solution = alternative?.filter((v) => {
            if (!v) {
                return false;
            }
            return JSON.stringify(v).toLowerCase().indexOf(search.toLowerCase()) > -1;
        });
        return solution;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SearchFromAlternativeArrayPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.8", ngImport: i0, type: SearchFromAlternativeArrayPipe, name: "searchFromAlternativeArray" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SearchFromAlternativeArrayPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'searchFromAlternativeArray' }]
        }] });

/**
 * A custom Angular pipe for translating text using ngx-translate/core.
 *
 * This pipe allows you to translate a given query using ngx-translate/core's TranslateService.
 * If the TranslateService is available, it fetches the translation for the provided query and optional arguments.
 * If the TranslateService is not available, it returns an empty string.
 *
 * Usage:
 * ```html
 * {{ 'HELLO' | translateInternal | async }}
 * ```
 *
 * @example
 * // In a component's template:
 * <div>{{ 'HELLO' | translateInternal | async }}</div>
 *
 * @param query - The translation key or query to be translated.
 * @param args - Optional arguments that can be passed to the translation service.
 * @returns The translated text if the TranslateService is available; otherwise, an empty string.
 *
 * @see {@link https://github.com/ngx-translate/core|ngx-translate/core}
 */
class TranslateInternalPipe {
    /**
     * Creates a new instance of the TranslateInternalPipe.
     *
     * @param translate - The optional TranslateService instance to be injected. If provided, it will be used for translation.
     */
    constructor(translate) {
        this.translate = translate;
    }
    /**
     * Transforms the input query into its translated version.
     *
     * @param query - The translation key or query to be translated.
     * @param args - Optional arguments that can be passed to the translation service.
     * @returns The translated text if the TranslateService is available; otherwise, an empty string.
     */
    async transform(query, ...args) {
        if (this.translate) {
            return await lastValueFrom(this.translate.get(query, args)) || '';
        }
        return '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: TranslateInternalPipe, deps: [{ token: i2$1.TranslateService, optional: true }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.8", ngImport: i0, type: TranslateInternalPipe, name: "translateInternal" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: TranslateInternalPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'translateInternal' }]
        }], ctorParameters: function () { return [{ type: i2$1.TranslateService, decorators: [{
                    type: Optional
                }] }]; } });

/**
 * Represents a search-based select component.
 *
 * @example
 * <sq-select-search
 *   [name]="'search'"
 *   [value]="selectedOption"
 *   [options]="searchOptions"
 *   (valueChange)="handleOptionSelection($event)"
 *   (searchChange)="handleSearch($event)"
 * >
 * </sq-select-search>
 *
 * @implements {OnChanges}
 */
class SqSelectSearchComponent {
    /**
     * Constructs a new SqSelectSearchComponent.
     *
     * @param {ElementRef} element - The element reference.
     * @param {TranslateService} translate - The optional TranslateService for internationalization.
     * @param {ChangeDetectorRef} changeDetector - Base class that provides change detection functionality.
     */
    constructor(element, translate, changeDetector) {
        this.element = element;
        this.translate = translate;
        this.changeDetector = changeDetector;
        /**
         * The name attribute for the search-based select input.
         */
        this.name = `random-name-${(1 + Date.now() + Math.random()).toString().replace('.', '')}`;
        /**
         * The label for the search-based select input.
         */
        this.label = '';
        /**
         * Custom CSS class for styling the component.
         */
        this.customClass = '';
        /**
         * Placeholder text for the input field.
         */
        this.placeholder = '';
        /**
         * External error message for the search-based select input.
         */
        this.externalError = '';
        /**
         * External icon for the search-based select input.
         */
        this.externalIcon = '';
        /**
         * Placeholder text for the search input field.
         */
        this.placeholderSearch = '';
        /**
         * Indicates whether the search-based select input is disabled.
         */
        this.disabled = false;
        /**
         * Indicates whether the search-based select input is readonly.
         */
        this.readonly = false;
        /**
         * Indicates whether the search-based select input is required.
         */
        this.required = false;
        /**
         * Indicates whether the search-based select input is in a loading state.
         */
        this.loading = false;
        /**
         * Indicates whether to use form errors for validation.
         */
        this.useFormErrors = true;
        /**
         * Indicates whether to display an error span.
         */
        this.errorSpan = true;
        /**
         * The time interval for input timeout in ms.
         */
        this.timeToChange = 800;
        /**
         * Options available for selection.
         */
        this.options = [];
        /**
         * Background color for the search-based select input.
         */
        this.backgroundColor = '';
        /**
         * Border color for the search-based select input.
         */
        this.borderColor = '';
        /**
         * Color for the label of the search-based select input.
         */
        this.labelColor = '';
        /**
         * Tooltip message for the search-based select input.
         */
        this.tooltipMessage = '';
        /**
         * Tooltip placement for the search-based select input.
         */
        this.tooltipPlacement = 'right center';
        /**
         * Tooltip color for the search-based select input.
         */
        this.tooltipColor = 'inherit';
        /**
         * Tooltip icon for the search-based select input.
         */
        this.tooltipIcon = '';
        /**
         * Event emitted when the selected value changes.
         */
        this.valueChange = new EventEmitter();
        /**
         * Event emitted when the search input value changes.
         */
        this.searchChange = new EventEmitter();
        /**
         * Event emitted when the search-based select input becomes valid or invalid.
         */
        this.valid = new EventEmitter();
        /**
         * Error message associated with the search-based select input.
         */
        this.error = '';
        /**
         * Timeout duration for input changes.
         */
        this.timeOutInputTime = 800;
        /**
         * Text entered in the search input field.
         */
        this.searchText = '';
        /**
         * Indicates when is the time to render the multi-tag select dropdown.
         */
        this.renderOptionsList = false;
        /**
         * Indicates whether the dropdown is open.
         */
        this.open = false;
        /**
         * Control pagination for options
         */
        this._options = [];
        /**
         * Indicate if has more options to add on _options
         */
        this.hasMoreOptions = true;
        /**
         * Loading for sq-infinity-scroll
         */
        this.loadingScroll = false;
        /**
         * Control quantity for limit and to addMore on _options
         */
        this.quantity = 15;
        /**
         * Control the _options limit
         */
        this.limit = this.quantity;
        /**
         * Return trackBy for ngFor
         */
        this.trackByOptValue = useMemo((index, opt) => opt.value);
        this.nativeElement = element.nativeElement;
    }
    /**
     * Lifecycle hook called when any input properties change.
     *
     * @param changes - The changes detected in the component's input properties.
     */
    async ngOnChanges(changes) {
        if (this.open && changes.hasOwnProperty('options')) {
            this.addMoreOptions(true);
        }
    }
    /**
     * Emits the selected value and closes the dropdown.
     *
     * @param {any} event - The event containing the selected value.
     */
    emit(event) {
        this.value = event;
        this.valueChange.emit(this.value);
        this.validate();
        this.closeDropdown();
    }
    /**
     * Validates the search-based select input and sets the error state.
     */
    validate() {
        if (this.externalError) {
            this.error = false;
        }
        else if (this.required && !this.value) {
            this.setError('forms.required');
            this.valid.emit(false);
        }
        else {
            this.valid.emit(true);
            this.error = '';
        }
    }
    /**
     * Do action to open or close thw dropdown list
     */
    async doDropDownAction() {
        if (this.open) {
            this.closeDropdown();
            this.renderOptionsList = await new Promise(resolve => setTimeout(() => {
                resolve(false);
            }, 300));
            this.changeDetector.detectChanges();
        }
        else {
            this.addMoreOptions();
            this.renderOptionsList = true;
            this.open = await new Promise(resolve => setTimeout(() => {
                resolve(true);
            }, 100));
            this.changeDetector.detectChanges();
        }
    }
    /**
     * Closes the dropdown and resets the search text.
     */
    closeDropdown() {
        this.open = false;
        this._options = [];
        this.limit = this.quantity;
        this.hasMoreOptions = true;
        this.searchText = '';
    }
    /**
     * Handles changes to the search input value.
     *
     * @param {string} event - The search input value.
     */
    async onTipSearchChange(event) {
        clearTimeout(this.timeoutInput);
        this.searchText = await new Promise(resolve => this.timeoutInput = setTimeout(() => {
            resolve(event);
        }, this.timeToChange)) || '';
        this.searchChange.emit(event);
        this.changeDetector.detectChanges();
    }
    /**
     * Sets an error message.
     *
     * @param {string} key - The translation key for the error message.
     */
    async setError(key) {
        if (this.useFormErrors && this.translate) {
            this.error = await this.translate.instant(key);
        }
    }
    /**
      * Function to add more values on _options
      */
    addMoreOptions(isOnChange = false) {
        if (this.hasMoreOptions || isOnChange) {
            this.loadingScroll = true;
            const limitState = this.limit > this.options.length ? this.options.length : this.limit;
            this._options = this.options.slice(0, limitState);
            this.limit = this.limit + this.quantity;
            this.hasMoreOptions = limitState !== this.options.length;
            this.loadingScroll = false;
            this.changeDetector.detectChanges();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqSelectSearchComponent, deps: [{ token: i0.ElementRef }, { token: i2$1.TranslateService, optional: true }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqSelectSearchComponent, selector: "sq-select-search", inputs: { name: "name", value: "value", id: "id", label: "label", customClass: "customClass", placeholder: "placeholder", externalError: "externalError", externalIcon: "externalIcon", placeholderSearch: "placeholderSearch", disabled: "disabled", readonly: "readonly", required: "required", loading: "loading", useFormErrors: "useFormErrors", errorSpan: "errorSpan", timeToChange: "timeToChange", options: "options", backgroundColor: "backgroundColor", borderColor: "borderColor", labelColor: "labelColor", tooltipMessage: "tooltipMessage", tooltipPlacement: "tooltipPlacement", tooltipColor: "tooltipColor", tooltipIcon: "tooltipIcon" }, outputs: { valueChange: "valueChange", searchChange: "searchChange", valid: "valid" }, providers: [], usesOnChanges: true, ngImport: i0, template: "<div\n  class=\"wrapper-all-inside-input wrapper-select-search {{ customClass }}\"\n  [ngClass]=\"{\n    error: (externalError && externalError !== '') || (error && error !== ''),\n    readonly: readonly,\n    loading: loading\n  }\"\n>\n  <label\n    class=\"display-flex\"\n    *ngIf=\"label?.length || tooltipMessage\"\n    [ngClass]=\"{\n      readonly: readonly\n    }\"\n    [for]=\"id\"\n  >\n    <div *ngIf=\"label\" [ngStyle]=\"{ 'color': labelColor }\" [innerHtml]=\"label | universalSafe\"></div>\n    <sq-tooltip\n      *ngIf=\"tooltipMessage\"\n      class=\"ml-1\"\n      [message]=\"tooltipMessage\"\n      [placement]=\"tooltipPlacement\"\n      [color]=\"tooltipColor\"\n      [icon]=\"tooltipIcon\"\n    ></sq-tooltip>\n  </label>\n  <div\n    [class]=\"'input-fake col  border-' + borderColor\"\n    style=\"min-height: auto\"\n    [ngStyle]=\"{ 'border-color': borderColor }\"\n    [ngClass]=\"{\n      'no-label': !label.length,\n      'has-icon': error || externalError,\n      disabled: disabled,\n      readonly: readonly\n    }\"\n    [ngStyle]=\"{\n      'background-color': backgroundColor,\n      'border-color': borderColor\n    }\"\n    (clickOutside)=\"closeDropdown()\"\n    [clickOutsideEnabled]=\"open\"\n  >\n    <div\n      class=\"input-fake-content\"\n      [ngClass]=\"{\n        disabled: disabled\n      }\"\n      (click)=\"doDropDownAction()\"\n    >\n      <div class=\"loading-wrapper\" *ngIf=\"loading\">\n        <sq-loader></sq-loader>\n      </div>\n      <span *ngIf=\"!value\">{{ placeholder }}</span>\n      <div class=\"input-fake-content-text\" *ngIf=\"value\">\n        <span class=\"text\" *ngIf=\"value\">\n          {{ value!.label }}\n        </span>\n      </div>\n      <i *ngIf=\"!loading\" class=\"icon-down fas fa-chevron-down\"></i>\n    </div>\n    <div\n      *ngIf=\"!disabled && !loading && renderOptionsList\"\n      class=\"input-window scrollbar\"\n      id=\"sq-select-search-scroll\"\n      [ngClass]=\"{\n        open: !disabled && !loading && renderOptionsList && open\n      }\"\n    >\n    <div class=\"input-search\">\n      <div class=\"wrapper-all-inside-input\">\n        <div class=\"p-0 wrapper-input wrapper-input-squid text-ellipsisarea\">\n          <input\n            [name]=\"name\"\n            [id]=\"id\"\n            [placeholder]=\"placeholderSearch || ('forms.search' | translateInternal | async) || ''\"\n            class=\"col input\"\n            [ngModel]=\"searchText\"\n            (ngModelChange)=\"onTipSearchChange($event)\"\n          />\n        </div>\n        <span class=\"icon icon-external textarea-icon\"><i class='fas fa-search'></i></span>\n      </div>\n    </div>\n    <sq-infinity-scroll\n      class=\"list\"\n      elementToScrollId=\"sq-select-search-scroll\"\n      loaderColor=\"var(--pink)\"\n      [length]=\"_options.length || 0\"\n      [hasMore]=\"hasMoreOptions\"\n      [loading]=\"loadingScroll\"\n      (scrolledEmitter)=\"addMoreOptions()\"\n    >\n      <ng-container *ngFor=\"let opt of _options | searchFromAlternativeArray:searchText:options; let i = index; trackBy: trackByOptValue\">\n        <ng-template *ngTemplateOutlet=\"option; context: { opt: opt, i: i }\"></ng-template>\n      </ng-container>\n    </sq-infinity-scroll>\n    <p class=\"mb-0 mt-3\" *ngIf=\"!_options?.length\">\n      {{ 'forms.searchSelectEmpty' | translateInternal | async }}\n    </p>\n    </div>\n  </div>\n  <div\n    class=\"box-validation box-invalid show\"\n    *ngIf=\"errorSpan\"\n    [ngClass]=\"{\n      'visibility-hidden-force': ((!externalError || externalError === '') && (!error || error === '')) || disabled || readonly\n    }\"\n  >\n    <i [ngClass]=\"{\n      'visibility-hidden-force': !error && !externalError\n    }\" class=\"fa-solid fa-triangle-exclamation\"></i>\n    {{ externalError ? externalError : '' }}\n    {{ error && !externalError ? error : '' }}\n  </div>\n</div>\n<ng-template #option let-opt=\"opt\" let-i=\"i\">\n  <li (click)=\"emit(opt)\">\n    <span class=\"text m-0 display-inline-block\">{{ opt?.label }}</span>\n  </li>\n</ng-template>\n", styles: [".wrapper-select-search{margin:0;padding:0;text-align:left;display:flex;flex-direction:column;position:relative}.wrapper-select-search.loading{cursor:wait}.wrapper-select-search .input-fake-content-text{display:flex;justify-content:flex-start;align-items:center;flex-wrap:wrap;gap:5px;max-width:calc(100% - 40px)}.wrapper-select-search .input-fake-content-text .text{margin:0;display:flex;width:100%;height:100%;font-weight:700;align-items:center;justify-content:flex-start}.wrapper-select-search .input-fake-content{min-height:44px;border:1px solid var(--border_color);background:var(--background);padding:.75rem 1rem;color:var(--text_color);border-radius:5px}.wrapper-select-search .input-fake-content .icon-down{right:5px;top:18px;position:absolute;font-size:.86rem}.wrapper-select-search .loading-wrapper{position:absolute;right:5px;bottom:10px}.wrapper-select-search .input-search{display:inline-block;position:sticky;top:0;z-index:1;width:100%;padding-top:1.1rem;margin-bottom:.7rem;background-color:var(--background_secondary)}.wrapper-select-search .input-search .wrapper-all-inside-input .icon{margin:0;font-size:1rem;font-weight:700;position:absolute;right:11px;top:27px}.wrapper-select-search .input-search .wrapper-all-inside-input .icon-external{color:inherit!important}.wrapper-select-search .input-window{width:100%;position:absolute;top:100%;max-height:0;height:0;transition:all .3s ease;overflow:hidden;z-index:5}.wrapper-select-search .input-window.open{height:auto;max-height:400px;background:var(--background_secondary);padding:0 .7rem 1.1rem;overflow-y:auto;box-shadow:var(--box_shadow)}.wrapper-select-search .list{padding:0;list-style:none;margin:0;width:100%;margin:.7rem 0 0}.wrapper-select-search .list li{margin:0;cursor:pointer;min-height:30px;flex-flow:row wrap;display:flex;align-items:center;justify-content:flex-start;transition:var(--transition);padding:.35rem;border-radius:5px}.wrapper-select-search .list li:hover{background-color:var(--border_color)}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i4.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i4.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: SqLoaderComponent, selector: "sq-loader", inputs: ["size", "customSize", "borderSize", "color", "customClass"] }, { kind: "component", type: SqTooltipComponent, selector: "sq-tooltip", inputs: ["color", "icon", "placement", "message", "tooltipClass", "textAlign", "fontSize", "trigger"] }, { kind: "component", type: SqInfinityComponent, selector: "sq-infinity-scroll", inputs: ["length", "endMessage", "hasMore", "loading", "loaderColor", "elementToScrollId"], outputs: ["scrolledEmitter"] }, { kind: "directive", type: SqClickOutsideDirective, selector: "[clickOutside]", inputs: ["clickOutsideEnabled"], outputs: ["clickOutside"] }, { kind: "pipe", type: i2.AsyncPipe, name: "async" }, { kind: "pipe", type: UniversalSafePipe, name: "universalSafe" }, { kind: "pipe", type: SearchFromAlternativeArrayPipe, name: "searchFromAlternativeArray" }, { kind: "pipe", type: TranslateInternalPipe, name: "translateInternal" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqSelectSearchComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-select-search', changeDetection: ChangeDetectionStrategy.OnPush, providers: [], template: "<div\n  class=\"wrapper-all-inside-input wrapper-select-search {{ customClass }}\"\n  [ngClass]=\"{\n    error: (externalError && externalError !== '') || (error && error !== ''),\n    readonly: readonly,\n    loading: loading\n  }\"\n>\n  <label\n    class=\"display-flex\"\n    *ngIf=\"label?.length || tooltipMessage\"\n    [ngClass]=\"{\n      readonly: readonly\n    }\"\n    [for]=\"id\"\n  >\n    <div *ngIf=\"label\" [ngStyle]=\"{ 'color': labelColor }\" [innerHtml]=\"label | universalSafe\"></div>\n    <sq-tooltip\n      *ngIf=\"tooltipMessage\"\n      class=\"ml-1\"\n      [message]=\"tooltipMessage\"\n      [placement]=\"tooltipPlacement\"\n      [color]=\"tooltipColor\"\n      [icon]=\"tooltipIcon\"\n    ></sq-tooltip>\n  </label>\n  <div\n    [class]=\"'input-fake col  border-' + borderColor\"\n    style=\"min-height: auto\"\n    [ngStyle]=\"{ 'border-color': borderColor }\"\n    [ngClass]=\"{\n      'no-label': !label.length,\n      'has-icon': error || externalError,\n      disabled: disabled,\n      readonly: readonly\n    }\"\n    [ngStyle]=\"{\n      'background-color': backgroundColor,\n      'border-color': borderColor\n    }\"\n    (clickOutside)=\"closeDropdown()\"\n    [clickOutsideEnabled]=\"open\"\n  >\n    <div\n      class=\"input-fake-content\"\n      [ngClass]=\"{\n        disabled: disabled\n      }\"\n      (click)=\"doDropDownAction()\"\n    >\n      <div class=\"loading-wrapper\" *ngIf=\"loading\">\n        <sq-loader></sq-loader>\n      </div>\n      <span *ngIf=\"!value\">{{ placeholder }}</span>\n      <div class=\"input-fake-content-text\" *ngIf=\"value\">\n        <span class=\"text\" *ngIf=\"value\">\n          {{ value!.label }}\n        </span>\n      </div>\n      <i *ngIf=\"!loading\" class=\"icon-down fas fa-chevron-down\"></i>\n    </div>\n    <div\n      *ngIf=\"!disabled && !loading && renderOptionsList\"\n      class=\"input-window scrollbar\"\n      id=\"sq-select-search-scroll\"\n      [ngClass]=\"{\n        open: !disabled && !loading && renderOptionsList && open\n      }\"\n    >\n    <div class=\"input-search\">\n      <div class=\"wrapper-all-inside-input\">\n        <div class=\"p-0 wrapper-input wrapper-input-squid text-ellipsisarea\">\n          <input\n            [name]=\"name\"\n            [id]=\"id\"\n            [placeholder]=\"placeholderSearch || ('forms.search' | translateInternal | async) || ''\"\n            class=\"col input\"\n            [ngModel]=\"searchText\"\n            (ngModelChange)=\"onTipSearchChange($event)\"\n          />\n        </div>\n        <span class=\"icon icon-external textarea-icon\"><i class='fas fa-search'></i></span>\n      </div>\n    </div>\n    <sq-infinity-scroll\n      class=\"list\"\n      elementToScrollId=\"sq-select-search-scroll\"\n      loaderColor=\"var(--pink)\"\n      [length]=\"_options.length || 0\"\n      [hasMore]=\"hasMoreOptions\"\n      [loading]=\"loadingScroll\"\n      (scrolledEmitter)=\"addMoreOptions()\"\n    >\n      <ng-container *ngFor=\"let opt of _options | searchFromAlternativeArray:searchText:options; let i = index; trackBy: trackByOptValue\">\n        <ng-template *ngTemplateOutlet=\"option; context: { opt: opt, i: i }\"></ng-template>\n      </ng-container>\n    </sq-infinity-scroll>\n    <p class=\"mb-0 mt-3\" *ngIf=\"!_options?.length\">\n      {{ 'forms.searchSelectEmpty' | translateInternal | async }}\n    </p>\n    </div>\n  </div>\n  <div\n    class=\"box-validation box-invalid show\"\n    *ngIf=\"errorSpan\"\n    [ngClass]=\"{\n      'visibility-hidden-force': ((!externalError || externalError === '') && (!error || error === '')) || disabled || readonly\n    }\"\n  >\n    <i [ngClass]=\"{\n      'visibility-hidden-force': !error && !externalError\n    }\" class=\"fa-solid fa-triangle-exclamation\"></i>\n    {{ externalError ? externalError : '' }}\n    {{ error && !externalError ? error : '' }}\n  </div>\n</div>\n<ng-template #option let-opt=\"opt\" let-i=\"i\">\n  <li (click)=\"emit(opt)\">\n    <span class=\"text m-0 display-inline-block\">{{ opt?.label }}</span>\n  </li>\n</ng-template>\n", styles: [".wrapper-select-search{margin:0;padding:0;text-align:left;display:flex;flex-direction:column;position:relative}.wrapper-select-search.loading{cursor:wait}.wrapper-select-search .input-fake-content-text{display:flex;justify-content:flex-start;align-items:center;flex-wrap:wrap;gap:5px;max-width:calc(100% - 40px)}.wrapper-select-search .input-fake-content-text .text{margin:0;display:flex;width:100%;height:100%;font-weight:700;align-items:center;justify-content:flex-start}.wrapper-select-search .input-fake-content{min-height:44px;border:1px solid var(--border_color);background:var(--background);padding:.75rem 1rem;color:var(--text_color);border-radius:5px}.wrapper-select-search .input-fake-content .icon-down{right:5px;top:18px;position:absolute;font-size:.86rem}.wrapper-select-search .loading-wrapper{position:absolute;right:5px;bottom:10px}.wrapper-select-search .input-search{display:inline-block;position:sticky;top:0;z-index:1;width:100%;padding-top:1.1rem;margin-bottom:.7rem;background-color:var(--background_secondary)}.wrapper-select-search .input-search .wrapper-all-inside-input .icon{margin:0;font-size:1rem;font-weight:700;position:absolute;right:11px;top:27px}.wrapper-select-search .input-search .wrapper-all-inside-input .icon-external{color:inherit!important}.wrapper-select-search .input-window{width:100%;position:absolute;top:100%;max-height:0;height:0;transition:all .3s ease;overflow:hidden;z-index:5}.wrapper-select-search .input-window.open{height:auto;max-height:400px;background:var(--background_secondary);padding:0 .7rem 1.1rem;overflow-y:auto;box-shadow:var(--box_shadow)}.wrapper-select-search .list{padding:0;list-style:none;margin:0;width:100%;margin:.7rem 0 0}.wrapper-select-search .list li{margin:0;cursor:pointer;min-height:30px;flex-flow:row wrap;display:flex;align-items:center;justify-content:flex-start;transition:var(--transition);padding:.35rem;border-radius:5px}.wrapper-select-search .list li:hover{background-color:var(--border_color)}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i2$1.TranslateService, decorators: [{
                    type: Optional
                }] }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { name: [{
                type: Input
            }], value: [{
                type: Input
            }], id: [{
                type: Input
            }], label: [{
                type: Input
            }], customClass: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], externalError: [{
                type: Input
            }], externalIcon: [{
                type: Input
            }], placeholderSearch: [{
                type: Input
            }], disabled: [{
                type: Input
            }], readonly: [{
                type: Input
            }], required: [{
                type: Input
            }], loading: [{
                type: Input
            }], useFormErrors: [{
                type: Input
            }], errorSpan: [{
                type: Input
            }], timeToChange: [{
                type: Input
            }], options: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }], borderColor: [{
                type: Input
            }], labelColor: [{
                type: Input
            }], tooltipMessage: [{
                type: Input
            }], tooltipPlacement: [{
                type: Input
            }], tooltipColor: [{
                type: Input
            }], tooltipIcon: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], searchChange: [{
                type: Output
            }], valid: [{
                type: Output
            }] } });

/**
 * A custom Angular channel to filter an array of objects where it returns the searched value or the original value if it does not have the searched result based on a search string.
 *
 * @example
 * // In your component template:
 * // Assuming 'items' is an array of objects and 'searchTerm' is a string.
 * <ul>
 *   <li *ngFor="let item of items | searchValidValues: searchTerm">{{ item.name }}</li>
 * </ul>
 *
 * @param {any[]} value - The array of objects to filter.
 * @param {string} search - The search string to filter by.
 * @returns {any[]} - The filtered array of objects.
 */
class SearchValidValuesPipe {
    /**
     * Transforms an array of objects with the searched value or the original value if it does not have the searched result by filtering based on a search string.
     *
     * @param {any[]} value - The array of objects to filter.
     * @param {string} search - The search string to filter by.
     * @returns {any[]} - The filtered array of objects.
     */
    transform(value, search) {
        if (!search) {
            return value;
        }
        if (!value) {
            return '';
        }
        const solution = value?.filter((v) => {
            if (!v) {
                return false;
            }
            return JSON.stringify(v).toLowerCase().indexOf(search.toLowerCase()) > -1;
        });
        if (!solution?.length) {
            return value;
        }
        return solution;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SearchValidValuesPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.8", ngImport: i0, type: SearchValidValuesPipe, name: "searchValidValues" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SearchValidValuesPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'searchValidValues' }]
        }] });

/**
 * Represents a multi-tag select component.
 *
 * @example
 * <sq-select-multi-tags
 *   [name]="'tags'"
 *   [value]="selectedTags"
 *   [options]="tagOptions"
 *   (valueChange)="handleTagSelection($event)"
 * >
 * </sq-select-multi-tags>
 *
 * @implements {OnChanges}
 */
class SqSelectMultiTagsComponent {
    /**
     * Constructs a new SqSelectMultiTagsComponent.
     *
     * @param {ElementRef} element - The element reference.
     * @param {TranslateService} translate - The optional TranslateService for internationalization.
     * @param {ChangeDetectorRef} changeDetector - Base class that provides change detection functionality.
     */
    constructor(element, translate, changeDetector) {
        this.element = element;
        this.translate = translate;
        this.changeDetector = changeDetector;
        /**
         * The name attribute for the multi-tag select input.
         *
         * @default 'random-name-[hash-random-code]'
         */
        this.name = `random-name-${(1 + Date.now() + Math.random()).toString().replace('.', '')}`;
        /**
         * The selected values for the multi-tag select input.
         */
        this.value = [];
        /**
         * The label for the multi-tag select input.
         */
        this.label = '';
        /**
         * Custom CSS class for styling the component.
         */
        this.customClass = '';
        /**
         * Placeholder text for the input field.
         */
        this.placeholder = '';
        /**
         * External error message for the multi-tag select input.
         */
        this.externalError = '';
        /**
         * External icon for the multi-tag select input.
         */
        this.externalIcon = '';
        /**
         * Placeholder text for the search input field.
         */
        this.placeholderSearch = '';
        /**
         * Indicates whether the multi-tag select input is disabled.
         */
        this.disabled = false;
        /**
         * Indicates whether the multi-tag select input is readonly.
         */
        this.readonly = false;
        /**
         * Indicates whether the multi-tag select input is required.
         */
        this.required = false;
        /**
         * Indicates whether the multi-tag select input is in a loading state.
         */
        this.loading = false;
        /**
         * Indicates whether to use form errors for validation.
         */
        this.useFormErrors = true;
        /**
         * Indicates whether to display an error span.
         */
        this.errorSpan = true;
        /**
         * Background color for the multi-tag select input.
         */
        this.backgroundColor = '';
        /**
         * Border color for the multi-tag select input.
         */
        this.borderColor = '';
        /**
         * Color for the label of the multi-tag select input.
         */
        this.labelColor = '';
        /**
         * Options available for selection.
         */
        this.options = [];
        /**
         * Indicates whether to show selected tags inside the input.
         */
        this.showInside = true;
        /**
         * Indicates whether to hide the search input.
         */
        this.hideSearch = false;
        /**
         * Tooltip message for the multi-tag select input.
         */
        this.tooltipMessage = '';
        /**
         * Tooltip placement for the multi-tag select input.
         */
        this.tooltipPlacement = 'right center';
        /**
         * Tooltip color for the multi-tag select input.
         */
        this.tooltipColor = 'inherit';
        /**
         * Tooltip icon for the multi-tag select input.
         */
        this.tooltipIcon = '';
        /**
         * Event emitted when the selected values change.
         */
        this.valueChange = new EventEmitter();
        /**
         * Event emitted when the search input value changes.
         */
        this.searchChange = new EventEmitter();
        /**
         * Event emitted when the multi-tag select dropdown is closed.
         */
        this.closeChange = new EventEmitter();
        /**
         * Event emitted when a tag is removed.
         */
        this.removeTag = new EventEmitter();
        /**
         * Event emitted when the multi-tag select input becomes valid or invalid.
         */
        this.valid = new EventEmitter();
        /**
         * Indicates when is the time to render the multi-tag select dropdown.
         */
        this.renderOptionsList = false;
        /**
         * Indicates whether the multi-tag select dropdown is open.
         */
        this.open = false;
        /**
         * Text entered in the search input field.
         */
        this.searchText = '';
        /**
         * Indicates whether the value has changed.
         */
        this.valueChanged = false;
        /**
         * Indicates whether a timeout has occurred for input changes.
         */
        this.timeouted = false;
        /**
         * Error message associated with the multi-tag select input.
         */
        this.error = '';
        /**
         * Time in milliseconds before triggering input timeout.
         */
        this.timeToChange = 800;
        /**
         * Control pagination for options
         */
        this._options = [];
        /**
         * Indicate if has more options to add on _options
         */
        this.hasMoreOptions = true;
        /**
         * Loading for sq-infinity-scroll
         */
        this.loadingScroll = false;
        /**
         * Control quantity for limit and to addMore on _options
         */
        this.quantity = 15;
        /**
         * Control the _options limit
         */
        this.limit = this.quantity;
        /**
         * Control the readonly on reach the maxTags
         */
        this.isMaxTags = false;
        /**
         * Determines if an item exists in the selected values.
         *
         * @param {OptionMulti} item - The item to search for.
         * @returns {boolean} True if the item exists in the selected values; otherwise, false.
         */
        this.findItemInValue = useMemo((item, value) => {
            return !!value?.find((value) => value.value === item.value);
        });
        /**
         * Verifies if any options have children.
         *
         * @param {OptionMulti[]} options - The options to check.
         * @returns {boolean} True if any option has children; otherwise, false.
         */
        this.verifyIfOptionsHasChildren = useMemo((options) => {
            return options.some((item) => item.children?.length);
        });
        /**
         * Verifies if an item has children that are selected.
         *
         * @param {OptionMulti} item - The item to check.
         * @returns {boolean} True if the item has selected children; otherwise, false.
         */
        this.verifyIfHasChildrenInValue = useMemo((item, value) => {
            if (item.children?.length) {
                const hasAllChildren = item.children.every((child) => this.findItemInValue(child, value));
                if (hasAllChildren && !this.findItemInValue(item, value) && !this.timeouted) {
                    this.timeouted = true;
                    setTimeout(() => {
                        this.emit(item, true);
                        this.timeouted = false;
                    }, 0);
                }
                return !!item.children.find((child) => this.findItemInValue(child, value));
            }
            return false;
        });
        /**
         * Return trackBy for ngFor
         */
        this.trackByOptValue = useMemo((index, opt) => opt.value);
        this.nativeElement = element.nativeElement;
    }
    /**
     * Lifecycle hook called when any input properties change.
     *
     * @param changes - The changes detected in the component's input properties.
     */
    async ngOnChanges(changes) {
        if (this.open && changes.hasOwnProperty('options')) {
            this.addMoreOptions(true);
        }
    }
    /**
     * Removes an item from the selected values.
     *
     * @param {OptionMulti} item - The item to remove.
     */
    removeItem(item, event) {
        event?.stopPropagation();
        if (!this.readonly && !this.disabled) {
            if (item.children?.length) {
                item.children.forEach((child) => {
                    this.value = this.value?.filter((value) => value.value !== child.value);
                });
            }
            this.value = this.value?.filter((value) => value.value !== item.value);
            this.valueChange.emit(this.value);
            this.removeTag.emit(item);
            this.validate();
        }
    }
    /**
     * Emits a change event with the specified object and checked state.
     *
     * @param {OptionMulti} object - The object to emit.
     * @param {boolean} checked - The checked state.
     */
    emit(object, checked) {
        if (checked) {
            this.value?.push(object);
            // This code adds all children of a parent to value. Commented out for now as it is not the desired behavior.
            // if (object.children?.length) {
            //   object.children.forEach((item) => {
            //     if (!this.value.find((child) => child.value === item.value)) {
            //       this.value.push(item)
            //     }
            //   })
            // }
        }
        else {
            this.value = this.value?.filter((item) => item.value !== object.value);
            if (object.children?.length) {
                object.children.forEach((item) => {
                    this.value = this.value?.filter((child) => child.value !== item.value);
                });
            }
        }
        this.valueChanged = true;
        this.valueChange.emit(this.value);
        this.validate();
    }
    /**
     * Do action to open or close thw dropdown list
     */
    async doDropDownAction() {
        if (this.open) {
            this.closeDropdown();
            this.renderOptionsList = await new Promise(resolve => setTimeout(() => {
                resolve(false);
            }, 300));
            this.changeDetector.detectChanges();
        }
        else {
            this.addMoreOptions();
            this.renderOptionsList = true;
            this.open = await new Promise(resolve => setTimeout(() => {
                resolve(true);
            }, 100));
            this.changeDetector.detectChanges();
        }
    }
    /**
     * Closes the multi-tag select dropdown.
     */
    closeDropdown() {
        this.open = false;
        this._options = [];
        this.limit = this.quantity;
        this.hasMoreOptions = true;
        this.searchText = '';
        this.closeChange.emit(this.valueChanged);
        this.valueChanged = false;
    }
    /**
     * Handles the collapse of an item.
     *
     * @param {OptionMulti} item - The item to collapse.
     */
    handleCollapse(item) {
        item.open = !item.open;
    }
    /**
     * Sets an error message.
     *
     * @param {string} key - The translation key for the error message.
     */
    async setError(key, interpolateParams = {}) {
        if (this.useFormErrors && this.translate) {
            this.error = await this.translate.instant(key, interpolateParams);
        }
    }
    /**
     * Validates the multi-tag select input and sets the error state.
     */
    validate() {
        if (this.externalError) {
            this.error = false;
        }
        else if (this.required && !this.value?.length) {
            this.setError('forms.required');
            this.valid.emit(false);
        }
        else if (this.minTags && this.value && this.value?.length < this.minTags) {
            this.setError('forms.minimumRequired', { minTags: this.minTags });
            this.valid.emit(false);
        }
        else if (this.maxTags && this.value && this.value?.length === this.maxTags) {
            this.renderOptionsList = false;
            this.isMaxTags = true;
            this.error = '';
            this.valid.emit(true);
        }
        else {
            this.isMaxTags = false;
            this.error = '';
            this.valid.emit(true);
        }
    }
    /**
     * Change searchtext with timeout and detect detectChanges
     */
    async modelChange(event) {
        clearTimeout(this.timeoutInput);
        this.searchText = await new Promise(resolve => this.timeoutInput = setTimeout(() => {
            resolve(event);
        }, this.timeToChange)) || '';
        this.searchChange.emit(event);
        this.changeDetector.detectChanges();
    }
    /**
     * Function to add more values on _options
     */
    addMoreOptions(isOnChange = false) {
        if (this.hasMoreOptions || isOnChange) {
            this.loadingScroll = true;
            const limitState = this.limit > this.options?.length ? this.options.length : this.limit;
            this._options = this.options.slice(0, limitState);
            this.limit = this.limit + this.quantity;
            this.hasMoreOptions = limitState !== this.options.length;
            this.loadingScroll = false;
            this.changeDetector.detectChanges();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqSelectMultiTagsComponent, deps: [{ token: i0.ElementRef }, { token: i2$1.TranslateService, optional: true }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqSelectMultiTagsComponent, selector: "sq-select-multi-tags", inputs: { name: "name", value: "value", id: "id", label: "label", customClass: "customClass", placeholder: "placeholder", externalError: "externalError", externalIcon: "externalIcon", placeholderSearch: "placeholderSearch", disabled: "disabled", readonly: "readonly", required: "required", loading: "loading", useFormErrors: "useFormErrors", errorSpan: "errorSpan", backgroundColor: "backgroundColor", borderColor: "borderColor", labelColor: "labelColor", options: "options", maxTags: "maxTags", minTags: "minTags", showInside: "showInside", hideSearch: "hideSearch", tooltipMessage: "tooltipMessage", tooltipPlacement: "tooltipPlacement", tooltipColor: "tooltipColor", tooltipIcon: "tooltipIcon" }, outputs: { valueChange: "valueChange", searchChange: "searchChange", closeChange: "closeChange", removeTag: "removeTag", valid: "valid" }, providers: [], usesOnChanges: true, ngImport: i0, template: "<div class=\"wrapper-all-inside-input {{ customClass }}\">\n  <label\n    class=\"display-flex\"\n    *ngIf=\"label?.length || tooltipMessage\"\n    [ngClass]=\"{\n      readonly: readonly || isMaxTags\n    }\"\n    [for]=\"id\"\n  >\n    <div *ngIf=\"label\" [ngStyle]=\"{ 'color': labelColor }\" [innerHtml]=\"label | universalSafe\"></div>\n    <sq-tooltip\n      *ngIf=\"tooltipMessage\"\n      class=\"ml-1\"\n      [message]=\"tooltipMessage\"\n      [placement]=\"tooltipPlacement\"\n      [color]=\"tooltipColor\"\n      [icon]=\"tooltipIcon\"\n    ></sq-tooltip>\n  </label>\n  <div\n    class=\"wrapper-select-multi\"\n    [ngClass]=\"{\n      error: (externalError && externalError !== '') || (error && error !== ''),\n      disabled: disabled,\n      readonly: readonly || isMaxTags,\n      loading: loading\n    }\"\n  >\n    <div\n      [class]=\"'input-fake col  border-' + borderColor\"\n      style=\"min-height: auto\"\n      [ngStyle]=\"{ 'border-color': borderColor }\"\n      [ngClass]=\"{\n        'no-label': !(label && label.length > 0),\n        'has-icon': error || externalError,\n        disabled: disabled,\n        readonly: readonly || isMaxTags\n      }\"\n      [ngStyle]=\"{\n        'background-color': backgroundColor,\n        'border-color': borderColor\n      }\"\n      [clickOutsideEnabled]=\"open\"\n      (clickOutside)=\"closeDropdown()\"\n    >\n      <div\n        class=\"input-fake-content\"\n        [ngClass]=\"{\n          disabled: disabled\n        }\"\n        (click)=\"doDropDownAction()\"\n      >\n        <div class=\"loading-wrapper\" *ngIf=\"loading\">\n          <sq-loader></sq-loader>\n        </div>\n        <span *ngIf=\"!value?.length || !showInside\">{{ placeholder }}</span>\n        <div class=\"input-fake-content-text\" *ngIf=\"showInside && value?.length\">\n          <span class=\"tag\" *ngFor=\"let opt of value; let i = index\" (click)=\"removeItem(opt, $event)\">\n            {{ opt?.label }} <i *ngIf=\"!readonly\" class=\"fas fa-times\"></i>\n          </span>\n        </div>\n        <span *ngIf=\"value?.length\" class=\"badge\">{{ value!.length }}</span>\n        <i *ngIf=\"!loading && !readonly\" class=\"icon-down fas fa-chevron-down\"></i>\n      </div>\n      <div\n        *ngIf=\"!loading && !disabled && !readonly && !isMaxTags && renderOptionsList\"\n        id=\"sq-select-multi-tags-scroll\"\n        class=\"input-window scrollbar\"\n        [ngClass]=\"{\n          open: !loading && !disabled && !isMaxTags && renderOptionsList && open\n        }\"\n      >\n        <div class=\"input-search\">\n          <div class=\"wrapper-all-inside-input\">\n            <div class=\"p-0 wrapper-input wrapper-input-squid text-ellipsisarea\">\n              <input\n                [name]=\"name\"\n                [id]=\"id\"\n                [placeholder]=\"placeholderSearch || ('forms.search' | translateInternal | async) || ''\"\n                class=\"col input\"\n                [ngModel]=\"searchText\"\n                (ngModelChange)=\"modelChange($event)\"\n                *ngIf=\"!hideSearch\"\n              />\n            </div>\n            <span class=\"icon icon-external textarea-icon\"><i class='fas fa-search'></i></span>\n          </div>\n        </div>\n        <sq-infinity-scroll\n          class=\"list\"\n          elementToScrollId=\"sq-select-multi-tags-scroll\"\n          loaderColor=\"var(--pink)\"\n          [length]=\"_options.length || 0\"\n          [hasMore]=\"hasMoreOptions\"\n          [loading]=\"loadingScroll\"\n          (scrolledEmitter)=\"addMoreOptions()\"\n        >\n          <ng-container *ngFor=\"let opt of _options | searchFromAlternativeArray:searchText:options; let i = index; trackBy: trackByOptValue\">\n            <ng-template *ngTemplateOutlet=\"option; context: { opt: opt, i: i }\"></ng-template>\n          </ng-container>\n        </sq-infinity-scroll>\n        <p *ngIf=\"!_options?.length\">\n          {{ 'forms.searchSelectEmpty' | translateInternal | async }}\n        </p>\n      </div>\n    </div>\n  </div>\n  <div\n    class=\"box-validation box-invalid show\"\n    *ngIf=\"errorSpan\"\n    [ngClass]=\"{\n      'visibility-hidden-force': ((!externalError || externalError === '') && (!error || error === '')) || disabled || readonly\n    }\"\n  >\n    <i [ngClass]=\"{\n      'visibility-hidden-force': !error && !externalError\n    }\" class=\"fa-solid fa-triangle-exclamation\"></i>\n    {{ externalError ? externalError : '' }}\n    {{ error && !externalError ? error : '' }}\n  </div>\n  <ng-template #option let-opt=\"opt\" let-i=\"i\">\n    <li>\n      <i\n        class=\"icon-collapse fas fa-chevron-down\"\n        [ngClass]=\"{ 'fa-rotate-by': !opt.open }\"\n        [ngStyle]=\"{ color: !opt?.children?.length || opt?.disabled ? 'transparent' : '' }\"\n        (click)=\"handleCollapse(opt)\"\n        style=\"--fa-rotate-angle: -90deg\"\n        *ngIf=\"verifyIfOptionsHasChildren(options)\"\n      ></i>\n      <sq-selector\n        [id]=\"(id || name) + '-checkbox-' + opt?.value + '-' + i\"\n        [name]=\"name + '-checkbox'\"\n        [value]=\"opt?.value\"\n        [disabled]=\"opt?.disabled\"\n        [checked]=\"findItemInValue(opt, value)\"\n        [indeterminate]=\"verifyIfHasChildrenInValue(opt, value)\"\n        (valueChange)=\"emit(opt, $event.checked)\"\n      ></sq-selector>\n      <span class=\"text m-0 display-inline-block\" [ngClass]=\"{ 'cursor-pointer': opt?.children?.length }\" (click)=\"handleCollapse(opt)\">{{\n        opt?.label\n      }}</span>\n      <ul class=\"children\" *ngIf=\"opt?.children?.length\" [ngClass]=\"{open: !opt?.disabled && opt?.open}\">\n        <ng-container *ngFor=\"let child of opt?.children | searchValidValues:searchText; let j = index; trackBy: trackByOptValue\">\n          <ng-template *ngTemplateOutlet=\"option; context: { opt: child, i: j }\"></ng-template>\n        </ng-container>\n      </ul>\n    </li>\n  </ng-template>\n</div>\n", styles: [".wrapper-select-multi{margin:0;padding:0;text-align:left;display:flex;flex-direction:column;position:relative}.wrapper-select-multi.loading{cursor:wait}.wrapper-select-multi .input-fake-content-text{display:flex;justify-content:flex-start;align-items:center;flex-wrap:wrap;gap:5px;max-width:calc(100% - 40px)}.wrapper-select-multi .input-fake-content-text .tag{margin:0;border:1px solid var(--border_color);border-radius:5px;padding:.2rem .35rem;display:flex;align-items:center;gap:5px;cursor:pointer;transition:var(--transition);background:var(--background_secondary)}.wrapper-select-multi .input-fake-content-text .tag:hover{background:var(--border_color)}.wrapper-select-multi .input-fake-content{min-height:44px;border:1px solid var(--border_color);background:var(--background);padding:.75rem 1rem;transition:var(--transition);color:var(--text_color);border-radius:5px}.wrapper-select-multi .input-fake-content .icon-down{right:5px;bottom:calc(50% - 6px);position:absolute;font-size:.86rem}.wrapper-select-multi .loading-wrapper{position:absolute;right:5px;bottom:10px}.wrapper-select-multi .badge{position:absolute;bottom:15px;right:20px;margin:0;background-color:var(--border_color);color:var(--black);font-size:.86rem;font-weight:700;padding:0 .35rem;border-radius:50%;min-width:20px;height:20px;line-height:20px;text-align:center;transition:var(--transition)}.wrapper-select-multi .input-search{display:inline-block;position:sticky;top:0;z-index:1;width:100%;padding-top:1.1rem;margin-bottom:.7rem;background-color:var(--background_secondary)}.wrapper-select-multi .input-search .wrapper-all-inside-input .icon{margin:0;font-size:1rem;font-weight:700;position:absolute;right:11px;top:27px}.wrapper-select-multi .input-search .wrapper-all-inside-input .icon-external{color:inherit!important}.wrapper-select-multi .input-window{width:100%;position:absolute;top:100%;max-height:0;height:0;transition:var(--transition);overflow:hidden;z-index:1}.wrapper-select-multi .input-window.open{height:auto;max-height:400px;background:var(--background_secondary);padding:0 .7rem 1.1rem;overflow-y:auto;box-shadow:var(--box_shadow)}.wrapper-select-multi .icon-collapse{transition:var(--transition);cursor:pointer;padding:.35rem;position:relative}.wrapper-select-multi .list,.wrapper-select-multi .children{padding:0;list-style:none;margin:0;width:100%}.wrapper-select-multi .list li,.wrapper-select-multi .children li{margin:0;min-height:30px;flex-flow:row wrap;display:flex;align-items:center;justify-content:flex-start;transition:var(--transition)}.wrapper-select-multi .children{max-height:0;overflow:hidden;transition:var(--transition);padding-left:1.4rem}.wrapper-select-multi .children.open{max-height:9999px}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i4.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i4.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: SqLoaderComponent, selector: "sq-loader", inputs: ["size", "customSize", "borderSize", "color", "customClass"] }, { kind: "component", type: SqTooltipComponent, selector: "sq-tooltip", inputs: ["color", "icon", "placement", "message", "tooltipClass", "textAlign", "fontSize", "trigger"] }, { kind: "component", type: SqInfinityComponent, selector: "sq-infinity-scroll", inputs: ["length", "endMessage", "hasMore", "loading", "loaderColor", "elementToScrollId"], outputs: ["scrolledEmitter"] }, { kind: "component", type: SqSelectorComponent, selector: "sq-selector", inputs: ["name", "type", "id", "value", "checked", "indeterminate", "disabled", "readonly", "required", "colorText", "colorBackground", "hideInput", "toggle", "externalError", "useFormErrors", "label", "errorSpan", "block"], outputs: ["valueChange", "valid"] }, { kind: "directive", type: SqClickOutsideDirective, selector: "[clickOutside]", inputs: ["clickOutsideEnabled"], outputs: ["clickOutside"] }, { kind: "pipe", type: i2.AsyncPipe, name: "async" }, { kind: "pipe", type: UniversalSafePipe, name: "universalSafe" }, { kind: "pipe", type: SearchFromAlternativeArrayPipe, name: "searchFromAlternativeArray" }, { kind: "pipe", type: SearchValidValuesPipe, name: "searchValidValues" }, { kind: "pipe", type: TranslateInternalPipe, name: "translateInternal" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqSelectMultiTagsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-select-multi-tags', changeDetection: ChangeDetectionStrategy.OnPush, providers: [], template: "<div class=\"wrapper-all-inside-input {{ customClass }}\">\n  <label\n    class=\"display-flex\"\n    *ngIf=\"label?.length || tooltipMessage\"\n    [ngClass]=\"{\n      readonly: readonly || isMaxTags\n    }\"\n    [for]=\"id\"\n  >\n    <div *ngIf=\"label\" [ngStyle]=\"{ 'color': labelColor }\" [innerHtml]=\"label | universalSafe\"></div>\n    <sq-tooltip\n      *ngIf=\"tooltipMessage\"\n      class=\"ml-1\"\n      [message]=\"tooltipMessage\"\n      [placement]=\"tooltipPlacement\"\n      [color]=\"tooltipColor\"\n      [icon]=\"tooltipIcon\"\n    ></sq-tooltip>\n  </label>\n  <div\n    class=\"wrapper-select-multi\"\n    [ngClass]=\"{\n      error: (externalError && externalError !== '') || (error && error !== ''),\n      disabled: disabled,\n      readonly: readonly || isMaxTags,\n      loading: loading\n    }\"\n  >\n    <div\n      [class]=\"'input-fake col  border-' + borderColor\"\n      style=\"min-height: auto\"\n      [ngStyle]=\"{ 'border-color': borderColor }\"\n      [ngClass]=\"{\n        'no-label': !(label && label.length > 0),\n        'has-icon': error || externalError,\n        disabled: disabled,\n        readonly: readonly || isMaxTags\n      }\"\n      [ngStyle]=\"{\n        'background-color': backgroundColor,\n        'border-color': borderColor\n      }\"\n      [clickOutsideEnabled]=\"open\"\n      (clickOutside)=\"closeDropdown()\"\n    >\n      <div\n        class=\"input-fake-content\"\n        [ngClass]=\"{\n          disabled: disabled\n        }\"\n        (click)=\"doDropDownAction()\"\n      >\n        <div class=\"loading-wrapper\" *ngIf=\"loading\">\n          <sq-loader></sq-loader>\n        </div>\n        <span *ngIf=\"!value?.length || !showInside\">{{ placeholder }}</span>\n        <div class=\"input-fake-content-text\" *ngIf=\"showInside && value?.length\">\n          <span class=\"tag\" *ngFor=\"let opt of value; let i = index\" (click)=\"removeItem(opt, $event)\">\n            {{ opt?.label }} <i *ngIf=\"!readonly\" class=\"fas fa-times\"></i>\n          </span>\n        </div>\n        <span *ngIf=\"value?.length\" class=\"badge\">{{ value!.length }}</span>\n        <i *ngIf=\"!loading && !readonly\" class=\"icon-down fas fa-chevron-down\"></i>\n      </div>\n      <div\n        *ngIf=\"!loading && !disabled && !readonly && !isMaxTags && renderOptionsList\"\n        id=\"sq-select-multi-tags-scroll\"\n        class=\"input-window scrollbar\"\n        [ngClass]=\"{\n          open: !loading && !disabled && !isMaxTags && renderOptionsList && open\n        }\"\n      >\n        <div class=\"input-search\">\n          <div class=\"wrapper-all-inside-input\">\n            <div class=\"p-0 wrapper-input wrapper-input-squid text-ellipsisarea\">\n              <input\n                [name]=\"name\"\n                [id]=\"id\"\n                [placeholder]=\"placeholderSearch || ('forms.search' | translateInternal | async) || ''\"\n                class=\"col input\"\n                [ngModel]=\"searchText\"\n                (ngModelChange)=\"modelChange($event)\"\n                *ngIf=\"!hideSearch\"\n              />\n            </div>\n            <span class=\"icon icon-external textarea-icon\"><i class='fas fa-search'></i></span>\n          </div>\n        </div>\n        <sq-infinity-scroll\n          class=\"list\"\n          elementToScrollId=\"sq-select-multi-tags-scroll\"\n          loaderColor=\"var(--pink)\"\n          [length]=\"_options.length || 0\"\n          [hasMore]=\"hasMoreOptions\"\n          [loading]=\"loadingScroll\"\n          (scrolledEmitter)=\"addMoreOptions()\"\n        >\n          <ng-container *ngFor=\"let opt of _options | searchFromAlternativeArray:searchText:options; let i = index; trackBy: trackByOptValue\">\n            <ng-template *ngTemplateOutlet=\"option; context: { opt: opt, i: i }\"></ng-template>\n          </ng-container>\n        </sq-infinity-scroll>\n        <p *ngIf=\"!_options?.length\">\n          {{ 'forms.searchSelectEmpty' | translateInternal | async }}\n        </p>\n      </div>\n    </div>\n  </div>\n  <div\n    class=\"box-validation box-invalid show\"\n    *ngIf=\"errorSpan\"\n    [ngClass]=\"{\n      'visibility-hidden-force': ((!externalError || externalError === '') && (!error || error === '')) || disabled || readonly\n    }\"\n  >\n    <i [ngClass]=\"{\n      'visibility-hidden-force': !error && !externalError\n    }\" class=\"fa-solid fa-triangle-exclamation\"></i>\n    {{ externalError ? externalError : '' }}\n    {{ error && !externalError ? error : '' }}\n  </div>\n  <ng-template #option let-opt=\"opt\" let-i=\"i\">\n    <li>\n      <i\n        class=\"icon-collapse fas fa-chevron-down\"\n        [ngClass]=\"{ 'fa-rotate-by': !opt.open }\"\n        [ngStyle]=\"{ color: !opt?.children?.length || opt?.disabled ? 'transparent' : '' }\"\n        (click)=\"handleCollapse(opt)\"\n        style=\"--fa-rotate-angle: -90deg\"\n        *ngIf=\"verifyIfOptionsHasChildren(options)\"\n      ></i>\n      <sq-selector\n        [id]=\"(id || name) + '-checkbox-' + opt?.value + '-' + i\"\n        [name]=\"name + '-checkbox'\"\n        [value]=\"opt?.value\"\n        [disabled]=\"opt?.disabled\"\n        [checked]=\"findItemInValue(opt, value)\"\n        [indeterminate]=\"verifyIfHasChildrenInValue(opt, value)\"\n        (valueChange)=\"emit(opt, $event.checked)\"\n      ></sq-selector>\n      <span class=\"text m-0 display-inline-block\" [ngClass]=\"{ 'cursor-pointer': opt?.children?.length }\" (click)=\"handleCollapse(opt)\">{{\n        opt?.label\n      }}</span>\n      <ul class=\"children\" *ngIf=\"opt?.children?.length\" [ngClass]=\"{open: !opt?.disabled && opt?.open}\">\n        <ng-container *ngFor=\"let child of opt?.children | searchValidValues:searchText; let j = index; trackBy: trackByOptValue\">\n          <ng-template *ngTemplateOutlet=\"option; context: { opt: child, i: j }\"></ng-template>\n        </ng-container>\n      </ul>\n    </li>\n  </ng-template>\n</div>\n", styles: [".wrapper-select-multi{margin:0;padding:0;text-align:left;display:flex;flex-direction:column;position:relative}.wrapper-select-multi.loading{cursor:wait}.wrapper-select-multi .input-fake-content-text{display:flex;justify-content:flex-start;align-items:center;flex-wrap:wrap;gap:5px;max-width:calc(100% - 40px)}.wrapper-select-multi .input-fake-content-text .tag{margin:0;border:1px solid var(--border_color);border-radius:5px;padding:.2rem .35rem;display:flex;align-items:center;gap:5px;cursor:pointer;transition:var(--transition);background:var(--background_secondary)}.wrapper-select-multi .input-fake-content-text .tag:hover{background:var(--border_color)}.wrapper-select-multi .input-fake-content{min-height:44px;border:1px solid var(--border_color);background:var(--background);padding:.75rem 1rem;transition:var(--transition);color:var(--text_color);border-radius:5px}.wrapper-select-multi .input-fake-content .icon-down{right:5px;bottom:calc(50% - 6px);position:absolute;font-size:.86rem}.wrapper-select-multi .loading-wrapper{position:absolute;right:5px;bottom:10px}.wrapper-select-multi .badge{position:absolute;bottom:15px;right:20px;margin:0;background-color:var(--border_color);color:var(--black);font-size:.86rem;font-weight:700;padding:0 .35rem;border-radius:50%;min-width:20px;height:20px;line-height:20px;text-align:center;transition:var(--transition)}.wrapper-select-multi .input-search{display:inline-block;position:sticky;top:0;z-index:1;width:100%;padding-top:1.1rem;margin-bottom:.7rem;background-color:var(--background_secondary)}.wrapper-select-multi .input-search .wrapper-all-inside-input .icon{margin:0;font-size:1rem;font-weight:700;position:absolute;right:11px;top:27px}.wrapper-select-multi .input-search .wrapper-all-inside-input .icon-external{color:inherit!important}.wrapper-select-multi .input-window{width:100%;position:absolute;top:100%;max-height:0;height:0;transition:var(--transition);overflow:hidden;z-index:1}.wrapper-select-multi .input-window.open{height:auto;max-height:400px;background:var(--background_secondary);padding:0 .7rem 1.1rem;overflow-y:auto;box-shadow:var(--box_shadow)}.wrapper-select-multi .icon-collapse{transition:var(--transition);cursor:pointer;padding:.35rem;position:relative}.wrapper-select-multi .list,.wrapper-select-multi .children{padding:0;list-style:none;margin:0;width:100%}.wrapper-select-multi .list li,.wrapper-select-multi .children li{margin:0;min-height:30px;flex-flow:row wrap;display:flex;align-items:center;justify-content:flex-start;transition:var(--transition)}.wrapper-select-multi .children{max-height:0;overflow:hidden;transition:var(--transition);padding-left:1.4rem}.wrapper-select-multi .children.open{max-height:9999px}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i2$1.TranslateService, decorators: [{
                    type: Optional
                }] }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { name: [{
                type: Input
            }], value: [{
                type: Input
            }], id: [{
                type: Input
            }], label: [{
                type: Input
            }], customClass: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], externalError: [{
                type: Input
            }], externalIcon: [{
                type: Input
            }], placeholderSearch: [{
                type: Input
            }], disabled: [{
                type: Input
            }], readonly: [{
                type: Input
            }], required: [{
                type: Input
            }], loading: [{
                type: Input
            }], useFormErrors: [{
                type: Input
            }], errorSpan: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }], borderColor: [{
                type: Input
            }], labelColor: [{
                type: Input
            }], options: [{
                type: Input
            }], maxTags: [{
                type: Input
            }], minTags: [{
                type: Input
            }], showInside: [{
                type: Input
            }], hideSearch: [{
                type: Input
            }], tooltipMessage: [{
                type: Input
            }], tooltipPlacement: [{
                type: Input
            }], tooltipColor: [{
                type: Input
            }], tooltipIcon: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], searchChange: [{
                type: Output
            }], closeChange: [{
                type: Output
            }], removeTag: [{
                type: Output
            }], valid: [{
                type: Output
            }] } });

/**
 * Represents an input field with mask functionality that extends SqInputComponent.
 *
 * This component uses ngx-mask library
 * @see {@link https://github.com/JsDaddy/ngx-mask}
 *
 * This component extends the {@link SqInputComponent} and adds additional properties and behavior for handling money input.
 *
 * @example
 * <sq-input-mask [(value)]='phone' [name]="'masked-input'" [id]="'masked-input'" [label]="'Masked Input'" [mask]="'(999) 999-9999'"></sq-input-mask>
 */
class SqInputMaskComponent extends SqInputComponent {
    /**
     * Constructs a new instance of SqInputMaskComponent.
     * @param validatorHelper - The ValidatorHelper service for input validation.
     * @param element - Reference to the native element.
     * @param translate - The TranslateService for translation support (optional).
     */
    constructor(validatorHelper, element, translate) {
        super(validatorHelper, element, translate);
        this.validatorHelper = validatorHelper;
        this.translate = translate;
        /**
         * The mask pattern for input validation and formatting.
         */
        this.mask = '';
        /**
         * The character used as a thousand separator in numeric input.
         */
        this.thousandSeparator = '';
        /**
         * The suffix to be appended to the input value.
         */
        this.suffix = '';
        /**
         * The prefix to be prepended to the input value.
         */
        this.prefix = '';
        /**
         * Indicates whether the mask should be visible while typing.
         */
        this.showMaskTyped = false;
        /**
         * Indicates whether negative numbers are allowed.
         */
        this.allowNegativeNumbers = false;
        /**
         * The decimal marker character or an array of characters to represent decimal values.
         */
        this.decimalMarker = [".", ","];
        /**
         * The character to use as a placeholder in empty mask slots.
         */
        this.placeHolderCharacter = '';
        /**
         * Indicates whether leading zeros should be preserved.
         */
        this.leadZero = false;
        this.nativeElement = element.nativeElement;
    }
    /**
     * Asynchronously validate the date input value.
     * @param isBlur - Indicates if the input has lost focus.
     */
    async validate(isBlur = false) {
        const numericValue = Number(this.value);
        if (this.externalError) {
            this.error = false;
        }
        else if (this.required && (this.value === null || this.value === undefined || this.value === '')) {
            this.valid.emit(false);
            this.setError('forms.required');
        }
        else if (this.maxValue && numericValue > this.maxValue) {
            this.valid.emit(false);
            this.setError('forms.maxValueAllowed', { max: this.maxValue });
        }
        else if (this.minValue && numericValue < this.minValue) {
            this.valid.emit(false);
            this.setError('forms.minValueAllowed', { min: this.minValue });
        }
        else {
            this.valid.emit(true);
            this.error = '';
        }
        if (isBlur) {
            this.inFocus.emit(false);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqInputMaskComponent, deps: [{ token: ValidatorHelper }, { token: i0.ElementRef }, { token: i2$1.TranslateService, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqInputMaskComponent, selector: "sq-input-mask", inputs: { mask: "mask", thousandSeparator: "thousandSeparator", suffix: "suffix", prefix: "prefix", showMaskTyped: "showMaskTyped", allowNegativeNumbers: "allowNegativeNumbers", decimalMarker: "decimalMarker", placeHolderCharacter: "placeHolderCharacter", leadZero: "leadZero", minValue: "minValue", maxValue: "maxValue" }, usesInheritance: true, ngImport: i0, template: "<div class=\"wrapper-all-inside-input {{ customClass }}\">\n  <label\n    class=\"display-flex\"\n    *ngIf=\"label?.length || tooltipMessage\"\n    [ngClass]=\"{\n      readonly: readonly\n    }\"\n    [for]=\"id\"\n  >\n    <div *ngIf=\"label\" [ngStyle]=\"{ 'color': labelColor }\" [innerHtml]=\"label | universalSafe\"></div>\n    <sq-tooltip\n      *ngIf=\"tooltipMessage\"\n      class=\"ml-1\"\n      [message]=\"tooltipMessage\"\n      [placement]=\"tooltipPlacement\"\n      [color]=\"tooltipColor\"\n      [icon]=\"tooltipIcon\"\n    ></sq-tooltip>\n  </label>\n  <div\n    class=\"p-0 wrapper-input wrapper-input-squid text-ellipsisarea\"\n    [ngClass]=\"{\n      error: (externalError && externalError !== '') || (error && error !== ''),\n      readonly: readonly\n    }\"\n  >\n    <span class=\"input-group-text m-0\" *ngIf=\"leftLabel\">\n      <ng-container *ngTemplateOutlet=\"leftLabel\"></ng-container>\n    </span>\n    <input\n      class=\"col input\"\n      [ngClass]=\"{\n        'has-icon': error || externalError,\n        disabled: disabled,\n        readonly: readonly\n      }\"\n      [ngStyle]=\"{\n        'background-color': backgroundColor,\n        'border-color': borderColor\n      }\"\n      [id]=\"id\"\n      [type]=\"'text'\"\n      [name]=\"name\"\n      [placeholder]=\"placeholder || ''\"\n      [required]=\"required\"\n      [disabled]=\"disabled\"\n      [readonly]=\"readonly\"\n      (blur)=\"validate(true)\"\n      [ngModel]=\"_value\"\n      [maxlength]=\"maxLength\"\n      (ngModelChange)=\"change($event)\"\n      (keydown)=\"keyDown($event)\"\n      (keyup)=\"keyUp($event)\"\n      [pattern]=\"pattern\"\n      [attr.inputmode]=\"inputMode\"\n      [mask]=\"mask\"\n      [showMaskTyped]=\"showMaskTyped\"\n      [thousandSeparator]=\"thousandSeparator\"\n      [suffix]=\"suffix\"\n      [prefix]=\"prefix\"\n      [allowNegativeNumbers]=\"allowNegativeNumbers\"\n      [decimalMarker]=\"decimalMarker\"\n      [placeHolderCharacter]=\"placeHolderCharacter\"\n      [leadZero]=\"leadZero\"\n      ngDefaultControl\n    />\n    <span class=\"input-group-text m-0\" *ngIf=\"rightLabel\">\n      <ng-container *ngTemplateOutlet=\"rightLabel\"></ng-container>\n    </span>\n  </div>\n  <span\n    *ngIf=\"externalIcon\"\n    class=\"icon icon-external textarea-icon\"\n    [ngClass]=\"{\n      'no-label': !label?.length\n    }\"\n    [innerHtml]=\"externalIcon || '' | universalSafe\"\n  ></span>\n  <div\n    class=\"box-validation box-invalid show\"\n    *ngIf=\"errorSpan\"\n    [ngClass]=\"{\n      'has-max-length': maxLength\n    }\"\n  >\n    <i [ngClass]=\"{\n      'visibility-hidden-force': !error && !externalError\n    }\" class=\"fa-solid fa-triangle-exclamation\"></i>\n    {{ externalError ? externalError : '' }}\n    {{ error && !externalError ? error : '' }}\n    <span\n      class=\"max-length-name\"\n      [ngClass]=\"{\n        'visibility-hidden-force': disabled || readonly\n      }\"\n      *ngIf=\"maxLength\"\n    >\n      {{ maxLength - (value?.length || 0) }}\n    </span>\n  </div>\n</div>\n", styles: [".wrapper-all-inside-input .icon{margin:0;font-size:1rem;font-weight:700;position:absolute;right:24px;top:40px}.wrapper-all-inside-input .icon.no-label{top:75px}.wrapper-all-inside-input .icon-external{color:inherit!important}.wrapper-all-inside-input .max-length-name{font-size:inherit;float:right}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i4.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i4.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i4.MaxLengthValidator, selector: "[maxlength][formControlName],[maxlength][formControl],[maxlength][ngModel]", inputs: ["maxlength"] }, { kind: "directive", type: i4.PatternValidator, selector: "[pattern][formControlName],[pattern][formControl],[pattern][ngModel]", inputs: ["pattern"] }, { kind: "directive", type: i4.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: i5.NgxMaskDirective, selector: "input[mask], textarea[mask]", inputs: ["mask", "specialCharacters", "patterns", "prefix", "suffix", "thousandSeparator", "decimalMarker", "dropSpecialCharacters", "hiddenInput", "showMaskTyped", "placeHolderCharacter", "shownMaskExpression", "showTemplate", "clearIfNotMatch", "validation", "separatorLimit", "allowNegativeNumbers", "leadZeroDateTime", "leadZero", "triggerOnMaskChange", "apm", "inputTransformFn", "outputTransformFn", "keepCharacterPositions"], outputs: ["maskFilled"], exportAs: ["mask", "ngxMask"] }, { kind: "component", type: SqTooltipComponent, selector: "sq-tooltip", inputs: ["color", "icon", "placement", "message", "tooltipClass", "textAlign", "fontSize", "trigger"] }, { kind: "pipe", type: UniversalSafePipe, name: "universalSafe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqInputMaskComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-input-mask', template: "<div class=\"wrapper-all-inside-input {{ customClass }}\">\n  <label\n    class=\"display-flex\"\n    *ngIf=\"label?.length || tooltipMessage\"\n    [ngClass]=\"{\n      readonly: readonly\n    }\"\n    [for]=\"id\"\n  >\n    <div *ngIf=\"label\" [ngStyle]=\"{ 'color': labelColor }\" [innerHtml]=\"label | universalSafe\"></div>\n    <sq-tooltip\n      *ngIf=\"tooltipMessage\"\n      class=\"ml-1\"\n      [message]=\"tooltipMessage\"\n      [placement]=\"tooltipPlacement\"\n      [color]=\"tooltipColor\"\n      [icon]=\"tooltipIcon\"\n    ></sq-tooltip>\n  </label>\n  <div\n    class=\"p-0 wrapper-input wrapper-input-squid text-ellipsisarea\"\n    [ngClass]=\"{\n      error: (externalError && externalError !== '') || (error && error !== ''),\n      readonly: readonly\n    }\"\n  >\n    <span class=\"input-group-text m-0\" *ngIf=\"leftLabel\">\n      <ng-container *ngTemplateOutlet=\"leftLabel\"></ng-container>\n    </span>\n    <input\n      class=\"col input\"\n      [ngClass]=\"{\n        'has-icon': error || externalError,\n        disabled: disabled,\n        readonly: readonly\n      }\"\n      [ngStyle]=\"{\n        'background-color': backgroundColor,\n        'border-color': borderColor\n      }\"\n      [id]=\"id\"\n      [type]=\"'text'\"\n      [name]=\"name\"\n      [placeholder]=\"placeholder || ''\"\n      [required]=\"required\"\n      [disabled]=\"disabled\"\n      [readonly]=\"readonly\"\n      (blur)=\"validate(true)\"\n      [ngModel]=\"_value\"\n      [maxlength]=\"maxLength\"\n      (ngModelChange)=\"change($event)\"\n      (keydown)=\"keyDown($event)\"\n      (keyup)=\"keyUp($event)\"\n      [pattern]=\"pattern\"\n      [attr.inputmode]=\"inputMode\"\n      [mask]=\"mask\"\n      [showMaskTyped]=\"showMaskTyped\"\n      [thousandSeparator]=\"thousandSeparator\"\n      [suffix]=\"suffix\"\n      [prefix]=\"prefix\"\n      [allowNegativeNumbers]=\"allowNegativeNumbers\"\n      [decimalMarker]=\"decimalMarker\"\n      [placeHolderCharacter]=\"placeHolderCharacter\"\n      [leadZero]=\"leadZero\"\n      ngDefaultControl\n    />\n    <span class=\"input-group-text m-0\" *ngIf=\"rightLabel\">\n      <ng-container *ngTemplateOutlet=\"rightLabel\"></ng-container>\n    </span>\n  </div>\n  <span\n    *ngIf=\"externalIcon\"\n    class=\"icon icon-external textarea-icon\"\n    [ngClass]=\"{\n      'no-label': !label?.length\n    }\"\n    [innerHtml]=\"externalIcon || '' | universalSafe\"\n  ></span>\n  <div\n    class=\"box-validation box-invalid show\"\n    *ngIf=\"errorSpan\"\n    [ngClass]=\"{\n      'has-max-length': maxLength\n    }\"\n  >\n    <i [ngClass]=\"{\n      'visibility-hidden-force': !error && !externalError\n    }\" class=\"fa-solid fa-triangle-exclamation\"></i>\n    {{ externalError ? externalError : '' }}\n    {{ error && !externalError ? error : '' }}\n    <span\n      class=\"max-length-name\"\n      [ngClass]=\"{\n        'visibility-hidden-force': disabled || readonly\n      }\"\n      *ngIf=\"maxLength\"\n    >\n      {{ maxLength - (value?.length || 0) }}\n    </span>\n  </div>\n</div>\n", styles: [".wrapper-all-inside-input .icon{margin:0;font-size:1rem;font-weight:700;position:absolute;right:24px;top:40px}.wrapper-all-inside-input .icon.no-label{top:75px}.wrapper-all-inside-input .icon-external{color:inherit!important}.wrapper-all-inside-input .max-length-name{font-size:inherit;float:right}\n"] }]
        }], ctorParameters: function () { return [{ type: ValidatorHelper }, { type: i0.ElementRef }, { type: i2$1.TranslateService, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { mask: [{
                type: Input
            }], thousandSeparator: [{
                type: Input
            }], suffix: [{
                type: Input
            }], prefix: [{
                type: Input
            }], showMaskTyped: [{
                type: Input
            }], allowNegativeNumbers: [{
                type: Input
            }], decimalMarker: [{
                type: Input
            }], placeHolderCharacter: [{
                type: Input
            }], leadZero: [{
                type: Input
            }], minValue: [{
                type: Input
            }], maxValue: [{
                type: Input
            }] } });

/**
 * Represents an input component for handling money values.
 *
 * @implements {OnChanges}
 *
 * This component uses ngx-mask library
 * @see {@link https://github.com/JsDaddy/ngx-mask}
 *
 * This component extends the {@link SqInputComponent} and adds additional properties and behavior for handling money input.
 *
 * @example
 * <sq-input-money [(value)]='payment' [name]="'money-input'" [id]="'money-input'" [label]="'Money Input'" currency='USD'></sq-input-money>
 */
class SqInputMoneyComponent extends SqInputComponent {
    /**
     * The value of the input element.
     */
    set value(value) {
        if (typeof value === 'number') {
            this._value = value.toString();
        }
        else {
            this._value = value || '';
        }
    }
    get value() {
        return parseFloat(this._value);
    }
    /**
     * Constructs a new instance of SqInputMaskComponent.
     * @param validatorHelper - The ValidatorHelper service for input validation.
     * @param element - Reference to the native element.
     * @param translate - The TranslateService for translation support (optional).
     */
    constructor(validatorHelper, element, translate) {
        super(validatorHelper, element, translate);
        this.validatorHelper = validatorHelper;
        this.translate = translate;
        /**
         * The character used for thousand separators (e.g., ',' or '.').
         */
        this.thousandSeparator = '.';
        /**
         * Whether to display the input mask as the user types.
         */
        this.showMaskTyped = false;
        /**
         * Whether to allow negative numbers.
         */
        this.allowNegativeNumbers = false;
        /**
         * The character used as a placeholder for empty positions.
         */
        this.placeHolderCharacter = '';
        /**
         * The character used as a decimal marker (e.g., ',' or '.').
         */
        this.decimalMarker = ",";
        /**
         * The currency symbol (e.g., 'USD', 'EUR', 'BRL').
         */
        this.currency = 'BRL';
        /**
         * Event emitter for changes in the money value.
         */
        this.valueChange = new EventEmitter();
        /**
         * The currency prefix based on the current currency.
         */
        this.prefix = this.getCurrencyPrefix();
        /**
         * Content child template for the right label override.
         */
        this.rightLabelOverwrite = null;
        this.nativeElement = element.nativeElement;
    }
    /**
     * Lifecycle hook that is called when input property values change.
     * @param changes - An object containing changed properties and their previous and current values.
     */
    ngOnChanges(changes) {
        if (changes["currency"] && changes["currency"].currentValue !== changes["currency"].previousValue && changes["currency"].currentValue) {
            this.prefix = this.getCurrencyPrefix();
        }
    }
    /**
     * Get the currency prefix based on the current currency.
     * @returns The currency prefix as a string.
     */
    getCurrencyPrefix() {
        return Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency, }).format(0).replace(/\d/g, '').replace('.', '').replace(',', '');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqInputMoneyComponent, deps: [{ token: ValidatorHelper }, { token: i0.ElementRef }, { token: i2$1.TranslateService, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqInputMoneyComponent, selector: "sq-input-money", inputs: { value: "value", thousandSeparator: "thousandSeparator", showMaskTyped: "showMaskTyped", allowNegativeNumbers: "allowNegativeNumbers", placeHolderCharacter: "placeHolderCharacter", decimalMarker: "decimalMarker", currency: "currency" }, outputs: { valueChange: "valueChange" }, queries: [{ propertyName: "rightLabelOverwrite", first: true, predicate: ["rightLabelOverwrite"], descendants: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<sq-input-mask\n  [value]=\"_value\"\n  [id]=\"id\"\n  [name]=\"name\"\n  mask=\"separator.2\"\n  [thousandSeparator]=\"thousandSeparator\"\n  [decimalMarker]=\"decimalMarker\"\n  [placeHolderCharacter]=\"placeHolderCharacter\"\n  [timeToChange]=\"timeToChange\"\n  [label]=\"label\"\n  [customClass]=\"customClass\"\n  [placeholder]=\"placeholder\"\n  [externalError]=\"externalError\"\n  [externalIcon]=\"externalIcon\"\n  [errorSpan]=\"errorSpan\"\n  [disabled]=\"disabled\"\n  [readonly]=\"readonly\"\n  [required]=\"required\"\n  [useFormErrors]=\"useFormErrors\"\n  [tooltipMessage]=\"tooltipMessage\"\n  [tooltipPlacement]=\"tooltipPlacement\"\n  [tooltipColor]=\"tooltipColor\"\n  [tooltipIcon]=\"tooltipIcon\"\n  [backgroundColor]=\"backgroundColor\"\n  [allowNegativeNumbers]=\"allowNegativeNumbers\"\n  [borderColor]=\"borderColor\"\n  [leadZero]=\"true\"\n  [showMaskTyped]=\"showMaskTyped\"\n  [labelColor]=\"labelColor\"\n  [borderColor]=\"borderColor\"\n  inputMode=\"numeric\"\n  pattern=\"[0-9]*\"\n  (valueChange)=\"change($event)\"\n  (valid)=\"valid.emit($event)\"\n  (inFocus)=\"inFocus.emit($event)\"\n  (keyPressUp)=\"keyPressUp.emit($event)\"\n  (keyPressDown)=\"keyPressDown.emit($event)\"\n>\n  <ng-container *ngIf=\"prefix\">\n    <ng-template #leftLabel>\n      {{ prefix }}\n    </ng-template>\n  </ng-container>\n\n  <ng-container *ngIf=\"rightLabelOverwrite\">\n    <ng-template #rightLabel>\n      <ng-container *ngTemplateOutlet=\"rightLabelOverwrite\"></ng-container>\n    </ng-template>\n  </ng-container>\n</sq-input-mask>\n", styles: [""], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: SqInputMaskComponent, selector: "sq-input-mask", inputs: ["mask", "thousandSeparator", "suffix", "prefix", "showMaskTyped", "allowNegativeNumbers", "decimalMarker", "placeHolderCharacter", "leadZero", "minValue", "maxValue"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqInputMoneyComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-input-money', template: "<sq-input-mask\n  [value]=\"_value\"\n  [id]=\"id\"\n  [name]=\"name\"\n  mask=\"separator.2\"\n  [thousandSeparator]=\"thousandSeparator\"\n  [decimalMarker]=\"decimalMarker\"\n  [placeHolderCharacter]=\"placeHolderCharacter\"\n  [timeToChange]=\"timeToChange\"\n  [label]=\"label\"\n  [customClass]=\"customClass\"\n  [placeholder]=\"placeholder\"\n  [externalError]=\"externalError\"\n  [externalIcon]=\"externalIcon\"\n  [errorSpan]=\"errorSpan\"\n  [disabled]=\"disabled\"\n  [readonly]=\"readonly\"\n  [required]=\"required\"\n  [useFormErrors]=\"useFormErrors\"\n  [tooltipMessage]=\"tooltipMessage\"\n  [tooltipPlacement]=\"tooltipPlacement\"\n  [tooltipColor]=\"tooltipColor\"\n  [tooltipIcon]=\"tooltipIcon\"\n  [backgroundColor]=\"backgroundColor\"\n  [allowNegativeNumbers]=\"allowNegativeNumbers\"\n  [borderColor]=\"borderColor\"\n  [leadZero]=\"true\"\n  [showMaskTyped]=\"showMaskTyped\"\n  [labelColor]=\"labelColor\"\n  [borderColor]=\"borderColor\"\n  inputMode=\"numeric\"\n  pattern=\"[0-9]*\"\n  (valueChange)=\"change($event)\"\n  (valid)=\"valid.emit($event)\"\n  (inFocus)=\"inFocus.emit($event)\"\n  (keyPressUp)=\"keyPressUp.emit($event)\"\n  (keyPressDown)=\"keyPressDown.emit($event)\"\n>\n  <ng-container *ngIf=\"prefix\">\n    <ng-template #leftLabel>\n      {{ prefix }}\n    </ng-template>\n  </ng-container>\n\n  <ng-container *ngIf=\"rightLabelOverwrite\">\n    <ng-template #rightLabel>\n      <ng-container *ngTemplateOutlet=\"rightLabelOverwrite\"></ng-container>\n    </ng-template>\n  </ng-container>\n</sq-input-mask>\n" }]
        }], ctorParameters: function () { return [{ type: ValidatorHelper }, { type: i0.ElementRef }, { type: i2$1.TranslateService, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { value: [{
                type: Input
            }], thousandSeparator: [{
                type: Input
            }], showMaskTyped: [{
                type: Input
            }], allowNegativeNumbers: [{
                type: Input
            }], placeHolderCharacter: [{
                type: Input
            }], decimalMarker: [{
                type: Input
            }], currency: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], rightLabelOverwrite: [{
                type: ContentChild,
                args: ['rightLabelOverwrite']
            }] } });

/**
 * Represents an input component for handling numeric values.
 *
 * This component extends the {@link SqInputComponent} and adds additional properties and behavior for handling numeric input.
 *
 * @example
 * <sq-input-number [(value)]='number' [name]="'number-input'" [id]="'number-input'"></sq-input-number>
 */
class SqInputNumberComponent extends SqInputComponent {
    /**
     * The value of the input element.
     */
    set value(value) {
        if (typeof value === 'number') {
            this._value = value.toString();
        }
        else {
            this._value = value || '';
        }
    }
    get value() {
        return parseFloat(this._value);
    }
    /**
   * Constructs a new instance of SqInputMaskComponent.
   * @param validatorHelper - The ValidatorHelper service for input validation.
   * @param element - Reference to the native element.
   * @param translate - The TranslateService for translation support (optional).
   */
    constructor(validatorHelper, element, translate) {
        super(validatorHelper, element, translate);
        this.validatorHelper = validatorHelper;
        this.translate = translate;
        /**
         * The character used for thousand separators (e.g., ',' or '.').
         */
        this.thousandSeparator = '.';
        /**
         * Whether to display the input mask as the user types.
         */
        this.showMaskTyped = false;
        /**
         * Whether to allow negative numbers.
         */
        this.allowNegativeNumbers = false;
        /**
         * Whether to add a leading zero for single-digit numbers (e.g., '01' instead of '1').
         */
        this.leadZero = false;
        /**
         * The character used as a placeholder for empty positions.
         */
        this.placeHolderCharacter = '';
        /**
         * The character used as a decimal marker (e.g., ',' or '.').
         */
        this.decimalMarker = ',';
        /**
         * Event emitter for changes in the numeric value.
         */
        this.valueChange = new EventEmitter();
        /**
         * Content child template for the left label override.
         */
        this.leftLabelOverwrite = null;
        /**
         * Content child template for the right label override.
         */
        this.rightLabelOverwrite = null;
        this.nativeElement = element.nativeElement;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqInputNumberComponent, deps: [{ token: ValidatorHelper }, { token: i0.ElementRef }, { token: i2$1.TranslateService, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.8", type: SqInputNumberComponent, selector: "sq-input-number", inputs: { value: "value", thousandSeparator: "thousandSeparator", showMaskTyped: "showMaskTyped", allowNegativeNumbers: "allowNegativeNumbers", leadZero: "leadZero", placeHolderCharacter: "placeHolderCharacter", decimalMarker: "decimalMarker", minValue: "minValue", maxValue: "maxValue" }, outputs: { valueChange: "valueChange" }, queries: [{ propertyName: "leftLabelOverwrite", first: true, predicate: ["leftLabelOverwrite"], descendants: true }, { propertyName: "rightLabelOverwrite", first: true, predicate: ["rightLabelOverwrite"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<sq-input-mask\n  [value]=\"_value\"\n  [id]=\"id\"\n  [name]=\"name\"\n  mask=\"separator\"\n  [thousandSeparator]=\"thousandSeparator\"\n  [decimalMarker]=\"decimalMarker\"\n  [placeHolderCharacter]=\"placeHolderCharacter\"\n  [timeToChange]=\"timeToChange\"\n  [label]=\"label\"\n  [customClass]=\"customClass\"\n  [placeholder]=\"placeholder\"\n  [externalError]=\"externalError\"\n  [externalIcon]=\"externalIcon\"\n  [errorSpan]=\"errorSpan\"\n  [disabled]=\"disabled\"\n  [readonly]=\"readonly\"\n  [required]=\"required\"\n  [useFormErrors]=\"useFormErrors\"\n  [tooltipMessage]=\"tooltipMessage\"\n  [tooltipPlacement]=\"tooltipPlacement\"\n  [tooltipColor]=\"tooltipColor\"\n  [tooltipIcon]=\"tooltipIcon\"\n  [backgroundColor]=\"backgroundColor\"\n  [allowNegativeNumbers]=\"allowNegativeNumbers\"\n  [borderColor]=\"borderColor\"\n  [leadZero]=\"leadZero\"\n  [showMaskTyped]=\"showMaskTyped\"\n  [labelColor]=\"labelColor\"\n  [maxValue]=\"maxValue\"\n  [minValue]=\"minValue\"\n  inputMode=\"numeric\"\n  pattern=\"[0-9]*\"\n  (valueChange)=\"change($event)\"\n  (valid)=\"valid.emit($event)\"\n  (inFocus)=\"inFocus.emit($event)\"\n  (keyPressUp)=\"keyPressUp.emit($event)\"\n  (keyPressDown)=\"keyPressDown.emit($event)\"\n>\n  <ng-container *ngIf=\"leftLabelOverwrite\">\n    <ng-template #leftLabel>\n      <ng-container *ngTemplateOutlet=\"leftLabelOverwrite\"></ng-container>\n    </ng-template>\n  </ng-container>\n  <ng-container *ngIf=\"rightLabelOverwrite\">\n    <ng-template #rightLabel>\n      <ng-container *ngTemplateOutlet=\"rightLabelOverwrite\"></ng-container>\n    </ng-template>\n  </ng-container>\n</sq-input-mask>\n", styles: [""], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: SqInputMaskComponent, selector: "sq-input-mask", inputs: ["mask", "thousandSeparator", "suffix", "prefix", "showMaskTyped", "allowNegativeNumbers", "decimalMarker", "placeHolderCharacter", "leadZero", "minValue", "maxValue"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqInputNumberComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sq-input-number', template: "<sq-input-mask\n  [value]=\"_value\"\n  [id]=\"id\"\n  [name]=\"name\"\n  mask=\"separator\"\n  [thousandSeparator]=\"thousandSeparator\"\n  [decimalMarker]=\"decimalMarker\"\n  [placeHolderCharacter]=\"placeHolderCharacter\"\n  [timeToChange]=\"timeToChange\"\n  [label]=\"label\"\n  [customClass]=\"customClass\"\n  [placeholder]=\"placeholder\"\n  [externalError]=\"externalError\"\n  [externalIcon]=\"externalIcon\"\n  [errorSpan]=\"errorSpan\"\n  [disabled]=\"disabled\"\n  [readonly]=\"readonly\"\n  [required]=\"required\"\n  [useFormErrors]=\"useFormErrors\"\n  [tooltipMessage]=\"tooltipMessage\"\n  [tooltipPlacement]=\"tooltipPlacement\"\n  [tooltipColor]=\"tooltipColor\"\n  [tooltipIcon]=\"tooltipIcon\"\n  [backgroundColor]=\"backgroundColor\"\n  [allowNegativeNumbers]=\"allowNegativeNumbers\"\n  [borderColor]=\"borderColor\"\n  [leadZero]=\"leadZero\"\n  [showMaskTyped]=\"showMaskTyped\"\n  [labelColor]=\"labelColor\"\n  [maxValue]=\"maxValue\"\n  [minValue]=\"minValue\"\n  inputMode=\"numeric\"\n  pattern=\"[0-9]*\"\n  (valueChange)=\"change($event)\"\n  (valid)=\"valid.emit($event)\"\n  (inFocus)=\"inFocus.emit($event)\"\n  (keyPressUp)=\"keyPressUp.emit($event)\"\n  (keyPressDown)=\"keyPressDown.emit($event)\"\n>\n  <ng-container *ngIf=\"leftLabelOverwrite\">\n    <ng-template #leftLabel>\n      <ng-container *ngTemplateOutlet=\"leftLabelOverwrite\"></ng-container>\n    </ng-template>\n  </ng-container>\n  <ng-container *ngIf=\"rightLabelOverwrite\">\n    <ng-template #rightLabel>\n      <ng-container *ngTemplateOutlet=\"rightLabelOverwrite\"></ng-container>\n    </ng-template>\n  </ng-container>\n</sq-input-mask>\n" }]
        }], ctorParameters: function () { return [{ type: ValidatorHelper }, { type: i0.ElementRef }, { type: i2$1.TranslateService, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { value: [{
                type: Input
            }], thousandSeparator: [{
                type: Input
            }], showMaskTyped: [{
                type: Input
            }], allowNegativeNumbers: [{
                type: Input
            }], leadZero: [{
                type: Input
            }], placeHolderCharacter: [{
                type: Input
            }], decimalMarker: [{
                type: Input
            }], minValue: [{
                type: Input
            }], maxValue: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], leftLabelOverwrite: [{
                type: ContentChild,
                args: ['leftLabelOverwrite']
            }], rightLabelOverwrite: [{
                type: ContentChild,
                args: ['rightLabelOverwrite']
            }] } });

/**
 * Angular directive for creating and controlling dropdown menus.
 *
 * This directive allows you to create and manage dropdown menus in your Angular application.
 * Dropdowns can display additional content when clicked or triggered by user actions.
 *
 * @see {@link https://css.squidit.com.br/components/dropdown|Official Dropdown Documentation}
 *
 * @example
 * <!-- Basic usage -->
 * <div
 *  [dropdown]="true"
 *  dropdownPlacement="left bottom"
 * >
 *   <!-- HTML content, this container may be a button, span, etc -->
 * </div>
 * <ul class="dropdown">
 *   <li>
 *     <!-- Common Item Like <a> or <button> or forms -->
 *   </li>
 *   <li>
 *     <!-- Custom HR -->
 *     <hr class="dropdown-divider" />
 *   </li>
 *   <li>
 *     <div class="dropdown-plain">
 *       <!-- Plain HTML -->
 *     </div>
 *   </li>
 * </ul>
 */
class SqDropdownDirective {
    /**
     * Constructs a new SqDropdownDirective.
     *
     * @param {ElementRef} el - The ElementRef of the host element.
     * @param {Renderer2} renderer - The Renderer2 for DOM manipulation.
     * @param {Router} router - The Angular Router service.
     * @param {Document} documentImported - The injected Document object for DOM manipulation.
     */
    constructor(el, renderer, router, documentImported) {
        this.el = el;
        this.renderer = renderer;
        this.router = router;
        this.documentImported = documentImported;
        /**
         * Indicates whether the dropdown menu is open or closed.
         */
        this.content = false;
        /**
         * Defines the placement of the dropdown menu in relation to the host element.
         * Possible values: 'left top', 'left bottom', 'center top', 'center bottom', 'right top', 'right bottom'.
         */
        this.dropdownPlacement = 'center bottom';
        /**
         * The delay in milliseconds before closing the dropdown after a click outside event.
         */
        this.dropdownDelay = 0;
        /**
         * Additional CSS class to be applied to the dropdown menu.
         */
        this.dropdownClass = '';
        /**
         * The width of the dropdown menu in pixels.
         */
        this.dropdownWidth = 'auto';
        /**
         * The vertical distance between the host element and the dropdown menu.
         */
        this.dropdownDistanceVertical = 3;
        /**
         * The horizontal distance between the host element and the dropdown menu.
         */
        this.dropdownDistanceHorizontal = 0;
        /**
         * Indicates whether the dropdown should close when a click occurs outside the menu.
         */
        this.closeOnClick = false;
        /**
         * Reference to the generated dropdown menu element.
         */
        this.dropdownElement = null;
        /**
         * Indicates whether the dropdown menu is open or closed. Used for internal control
         */
        this.open = false;
        // Bind the hide function to the current instance.
        this.hide = this.hide.bind(this);
        // Assign the document object for DOM manipulation.
        this.document = this.documentImported || document;
    }
    /**
     * Event listener for the 'click' event on the host element to toggle the dropdown menu.
     */
    onClick() {
        if (this.dropdownElement) {
            this.hide();
        }
        if (!this.dropdownElement) {
            this.show();
        }
    }
    /**
     * Initializes the directive, subscribing to router events to automatically close the dropdown on navigation.
     */
    ngOnInit() {
        this.router.events.subscribe((val) => {
            if (val instanceof NavigationEnd) {
                this.hide();
            }
        });
    }
    /**
     * Cleans up the directive when it is destroyed, ensuring the dropdown is closed.
     */
    ngOnDestroy() {
        this.hide();
    }
    /**
     * Opens the dropdown menu and sets its position.
     */
    show() {
        this.create();
        this.document?.addEventListener('click', this.hide, true);
        if (this.dropdownElement) {
            this.renderer.addClass(this.dropdownElement, 'open');
            this.setPosition();
            this.open = true;
        }
    }
    /**
     * Closes the dropdown menu with a delay to allow for animations, and performs cleanup.
     */
    hide() {
        if (this.dropdownElement && this.open) {
            window.setTimeout(async () => {
                this.open = false;
                this.renderer.removeClass(this.dropdownElement, 'open');
                this.renderer.removeClass(this.dropdownElement, 'dropdown-generated');
                this.renderer.removeClass(this.dropdownElement, `dropdown-${this.dropdownPlacement.replace(' ', '-')}`);
                this.renderer.removeAttribute(this.dropdownElement, 'style');
                this.renderer.insertBefore(this.el.nativeElement.parentNode, this.dropdownElement, this.el.nativeElement.nextSibling);
                await sleep(500); // Wait for animations.
                this.dropdownElement = null;
                this.document.removeEventListener('click', this.hide, true);
            }, this.dropdownDelay);
        }
    }
    /**
     * Creates the dropdown menu element and appends it to the DOM.
     */
    create() {
        if (this.content) {
            let menu = this.el.nativeElement.nextSibling;
            let foundDropdown = false;
            while (!foundDropdown) {
                if (!menu || menu?.classList?.contains('dropdown')) {
                    foundDropdown = true;
                    break;
                }
                menu = menu.nextSibling;
            }
            if (!menu?.classList || !menu.classList?.contains('dropdown')) {
                return;
            }
            this.dropdownElement = menu;
            menu.getAttribute('dropdown');
            this.renderer.addClass(this.dropdownElement, 'dropdown-generated');
            this.renderer.addClass(this.dropdownElement, `dropdown-${this.dropdownPlacement.replace(' ', '-')}`);
            this.renderer.setAttribute(this.dropdownElement, 'style', `width: ${this.dropdownWidth}`);
            this.renderer.appendChild(this.document.body, this.dropdownElement);
        }
    }
    /**
     * Sets the position of the dropdown menu relative to the host element.
     */
    setPosition() {
        const hostPos = this.el.nativeElement.getBoundingClientRect();
        if (this.dropdownElement) {
            const dropdownPos = this.dropdownElement.getBoundingClientRect();
            const scrollPos = window.pageYOffset || this.document.documentElement.scrollTop || this.document.body.scrollTop || 0;
            let top;
            let left;
            const posHorizontal = this.dropdownPlacement.split(' ')[0] || 'center';
            const posVertical = this.dropdownPlacement.split(' ')[1] || 'bottom';
            switch (posHorizontal) {
                case 'left':
                    left = hostPos.left + hostPos.width - dropdownPos.width + this.dropdownDistanceHorizontal;
                    break;
                case 'right':
                    left = hostPos.left + this.dropdownDistanceHorizontal;
                    break;
                default:
                case 'center':
                    left = hostPos.left - dropdownPos.width / 2 + hostPos.width / 2 + this.dropdownDistanceHorizontal;
                    break;
            }
            switch (posVertical) {
                case 'bottom':
                    top = hostPos.bottom + this.dropdownDistanceVertical;
                    break;
                default:
                case 'top':
                    top = hostPos.top - dropdownPos.height - this.dropdownDistanceVertical;
            }
            this.renderer.setStyle(this.dropdownElement, 'top', `${top + scrollPos}px`);
            this.renderer.setStyle(this.dropdownElement, 'left', `${left}px`);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqDropdownDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i1.Router }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.8", type: SqDropdownDirective, selector: "[dropdown]", inputs: { content: ["dropdown", "content"], dropdownPlacement: "dropdownPlacement", dropdownDelay: "dropdownDelay", dropdownClass: "dropdownClass", dropdownWidth: "dropdownWidth", dropdownDistanceVertical: "dropdownDistanceVertical", dropdownDistanceHorizontal: "dropdownDistanceHorizontal", closeOnClick: "closeOnClick" }, host: { listeners: { "click": "onClick()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SqDropdownDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dropdown]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1.Router }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { content: [{
                type: Input,
                args: ['dropdown']
            }], dropdownPlacement: [{
                type: Input
            }], dropdownDelay: [{
                type: Input
            }], dropdownClass: [{
                type: Input
            }], dropdownWidth: [{
                type: Input
            }], dropdownDistanceVertical: [{
                type: Input
            }], dropdownDistanceHorizontal: [{
                type: Input
            }], closeOnClick: [{
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

/**
 * A custom Angular pipe for formatting numbers with thousand suffixes (e.g., K, M, G).
 *
 * @example
 * // In your component template:
 * // Assuming 'value' is a number.
 * {{ value | thousandSuff:'round':2 }}
 *
 * @param {number} input - The number to format.
 * @param {string} round - Optional. Specify 'round' to round the number to the nearest whole number.
 * @param {number} toFixedArgs - Optional. The number of decimal places to round to.
 * @returns {string} - The formatted number with suffix.
 */
class ThousandSuffixesPipe {
    /**
     * Transforms a number into a formatted string with thousand suffixes (e.g., K, M, G).
     *
     * @param {number} input - The number to format.
     * @param {string} round - Optional. Specify 'round' to round the number to the nearest whole number.
     * @param {number} toFixedArgs - Optional. The number of decimal places to round to.
     * @returns {string} - The formatted number with suffix.
     */
    transform(input, round, toFixedArgs) {
        const suffixes = ['k', 'M', 'G', 'T', 'P', 'E'];
        // Handle special cases
        if (Number.isNaN(input) || input === null || (!input && input !== 0)) {
            return '∞';
        }
        // If input is less than 1000, return it as is (optionally rounded)
        if (input < 1000) {
            if (round) {
                return String(Math.round(input));
            }
            return String(input);
        }
        // Calculate the appropriate suffix and format the number
        const exp = Math.floor(Math.log(input) / Math.log(1000));
        return (input / Math.pow(1000, exp)).toFixed(toFixedArgs) + suffixes[exp - 1];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: ThousandSuffixesPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.8", ngImport: i0, type: ThousandSuffixesPipe, name: "thousandSuff" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: ThousandSuffixesPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'thousandSuff' }]
        }] });

/**
 * A custom Angular pipe for filtering an array of objects based on a search string.
 *
 * @example
 * // In your component template:
 * // Assuming 'items' is an array of objects and 'searchTerm' is a string.
 * <ul>
 *   <li *ngFor="let item of items | search: searchTerm">{{ item.name }}</li>
 * </ul>
 *
 * @param {any[]} value - The array of objects to filter.
 * @param {string} search - The search string to filter by.
 * @returns {any[]} - The filtered array of objects.
 */
class SearchPipe {
    /**
     * Transforms an array of objects by filtering based on a search string.
     *
     * @param {any[]} value - The array of objects to filter.
     * @param {string} search - The search string to filter by.
     * @returns {any[]} - The filtered array of objects.
     */
    transform(value, search) {
        if (!search) {
            return value;
        }
        if (!value) {
            return '';
        }
        const solution = value?.filter((v) => {
            if (!v) {
                return false;
            }
            return JSON.stringify(v).toLowerCase().indexOf(search.toLowerCase()) > -1;
        });
        return solution;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SearchPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.8", ngImport: i0, type: SearchPipe, name: "search" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SearchPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'search' }]
        }] });

/**
 * A service for displaying toast messages in Angular applications.
 *
 * Toast messages are often used to provide feedback to users in a non-intrusive way. This service
 * provides methods to display different types of toast messages with various configurations.
 *
 * Look the link about the component in original framework and the appearance
 *
 * @see {@link https://css.squidit.com.br/components/toast}
 *
 * @example
 * @Component({
 *   selector: 'app-root',
 *   template: '<button (click)="showToast()">Show Toast</button>',
 * })
 * export class AppComponent {
 *   constructor(private toastHelper: ToastHelper) {}
 *
 *   showToast() {
 *     // Display a success toast message.
 *     this.toastHelper.toast.success('Operation was successful.', { duration: 3000 })
 *   }
 * }
 */
class ToastHelper {
    /**
     * Creates an instance of the ToastHelper service.
     */
    constructor() {
        /**
         * An object containing methods for displaying different types of toast messages.
         */
        this.toast = {
            success: (message, config) => this.toastLogSrr(message, config),
            error: (message, config) => this.toastLogSrr(message, config),
            inverted: (message, config) => this.toastLogSrr(message, config),
            info: (message, config) => this.toastLogSrr(message, config),
            warning: (message, config) => this.toastLogSrr(message, config),
            grayscale: (message, config) => this.toastLogSrr(message, config),
            custom: (message, config) => this.toastLogSrr(message, config),
            default: (message, config) => this.toastLogSrr(message, config),
            show: (message, config) => this.toastLogSrr(message, config),
            theme: (message, config) => this.toastLogSrr(message, config),
        };
        this.toast = Toast || window['Toast'];
    }
    /**
     * Logs and returns a toast message with the provided message and configuration.
     * @param {string} message - The message content of the toast.
     * @param {ToastConfig} config - The configuration options for the toast.
     * @returns {ToastResponse} - The response containing the message and configuration.
     */
    toastLogSrr(message, config) {
        return {
            message,
            config,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: ToastHelper, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: ToastHelper, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: ToastHelper, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

/**
 * Format time in seconds to a readable format.
 * @param {number} totalSeconds - Time in seconds
 * @returns {string} A string that contains the formatted time
 *
 * @example
 * // returns '1d 01h 01m 01s'
 * formatTime(90061)
 */
function formatTime(totalSeconds) {
    const seconds = totalSeconds % 60;
    const minutes = Math.floor(totalSeconds / 60) % 60;
    const hours = Math.floor(totalSeconds / 3600) % 24;
    const days = Math.floor(totalSeconds / 86400);
    if (days > 0) {
        return `${days}d ${hours.toString().padStart(2, '0')}h ${minutes
            .toString()
            .padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;
    }
    else if (hours > 0) {
        return `${hours.toString().padStart(2, '0')}h ${minutes
            .toString()
            .padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;
    }
    else if (minutes > 0) {
        return `${minutes.toString().padStart(2, '0')}m ${seconds
            .toString()
            .padStart(2, '0')}s`;
    }
    else {
        return `${seconds.toString().padStart(2, '0')}s`;
    }
}

/**
 * Array containing a collection of Angular components, directives, and pipes.
 * These elements can be used within the SquidCSSModule for building UI features.
 *
 * @type {(Type<any> | any)[]}
 */
const components = [
    SqButtonComponent,
    SqLoaderComponent,
    SqProgressBarComponent,
    SqStepsComponent,
    SqTooltipDirective,
    SqTooltipComponent,
    UniversalSafePipe,
    ThousandSuffixesPipe,
    SqTabComponent,
    SqTabsComponent,
    SqInfinityComponent,
    SqOverlayComponent,
    SqModalComponent,
    SqCollapseComponent,
    SqPaginationComponent,
    SqTagComponent,
    SqAccordionComponent,
    SqTextAreaComponent,
    SqSelectComponent,
    SqSelectorComponent,
    SqInputComponent,
    SqInputDateComponent,
    SqInputRangeComponent,
    SqInputFileComponent,
    SqDropdownDirective,
    SqSelectSearchComponent,
    SqClickOutsideDirective,
    SearchPipe,
    SearchFromAlternativeArrayPipe,
    SearchValidValuesPipe,
    SqSelectMultiTagsComponent,
    SqInputMaskComponent,
    SqInputMoneyComponent,
    SqInputNumberComponent,
    TranslateInternalPipe
];
/**
 * Angular module that exports a collection of UI components, directives, and pipes
 * for use in Angular applications.
 */
class SquidCSSModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SquidCSSModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.8", ngImport: i0, type: SquidCSSModule, declarations: [SqButtonComponent,
            SqLoaderComponent,
            SqProgressBarComponent,
            SqStepsComponent,
            SqTooltipDirective,
            SqTooltipComponent,
            UniversalSafePipe,
            ThousandSuffixesPipe,
            SqTabComponent,
            SqTabsComponent,
            SqInfinityComponent,
            SqOverlayComponent,
            SqModalComponent,
            SqCollapseComponent,
            SqPaginationComponent,
            SqTagComponent,
            SqAccordionComponent,
            SqTextAreaComponent,
            SqSelectComponent,
            SqSelectorComponent,
            SqInputComponent,
            SqInputDateComponent,
            SqInputRangeComponent,
            SqInputFileComponent,
            SqDropdownDirective,
            SqSelectSearchComponent,
            SqClickOutsideDirective,
            SearchPipe,
            SearchFromAlternativeArrayPipe,
            SearchValidValuesPipe,
            SqSelectMultiTagsComponent,
            SqInputMaskComponent,
            SqInputMoneyComponent,
            SqInputNumberComponent,
            TranslateInternalPipe], imports: [CommonModule,
            FormsModule,
            NgxMaskDirective,
            NgxMaskPipe], exports: [SqButtonComponent,
            SqLoaderComponent,
            SqProgressBarComponent,
            SqStepsComponent,
            SqTooltipDirective,
            SqTooltipComponent,
            UniversalSafePipe,
            ThousandSuffixesPipe,
            SqTabComponent,
            SqTabsComponent,
            SqInfinityComponent,
            SqOverlayComponent,
            SqModalComponent,
            SqCollapseComponent,
            SqPaginationComponent,
            SqTagComponent,
            SqAccordionComponent,
            SqTextAreaComponent,
            SqSelectComponent,
            SqSelectorComponent,
            SqInputComponent,
            SqInputDateComponent,
            SqInputRangeComponent,
            SqInputFileComponent,
            SqDropdownDirective,
            SqSelectSearchComponent,
            SqClickOutsideDirective,
            SearchPipe,
            SearchFromAlternativeArrayPipe,
            SearchValidValuesPipe,
            SqSelectMultiTagsComponent,
            SqInputMaskComponent,
            SqInputMoneyComponent,
            SqInputNumberComponent,
            TranslateInternalPipe, NgxMaskDirective,
            NgxMaskPipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SquidCSSModule, providers: [
            provideNgxMask()
        ], imports: [CommonModule,
            FormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: SquidCSSModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: components,
                    imports: [
                        CommonModule,
                        FormsModule,
                        NgxMaskDirective,
                        NgxMaskPipe
                    ],
                    providers: [
                        provideNgxMask()
                    ],
                    exports: [
                        ...components,
                        NgxMaskDirective,
                        NgxMaskPipe
                    ]
                }]
        }] });

/**
 * Public API Surface of src
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ColorsHelper, SearchFromAlternativeArrayPipe, SearchPipe, SearchValidValuesPipe, SqAccordionComponent, SqButtonComponent, SqClickOutsideDirective, SqCollapseComponent, SqDropdownDirective, SqInfinityComponent, SqInputComponent, SqInputDateComponent, SqInputFileComponent, SqInputMaskComponent, SqInputMoneyComponent, SqInputNumberComponent, SqInputRangeComponent, SqLoaderComponent, SqModalComponent, SqOverlayComponent, SqPaginationComponent, SqProgressBarComponent, SqSelectComponent, SqSelectMultiTagsComponent, SqSelectSearchComponent, SqSelectorComponent, SqStepsComponent, SqTabComponent, SqTabsComponent, SqTagComponent, SqTextAreaComponent, SqTooltipComponent, SqTooltipDirective, SquidCSSModule, ThousandSuffixesPipe, ToastHelper, TranslateInternalPipe, UniversalSafePipe, ValidatorHelper, formatTime, sleep, useMemo };
